import { REQUISITION_STATUS } from '../constants/requisition'
import { Injectable } from '@angular/core'
import type { RequisitionCartError } from './marketplace.service'

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  getExportProductsText() {
    return $localize`:Company products|Export@@companyProducts.exportProducts:Export products`
  }
  getProductExportFailedText() {
    return $localize`:Company products|Export@@companyProducts.failed:The product export could not be downloaded. Please try again.`
  }
  getRequisitionJSONDownloadText(): string {
    return $localize`:Requisitions|JSON download@@requisition.json.download:Download requisition JSON`
  }
  getRequisitionJSONFailedText(): string {
    return $localize`:Requisitions|JSON download@@requisition.json.failed:The requisition JSON could not be downloaded. Try again.`
  }
  getRequisitionJSONCompleteText(): string {
    return $localize`:Requisitions|JSON download@@requisition.json.complete:Download complete`
  }
  getRequisitionJSONDownloadedText(): string {
    return $localize`:Requisitions|JSON download@@requisition.json.downloaded:The requisition JSON is ready. Your browser has started the download.`
  }
  getPurchaseOrderReviewDetailsText(): string {
    return $localize`:Purchase orders|Participants and review@@purchaseOrder.reviewDetails:Request and buyer review`
  }
  getPurchaseOrderRequestedByText(): string {
    return $localize`:Purchase orders|Participants and review@@purchaseOrder.requestedBy:Requested by`
  }
  getPurchaseOrderApproverText(): string {
    return $localize`:Purchase orders|Participants and review@@purchaseOrder.approver:Buyer approver`
  }
  getPurchaseOrderDecisionAtText(): string {
    return $localize`:Purchase orders|Participants and review@@purchaseOrder.decisionAt:Approval/rejection time`
  }
  getPurchaseOrderNotAvailableText(): string {
    return $localize`:Purchase orders|Participants and review@@purchaseOrder.notAvailable:Not available`
  }
  getPurchaseOrderTitleText() {
    return $localize`:Purchase orders|Detail title@@purchaseOrder.title:Purchase order`
  }
  getPurchaseOrderNoFilterMatchesText() {
    return $localize`:Purchase orders|No matching statuses@@purchaseOrder.noFilterMatches:No purchase orders match this filter.`
  }
  getPurchaseOrderRecordDetailsText() {
    return $localize`:Purchase orders|Record details@@purchaseOrder.recordDetails:Record details`
  }
  getPurchaseOrderNumberText() {
    return $localize`:Purchase orders|Stored number@@purchaseOrder.number:Purchase order number`
  }
  getPurchaseOrderSupplierCountText(count: number) {
    if (count === 1) {
      return $localize`:Purchase orders|One supplier@@purchaseOrder.supplierCountOne:1 supplier`
    }
    return $localize`:Purchase orders|Supplier count@@purchaseOrder.supplierCountMany:${count}:count: suppliers`
  }
  getPurchaseOrderEmptyHintText() {
    return $localize`:Purchase orders|Empty hint@@purchaseOrder.emptyHint:Purchase orders appear here after buyer approval.`
  }
  getPurchaseOrdersText() {
    return $localize`:Purchase orders|Orders@@purchaseOrder.orders:Purchase orders`
  }
  getPurchaseOrderBackText() {
    return $localize`:Purchase orders|Back@@purchaseOrder.back:Back to purchase orders`
  }
  getPurchaseOrderDownloadText() {
    return $localize`:Purchase orders|Download@@purchaseOrder.download:Download PDF`
  }
  getPurchaseOrderDownloadingText() {
    return $localize`:Purchase orders|Downloading@@purchaseOrder.downloading:Downloading...`
  }
  getPurchaseOrderLoadingText() {
    return $localize`:Purchase orders|Loading@@purchaseOrder.loading:Loading purchase orders...`
  }
  getPurchaseOrderLoadFailedText() {
    return $localize`:Purchase orders|LoadFailed@@purchaseOrder.loadFailed:Purchase orders could not be loaded, or you do not have access. Try again.`
  }
  getPurchaseOrderDownloadFailedText() {
    return $localize`:Purchase orders|DownloadFailed@@purchaseOrder.downloadFailed:The PDF could not be downloaded. Try again.`
  }
  getPurchaseOrderEmptyText() {
    return $localize`:Purchase orders|Empty@@purchaseOrder.empty:Your company has no purchase orders yet.`
  }
  getPurchaseOrderBuyerText() {
    return $localize`:Purchase orders|Buyer@@purchaseOrder.buyer:Buyer company`
  }
  getPurchaseOrderPhoneText() {
    return $localize`:Purchase orders|Phone@@purchaseOrder.phone:Contact phone`
  }
  getPurchaseOrderSourceText() {
    return $localize`:Purchase orders|Source@@purchaseOrder.source:Source requisition`
  }
  getPurchaseOrderApprovedByText() {
    return $localize`:Purchase orders|ApprovedBy@@purchaseOrder.approvedBy:Approved by`
  }
  getPurchaseOrderSupplierText() {
    return $localize`:Purchase orders|Supplier@@purchaseOrder.supplier:Supplier`
  }
  getPurchaseOrderUnpricedText() {
    return $localize`:Purchase orders|Unpriced@@purchaseOrder.unpriced:Price not specified`
  }
  getPurchaseOrderLineAmountText() {
    return $localize`:Purchase orders|LineAmount@@purchaseOrder.lineAmount:Line amount`
  }
  getPurchaseOrderReferencesText() {
    return $localize`:Purchase orders|References@@purchaseOrder.references:Supplied references`
  }
  getPurchaseOrderNoPricesText() {
    return $localize`:Purchase orders|NoPrices@@purchaseOrder.noPrices:No priced items`
  }
  getPurchaseOrderIncompleteCostText() {
    return $localize`:Purchase orders|IncompleteCost@@purchaseOrder.incompleteCost:These subtotals are not the complete cost.`
  }
  getPurchaseOrderCurrenciesText() {
    return $localize`:Purchase orders|Currencies@@purchaseOrder.currencies:Currencies are shown separately. No currency conversion is applied.`
  }
  getPurchaseOrderCreateText() {
    return $localize`:Purchase orders|Create@@purchaseOrder.create:Open or create purchase order`
  }
  getPurchaseOrderCreateFailedText() {
    return $localize`:Purchase orders|CreateFailed@@purchaseOrder.createFailed:The buyer review is saved, but the purchase order could not be created. Retry purchase order creation.`
  }
  getPurchaseOrderRetryText() {
    return $localize`:Purchase orders|Retry@@purchaseOrder.retry:Retry purchase order creation`
  }
  getProcurementReferenceText(key: string): string {
    switch (key) {
      case 'BusinessUnitId':
        return $localize`:Procurement|BusinessUnitId@@procurement.BusinessUnitId:Business unit ID`
      case 'storageLocation':
        return $localize`:Procurement|storageLocation@@procurement.storageLocation:Storage location`
      case 'batchId':
        return $localize`:Procurement|batchId@@procurement.batchId:Batch reference`
      case 'companyCode':
        return $localize`:Procurement|companyCode@@procurement.companyCode:Company code`
      case 'purchaseOrder':
        return $localize`:Procurement|purchaseOrder@@procurement.purchaseOrder:External purchase order`
      case 'purchaseOrderItem':
        return $localize`:Procurement|purchaseOrderItem@@procurement.purchaseOrderItem:External purchase order item`
      case 'movementType':
        return $localize`:Procurement|movementType@@procurement.movementType:Movement type`
      case 'itemDocument':
        return $localize`:Procurement|itemDocument@@procurement.itemDocument:Item document`
      case 'itemDocumentItem':
        return $localize`:Procurement|itemDocumentItem@@procurement.itemDocumentItem:Item document item`
      case 'transactionDate':
        return $localize`:Procurement|transactionDate@@procurement.transactionDate:Posting date`
      case 'invoiceNumber':
        return $localize`:Procurement|invoiceNumber@@procurement.invoiceNumber:Invoice number`
      case 'invoiceAmount':
        return $localize`:Procurement|invoiceAmount@@procurement.invoiceAmount:Invoice amount`
      case 'purchaseOrderID':
        return $localize`:Procurement|purchaseOrderID@@procurement.purchaseOrderID:Invoice PO reference`
      case 'goodsReceiptReference':
        return $localize`:Procurement|goodsReceiptReference@@procurement.goodsReceiptReference:Goods receipt (document/year/item)`
      default:
        return key
    }
  }

  getBrandNameText() {
    return $localize`:Brand|Application name@@brand.name:Vite Marketplace`
  }
  getBrandLogoText() {
    return $localize`:Brand|Logo alternative text@@brand.logo:Vite Marketplace logo`
  }
  getBrandHomeText() {
    return $localize`:Brand|Home link accessible name@@brand.home:Vite Marketplace home`
  }
  getBrandPageTitleText(pageTitle: string) {
    return $localize`:Brand|Browser title with page name@@brand.pageTitle:${pageTitle}:pageTitle: | Vite Marketplace`
  }

  getRequisitionStatusFilterText() {
    return $localize`:Requisitions|Status filter@@requisition.statusFilter:Status`
  }
  getRequisitionAllStatusesText() {
    return $localize`:Requisitions|All statuses filter@@requisition.allStatuses:All statuses`
  }
  getRequisitionClearFilterText() {
    return $localize`:Requisitions|Clear status filter@@requisition.clearFilter:Clear filter`
  }
  getRequisitionNoFilterMatchesText() {
    return $localize`:Requisitions|No matching statuses@@requisition.noFilterMatches:No requisitions match this filter.`
  }
  getRequisitionSavedText() {
    return $localize`:Requisitions|Saved list@@requisition.saved:Requisitions`
  }
  getRequisitionDraftTitleText() {
    return $localize`:Requisitions|Draft page title@@requisition.draftTitle:Requisition draft`
  }
  getRequisitionDetailTitleText() {
    return $localize`:Requisitions|Submitted page title@@requisition.detailTitle:Requisition`
  }
  getRequisitionBackText() {
    return $localize`:Requisitions|Back to saved list@@requisition.back:Back to requisitions`
  }
  getRequisitionUnsavedWarningText() {
    return $localize`:Requisitions|Warn before leaving a changed draft@@requisition.unsavedWarning:Your changes to this requisition have not been saved.`
  }
  getRequisitionUnsavedTitleText() {
    return $localize`:Requisitions|Unsaved changes dialog title@@requisition.unsavedTitle:Unsaved changes`
  }
  getRequisitionKeepEditingText() {
    return $localize`:Requisitions|Stay on changed draft@@requisition.keepEditing:Keep editing`
  }
  getRequisitionLeaveText() {
    return $localize`:Requisitions|Leave changed draft@@requisition.leave:Leave without saving`
  }
  getRequisitionLoadingText() {
    return $localize`:Requisitions|Loading@@requisition.loading:Loading requisitions...`
  }
  getRequisitionLoadFailedText() {
    return $localize`:Requisitions|Load error@@requisition.loadFailed:Requisitions could not be loaded. Try again.`
  }
  getRequisitionApproversFailedText() {
    return $localize`:Requisitions|Approver load error@@requisition.approversFailed:Approvers could not be loaded. Try again.`
  }
  getRequisitionEmptyText() {
    return $localize`:Requisitions|Empty list@@requisition.empty:You have no requisitions yet.`
  }
  getRequisitionItemsText() {
    return $localize`:Requisitions|Items heading@@requisition.items:Items`
  }
  getRequisitionItemCountText(count: number) {
    if (count === 1) {
      return $localize`:Requisitions|One item in a saved requisition@@requisition.itemCountOne:1 item`
    }
    return $localize`:Requisitions|Items in a saved requisition@@requisition.itemCountMany:${count}:count: items`
  }
  getRequisitionDraftText() {
    return $localize`:Requisitions|Draft status@@requisition.draft:Draft`
  }
  getRequisitionAwaitingText() {
    return $localize`:Requisitions|Submitted status@@requisition.awaiting:Awaiting buyer review`
  }
  getRequisitionRecipientPendingText() {
    return $localize`:Requisitions|Recipient missing@@requisition.recipientPending:Recipient not entered`
  }
  getRequisitionSubmittedAtText() {
    return $localize`:Requisitions|Submitted time@@requisition.submittedAt:Submitted`
  }
  getRequisitionDetailsText() {
    return $localize`:Requisitions|Details heading@@requisition.details:Request details`
  }
  getRequisitionRequiredHintText() {
    return $localize`:Requisitions|Submission requirements@@requisition.requiredHint:To submit, enter a recipient and a delivery or collection location, then choose an approver below.`
  }
  getRequisitionLocationText() {
    return $localize`:Requisitions|Required delivery or collection group@@requisition.location:Delivery or collection (required)`
  }
  getRequisitionLocationHintText() {
    return $localize`:Requisitions|Delivery or collection help@@requisition.locationHint:Enter at least one location to submit.`
  }
  getRequisitionAdditionalRequestText() {
    return $localize`:Requisitions|Optional request fields@@requisition.additionalRequest:Additional request details`
  }
  getRequisitionAdditionalItemText() {
    return $localize`:Requisitions|Optional item fields@@requisition.additionalItem:Additional item details`
  }
  getRequisitionDetailsFilledText() {
    return $localize`:Requisitions|Collapsed details contain data@@requisition.detailsFilled:Added`
  }
  getRequisitionRecipientText() {
    return $localize`:Requisitions|Recipient label@@requisition.recipient:Recipient`
  }
  getRequisitionDeliveryText() {
    return $localize`:Requisitions|Delivery label@@requisition.delivery:Delivery address`
  }
  getRequisitionCollectionText() {
    return $localize`:Requisitions|Collection label@@requisition.collection:Collection location`
  }
  getRequisitionNeededByText() {
    return $localize`:Requisitions|Date label@@requisition.neededBy:Needed by`
  }
  getRequisitionBusinessUnitText() {
    return $localize`:Requisitions|Business unit label@@requisition.businessUnit:Business unit ID`
  }
  getRequisitionStorageText() {
    return $localize`:Requisitions|Storage label@@requisition.storage:Storage location`
  }
  getRequisitionCompanyCodeText() {
    return $localize`:Requisitions|Company code label@@requisition.companyCode:Company code`
  }
  getRequisitionNoteText() {
    return $localize`:Requisitions|Note label@@requisition.note:Note`
  }
  getRequisitionEmptyItemsText() {
    return $localize`:Requisitions|Empty items@@requisition.emptyItems:Add at least one item before submitting.`
  }
  getRequisitionItemReferenceText() {
    return $localize`:Requisitions|External item reference@@requisition.itemReference:External material reference`
  }
  getRequisitionSupplierReferenceText() {
    return $localize`:Requisitions|External supplier reference@@requisition.supplierReference:External supplier reference`
  }
  getRequisitionBatchText() {
    return $localize`:Requisitions|Batch reference@@requisition.batch:Batch reference`
  }
  getRequisitionUnitPriceText() {
    return $localize`:Requisitions|Unit price@@requisition.unitPrice:Unit price`
  }
  getRequisitionCurrencyText() {
    return $localize`:Requisitions|Currency@@requisition.currency:Currency`
  }
  getRequisitionKnownSubtotalText() {
    return $localize`:Requisitions|Subtotal@@requisition.knownSubtotal:Subtotal of priced items`
  }
  getRequisitionUnpricedText(count: number) {
    if (count === 1) {
      return $localize`:Requisitions|One item without price@@requisition.unpricedOne:1 item without a price`
    }
    return $localize`:Requisitions|Multiple items without price@@requisition.unpricedMany:${count}:count: items without a price`
  }
  getRequisitionReviewText() {
    return $localize`:Requisitions|Review heading@@requisition.review:Review and submit`
  }
  getRequisitionApproverText() {
    return $localize`:Requisitions|Approver label@@requisition.approver:Approver`
  }
  getRequisitionNoApproverText() {
    return $localize`:Requisitions|No approver@@requisition.noApprover:No eligible approver is available. Ask a company administrator to add another active Admin. You can still save this draft.`
  }
  getRequisitionCompleteToSubmitText() {
    return $localize`:Requisitions|Incomplete submission checklist@@requisition.completeToSubmit:To submit, complete:`
  }
  getRequisitionCompareLatestText() {
    return $localize`:Requisitions|Compare stale draft in another tab@@requisition.compareLatest:Compare latest in new tab`
  }
  getRequisitionLoadLatestText() {
    return $localize`:Requisitions|Load server draft@@requisition.loadLatest:Load latest here`
  }
  getRequisitionLoadLatestTitleText() {
    return $localize`:Requisitions|Confirm replacement with server draft@@requisition.loadLatestTitle:Load latest version?`
  }
  getRequisitionLoadLatestWarningText() {
    return $localize`:Requisitions|Warn before replacing local edits@@requisition.loadLatestWarning:Loading the latest version here will discard the unsaved edits on this screen. Compare it in another tab first if needed.`
  }
  getRequisitionDiscardAndLoadText() {
    return $localize`:Requisitions|Discard local edits and load server draft@@requisition.discardAndLoad:Discard edits and load`
  }
  getRequisitionLatestLoadedText() {
    return $localize`:Requisitions|Latest draft loaded@@requisition.latestLoaded:Latest version loaded.`
  }
  getRequisitionSaveText() {
    return $localize`:Requisitions|Save button@@requisition.save:Save draft`
  }
  getRequisitionSubmitText() {
    return $localize`:Requisitions|Submit button@@requisition.submit:Submit for buyer review`
  }
  getRequisitionSavedNoticeText() {
    return $localize`:Requisitions|Save success@@requisition.savedNotice:Draft saved.`
  }
  getRequisitionAcceptUnitText(productName: string, unit: string) {
    return $localize`:Requisitions|Explicitly accept changed request unit and reset quantity@@requisition.acceptUnit:Use ${unit}:unit: for ${productName}:productName: and reset its quantity to 1`
  }
  getRequisitionSubmitUncertainText() {
    return $localize`:Requisitions|Submission response lost@@requisition.submitUncertain:Submission status could not be confirmed. Reload this requisition before retrying.`
  }
  getRequisitionSubmittedNoticeText() {
    return $localize`:Requisitions|Submit success@@requisition.submittedNotice:Requisition submitted for buyer review.`
  }
  getRequisitionIneligibleText() {
    return $localize`:Requisitions|Ineligible cart@@requisition.ineligible:Remove unavailable items before creating a draft.`
  }
  getRequisitionInvalidQuantityText() {
    return $localize`:Requisitions|Invalid quantity@@requisition.invalidQuantity:Check each requested quantity.`
  }
  getRequisitionMissingDetailsText() {
    return $localize`:Requisitions|Missing details@@requisition.missingDetails:Enter a recipient, delivery address or collection location, and an approver.`
  }
  getRequisitionErrorText(reason?: string) {
    if (reason === 'empty_request') return this.getRequisitionEmptyItemsText()
    if (reason === 'invalid_quantity')
      return this.getRequisitionInvalidQuantityText()
    if (reason === 'invalid_approver')
      return this.getRequisitionNoApproverText()
    if (reason === 'missing_request_details')
      return this.getRequisitionMissingDetailsText()
    if (reason === 'unavailable' || reason === 'requests_disabled')
      return this.getRequisitionIneligibleText()
    if (reason === 'invalid_price')
      return $localize`:Requisitions|Invalid price@@requisition.invalidPrice:Enter a currency with every price.`
    if (reason === 'invalid_date')
      return $localize`:Requisitions|Invalid date@@requisition.invalidDate:Enter a valid needed-by date.`
    if (reason === 'already_submitted')
      return $localize`:Requisitions|Submitted in another tab@@requisition.submittedElsewhere:This requisition was submitted elsewhere. Your local edits are still here. Compare the submitted version before loading it.`
    if (reason === 'cart_changed')
      return $localize`:Requisitions|Cart revision changed@@requisition.cartChanged:Your cart changed. Review it and try again.`
    if (reason === 'unit_changed')
      return $localize`:Requisitions|Request unit changed@@requisition.unitChanged:A product request unit changed. Accept the new unit and review its quantity before submitting.`
    if (reason === 'stale_draft')
      return $localize`:Requisitions|Draft changed in another tab@@requisition.staleDraft:This draft changed in another tab. Your edits are still here. Compare the latest version before loading it.`
    return $localize`:Requisitions|Save error@@requisition.saveFailed:The requisition could not be saved. Try again.`
  }
  getRequisitionStatusText(status: string) {
    switch (status) {
      case REQUISITION_STATUS.DRAFT:
        return this.getRequisitionDraftText()
      case REQUISITION_STATUS.APPROVED:
        return $localize`:Requisitions|Approved status@@requisition.approved:Approved`
      case REQUISITION_STATUS.PARTIALLY_APPROVED:
        return $localize`:Requisitions|Partial status@@requisition.partiallyApproved:Partially approved`
      case REQUISITION_STATUS.REJECTED:
        return $localize`:Requisitions|Rejected status@@requisition.rejected:Rejected`
      default:
        return this.getRequisitionAwaitingText()
    }
  }
  getRequisitionAwaitingYourReviewText() {
    return $localize`:Requisitions|Assigned to current reviewer@@requisition.yourReview:Awaiting your review`
  }
  getRequisitionBuyerReviewText() {
    return $localize`:Requisitions|Buyer review heading@@requisition.buyerReview:Buyer review`
  }
  getRequisitionReviewHintText() {
    return $localize`:Requisitions|Review guidance@@requisition.reviewHint:Decide every item below. You can approve the requested quantity, reduce it, or reject the item with a reason. Decisions are final after submission.`
  }
  getRequisitionBulkActionsText() {
    return $localize`:Requisitions|Bulk actions heading@@requisition.bulkActions:Bulk actions`
  }
  getRequisitionApproveAllText() {
    return $localize`:Requisitions|Bulk decision control@@requisition.approveAll:Approve all`
  }
  getRequisitionRejectAllText() {
    return $localize`:Requisitions|Bulk decision control@@requisition.rejectAll:Reject all`
  }
  getRequisitionBulkDecisionHintText() {
    return $localize`:Requisitions|Bulk decision control@@requisition.bulkDecisionHint:Bulk actions replace your current item decisions. Review or edit each item before finalizing.`
  }
  getRequisitionBulkRejectionReasonText() {
    return $localize`:Requisitions|Bulk decision control@@requisition.bulkRejectionReason:Rejection reason for all items`
  }
  getRequisitionApplyRejectAllText() {
    return $localize`:Requisitions|Bulk decision control@@requisition.applyRejectAll:Apply rejection to all items`
  }
  getRequisitionDecisionText() {
    return $localize`:Requisitions|Item decision label@@requisition.decision:Item decision`
  }
  getRequisitionApproveText() {
    return $localize`:Requisitions|Approve item option@@requisition.approve:Approve`
  }
  getRequisitionRejectText() {
    return $localize`:Requisitions|Reject item option@@requisition.reject:Reject`
  }
  getRequisitionRequestedQuantityText() {
    return $localize`:Requisitions|Original quantity label@@requisition.requestedQuantity:Requested quantity`
  }
  getRequisitionApprovedQuantityText() {
    return $localize`:Requisitions|Approved quantity label@@requisition.approvedQuantity:Approved quantity`
  }
  getRequisitionRejectionReasonValidationText() {
    return $localize`:Requisitions|Invalid rejection reason@@requisition.rejectionReasonValidation:Enter a reason for rejection. If it is too long, shorten it.`
  }
  getRequisitionRejectionReasonText() {
    return $localize`:Requisitions|Rejection reason label@@requisition.rejectionReason:Rejection reason`
  }
  getRequisitionReviewQuantityHintText() {
    return $localize`:Requisitions|Approved quantity hint@@requisition.reviewQuantityHint:Enter a positive quantity up to the requested amount. Each and pack require whole numbers.`
  }
  getRequisitionFinalizeReviewText() {
    return $localize`:Requisitions|Finalize button@@requisition.finalizeReview:Finalize review`
  }
  getRequisitionReviewSavingText() {
    return $localize`:Requisitions|Saving review@@requisition.reviewSaving:Saving review...`
  }
  getRequisitionReviewedNoticeText() {
    return $localize`:Requisitions|Review success@@requisition.reviewedNotice:Review finalized.`
  }
  getRequisitionReviewedAtText() {
    return $localize`:Requisitions|Final review timestamp@@requisition.reviewedAt:Reviewed`
  }
  getRequisitionReviewValidationText() {
    return $localize`:Requisitions|Incomplete decisions@@requisition.reviewValidation:Decide every item. Check approved quantities and enter a reason for each rejected item.`
  }
  getRequisitionReviewUnsavedText() {
    return $localize`:Requisitions|Unsaved review warning@@requisition.reviewUnsaved:Your item decisions have not been submitted.`
  }
  getRequisitionReloadReviewText() {
    return $localize`:Requisitions|Reload review@@requisition.reloadReview:Reload requisition`
  }
  getRequisitionReviewErrorText(reason?: string, status?: number) {
    if (status === 403)
      return $localize`:Requisitions|Review access lost@@requisition.reviewForbidden:You are no longer eligible to review this requisition.`
    if (reason === 'review_not_pending')
      return $localize`:Requisitions|Review conflict@@requisition.reviewConflict:This review changed or was already finalized. Reload the requisition to see the result.`
    if (
      [
        'missing_decisions',
        'invalid_review',
        'missing_rejection_reason',
        'invalid_approved_quantity',
      ].includes(reason || '')
    )
      return this.getRequisitionReviewValidationText()
    return $localize`:Requisitions|Review failure@@requisition.reviewFailed:The review could not be confirmed. Reload the requisition before trying again.`
  }
  getRequisitionCartText() {
    return $localize`:Requisition cart|Navigation@@cart.title:Cart`
  }
  getRequisitionCartCountText(count: number) {
    if (count === 1) {
      return $localize`:Requisition cart|Navigation count@@cart.countOne:Cart, ${count}:count: product`
    }
    return $localize`:Requisition cart|Navigation count@@cart.count:Cart, ${count}:count: products`
  }
  getRequisitionCartLoadFailedText() {
    return $localize`:Requisition cart|Load error@@cart.loadFailed:Your cart could not be loaded. Try again.`
  }
  getRequisitionCartEmptyText() {
    return $localize`:Requisition cart|Empty state@@cart.empty:Your cart is empty.`
  }
  getRequisitionCartLoadingText() {
    return $localize`:Requisition cart|Loading@@cart.loading:Loading your cart...`
  }
  getRequisitionCartErrorText(reason?: RequisitionCartError | null) {
    if (reason === 'unavailable')
      return this.getRequisitionCartUnavailableText()
    if (reason === 'requests_disabled')
      return this.getRequisitionCartRequestsDisabledText()
    if (reason === 'own_product') return this.getRequisitionCartOwnProductText()
    return $localize`:Requisition cart|Error@@cart.error:Cart changes could not be saved. Try again.`
  }
  getRequisitionCartOwnProductText() {
    return $localize`:Requisition cart|Own product@@cart.ownProduct:You cannot request your own product.`
  }
  getRequisitionCartUnavailableText() {
    return $localize`:Requisition cart|Unavailable product@@cart.unavailable:This product is no longer available to request.`
  }
  getRequisitionCartRequestsDisabledText() {
    return $localize`:Requisition cart|Requests disabled@@cart.requestsDisabled:Requests have been disabled for this product.`
  }
  getRequisitionCartRemoveText() {
    return $localize`:Requisition cart|Remove item@@cart.remove:Remove`
  }
  getRequisitionCartCreateText() {
    return $localize`:Requisition cart|Create requisition@@cart.create:Create Requisition`
  }
  getRequisitionCartDraftPendingText() {
    return $localize`:Requisition cart|Draft dependency@@cart.draftPending:Your cart is saved. Creating a requisition is coming soon.`
  }
  getRequisitionCartUnitText() {
    return $localize`:Requisition cart|Request unit@@cart.unit:Request unit`
  }
  getRequisitionCartBrowseText() {
    return $localize`:Requisition cart|Browse link@@cart.browse:Browse marketplace`
  }
  getRequisitionCartSelectedProductsText() {
    return $localize`:Requisition cart|Section heading@@cart.selectedProducts:Selected products`
  }
  getRequisitionCartNextStepText() {
    return $localize`:Requisition cart|Section heading@@cart.nextStep:Next step`
  }
  getRequisitionCartProductsInCartText() {
    return $localize`:Requisition cart|Summary label@@cart.productsInCart:Products in cart`
  }
  getRequisitionCartWholeQuantityText() {
    return $localize`:Requisition cart|Whole unit quantity validation@@cart.wholeQuantity:Enter a whole number greater than zero.`
  }
  getRequisitionCartPositiveQuantityText() {
    return $localize`:Requisition cart|Measured quantity validation@@cart.positiveQuantity:Enter a number greater than zero.`
  }
  getRequisitionCartBreadcrumbText() {
    return $localize`:Requisition cart|Breadcrumb navigation label@@cart.breadcrumb:Breadcrumb`
  }
  getProductMaterialTypeUndeclaredText(): string {
    return $localize`:General|Product material type undeclared@@product.materialType.undeclared:Composition not declared`
  }
  getMarketplaceBrowseText(): string {
    return $localize`:Marketplace|Navigation@@marketplace.browse:Marketplace`
  }
  getMarketplaceSearchText(): string {
    return $localize`:Marketplace|Search placeholder@@marketplace.search:Search products`
  }
  getMarketplaceAllProductsText(): string {
    return $localize`:Marketplace|All products@@marketplace.allProducts:All products`
  }
  getMarketplaceAllCurrenciesText(): string {
    return $localize`:Marketplace|All currencies@@marketplace.allCurrencies:All currencies`
  }

  getMarketplaceAllInCategoryText(): string {
    return $localize`:Marketplace|All in category@@marketplace.allInCategory:All`
  }
  getMarketplaceFiltersText(): string {
    return $localize`:Marketplace|Filters@@marketplace.filters:Filters`
  }

  /**
   * Returns the heading for product attribute filters.
   * @returns {string} The translated string.
   */
  getMarketplaceAttributesText(): string {
    return $localize`:Marketplace|Custom attribute filters@@marketplace.attributes:Product attributes`
  }

  /**
   * Returns the label for the attribute picker.
   * @returns {string} The translated string.
   */
  getMarketplaceAddAttributeText(): string {
    return $localize`:Marketplace|Attribute picker@@marketplace.addAttribute:Add attribute filter`
  }

  /**
   * Returns the message shown when the attribute-filter limit is reached.
   * @returns {string} The translated string.
   */
  getMarketplaceAttributeLimitText(): string {
    return $localize`:Marketplace|Attribute picker limit@@marketplace.attributeLimit:You can add up to 50 attribute filters. Remove one to add another.`
  }

  /**
   * Returns the message shown when an attribute search has no matches.
   * @returns {string} The translated string.
   */
  getMarketplaceNoAttributesText(): string {
    return $localize`:Marketplace|Empty attribute search@@marketplace.noAttributes:No available attributes match your search.`
  }

  /**
   * Returns the message shown when the marketplace has no custom attributes.
   * @returns {string} The translated string.
   */
  getMarketplaceNoAvailableAttributesText(): string {
    return $localize`:Marketplace|No available attributes@@marketplace.noAvailableAttributes:No custom attributes are available yet.`
  }

  /**
   * Returns the message shown when all matching attributes are already selected.
   * @returns {string} The translated string.
   */
  getMarketplaceAttributesAlreadyAddedText(): string {
    return $localize`:Marketplace|Attributes already selected@@marketplace.attributesAlreadyAdded:All matching attributes have already been added.`
  }

  /**
   * Returns the message asking the user to reduce selected attribute values.
   * @returns {string} The translated string.
   */
  getMarketplaceAttributeSelectionLimitText(): string {
    return $localize`:Marketplace|Attribute selection limit@@marketplace.attributeSelectionLimit:Too many attribute values are selected. Remove some values to continue.`
  }

  /**
   * Returns the hint asking the user to narrow the attribute search.
   * @returns {string} The translated string.
   */
  getMarketplaceRefineAttributeSearchText(): string {
    return $localize`:Marketplace|Attribute search hint@@marketplace.refineAttributeSearch:Type more to find another attribute.`
  }

  /**
   * Returns the loading message for attribute suggestions.
   * @returns {string} The translated string.
   */
  getMarketplaceLoadingAttributesText(): string {
    return $localize`:Marketplace|Attribute lookup loading@@marketplace.loadingAttributes:Loading attributes...`
  }

  /**
   * Returns the label for searching an attribute's values.
   * @returns {string} The translated string.
   */
  getMarketplaceSearchValuesText(): string {
    return $localize`:Marketplace|Attribute value picker@@marketplace.searchValues:Search values`
  }

  /**
   * Returns the hint asking the user to narrow the value search.
   * @returns {string} The translated string.
   */
  getMarketplaceRefineValueSearchText(): string {
    return $localize`:Marketplace|Attribute value search hint@@marketplace.refineValueSearch:Type more to find another value.`
  }

  /**
   * Returns the loading message for attribute values.
   * @returns {string} The translated string.
   */
  getMarketplaceLoadingValuesText(): string {
    return $localize`:Marketplace|Attribute value lookup loading@@marketplace.loadingValues:Loading values...`
  }

  /**
   * Returns the retry message for a failed attribute lookup.
   * @returns {string} The translated string.
   */
  getMarketplaceAttributeLoadFailedText(): string {
    return $localize`:Marketplace|Attribute lookup error@@marketplace.attributeLoadFailed:Attributes could not be loaded. Try again.`
  }

  /**
   * Returns the retry message for a failed value lookup.
   * @returns {string} The translated string.
   */
  getMarketplaceValueLoadFailedText(): string {
    return $localize`:Marketplace|Attribute value lookup error@@marketplace.valueLoadFailed:Values could not be loaded. Try again.`
  }

  /**
   * Returns the accessible label for the applied attribute filters.
   * @returns {string} The translated string.
   */
  getMarketplaceAppliedAttributesText(): string {
    return $localize`:Marketplace|Applied attribute filters@@marketplace.appliedAttributes:Applied attribute filters`
  }

  getMarketplaceApplyText(): string {
    return $localize`:Marketplace|Apply filters@@marketplace.apply:Apply filters`
  }
  getMarketplaceSortText(): string {
    return $localize`:Marketplace|Sort label@@marketplace.sort:Sort by`
  }
  getMarketplaceNewestText(): string {
    return $localize`:Marketplace|Newest sorting@@marketplace.newest:Newest`
  }
  getMarketplaceNameAscText(): string {
    return $localize`:Marketplace|Name sorting@@marketplace.nameAsc:Name A to Z`
  }
  getMarketplaceNameDescText(): string {
    return $localize`:Marketplace|Name sorting@@marketplace.nameDesc:Name Z to A`
  }
  getMarketplacePriceAscText(): string {
    return $localize`:Marketplace|Price sorting@@marketplace.priceAsc:Price low to high`
  }
  getMarketplacePriceDescText(): string {
    return $localize`:Marketplace|Price sorting@@marketplace.priceDesc:Price high to low`
  }
  getMarketplacePriceNotProvidedText(): string {
    return $localize`:Marketplace|Missing price@@marketplace.priceNotProvided:Price not provided`
  }
  getMarketplaceIncludeUnpricedText(): string {
    return $localize`:Marketplace|Price filter@@marketplace.includeUnpriced:Include unpriced products`
  }
  getMarketplaceMinText(): string {
    return $localize`:Marketplace|Minimum@@marketplace.min:Min`
  }
  getMarketplaceMaxText(): string {
    return $localize`:Marketplace|Maximum@@marketplace.max:Max`
  }
  getMarketplaceInvalidRangeText(): string {
    return $localize`:Marketplace|Range validation@@marketplace.invalidRange:Minimum cannot exceed maximum.`
  }

  /**
   * Returns the validation message for an incomplete numeric filter.
   * @returns {string} The translated string.
   */
  getMarketplaceInvalidNumberText(): string {
    return $localize`:Marketplace|Number validation@@marketplace.invalidNumber:Enter a complete number.`
  }

  getMarketplaceWeightUnitRequiredText(): string {
    return $localize`:Marketplace|Weight unit validation@@marketplace.weightUnitRequired:Select a weight unit.`
  }

  getMarketplaceWeightUnitText(): string {
    return $localize`:Marketplace|Weight unit@@marketplace.weightUnit:Weight unit`
  }
  getMarketplaceFlightRangeText(): string {
    return $localize`:Marketplace|Flight time filter@@marketplace.flightRange:Max. flight time`
  }
  getMarketplaceUnder30Text(): string {
    return $localize`:Marketplace|Flight time range@@marketplace.under30:Under 30 min`
  }
  getMarketplace30To44Text(): string {
    return $localize`:Marketplace|Flight time range@@marketplace.30to44:30 to under 45 min`
  }
  getMarketplace45PlusText(): string {
    return $localize`:Marketplace|Flight time range@@marketplace.45plus:45 min and over`
  }
  getMarketplaceProductsCountText(count: number): string {
    if (count === 1)
      return $localize`:Marketplace|Singular result count@@marketplace.productCountOne:${count}:count: product`
    return $localize`:Marketplace|Result count@@marketplace.productsCount:${count}:count: products`
  }
  getMarketplaceNoResultsText(): string {
    return $localize`:Marketplace|Empty results@@marketplace.noResults:No products match your filters.`
  }
  getMarketplaceLoadFailedText(): string {
    return $localize`:Marketplace|Load error@@marketplace.loadFailed:Products could not be loaded. Try again.`
  }
  getMarketplaceLoadingText(): string {
    return $localize`:Marketplace|Loading@@marketplace.loading:Loading products...`
  }
  getMarketplaceRetryText(): string {
    return $localize`:Marketplace|Retry@@marketplace.retry:Retry`
  }
  getMarketplaceGridText(): string {
    return $localize`:Marketplace|Grid view@@marketplace.grid:Grid view`
  }
  getMarketplaceListText(): string {
    return $localize`:Marketplace|List view@@marketplace.list:List view`
  }
  getMarketplaceViewDetailsText(): string {
    return $localize`:Marketplace|Product details@@marketplace.viewDetails:View details`
  }
  getMarketplaceSelectText(): string {
    return $localize`:Marketplace|Add product to requisition cart@@marketplace.addToCart:Add to Cart`
  }
  getMarketplaceSelectedText(): string {
    return $localize`:Marketplace|Selected product@@marketplace.selected:Selected`
  }
  getMarketplaceRemoveSelectionText(): string {
    return $localize`:Marketplace|Remove product from requisition cart@@marketplace.removeFromCart:Remove from Cart`
  }

  getMarketplaceSelectionCountText(count: number): string {
    if (count === 1)
      return $localize`:Marketplace|Singular cart count@@marketplace.inCartCountOne:${count}:count: in cart`
    return $localize`:Marketplace|Cart count@@marketplace.inCartCount:${count}:count: in cart`
  }
  getMarketplaceOwnListingText(): string {
    return $localize`:Marketplace|Product belongs to the current company@@marketplace.ownListing:Your listing`
  }

  getMarketplaceRequestsUnavailableText(): string {
    return $localize`:Marketplace|Requests disabled@@marketplace.requestsUnavailable:Requests are not available for this product.`
  }
  getMarketplaceDescriptionText(): string {
    return $localize`:Marketplace|Description@@marketplace.description:Description`
  }

  /**
   * Labels the link back to the parent product.
   * @param {string} name Parent product name, kept as entered by the seller.
   * @returns {string} Translated back-link label containing the product name.
   */
  getMarketplaceBackToProductText(name: string): string {
    return $localize`:Marketplace|Return to parent product@@marketplace.backToProduct:Back to ${name}:productName:`
  }

  /**
   * Labels the shortcut to the visible component cards.
   * @param {number} count Number of visible direct components.
   * @returns {string} Translated shortcut label including the component count.
   */
  getMarketplaceViewComponentsText(count: number): string {
    return $localize`:Marketplace|Jump to linked products@@marketplace.viewComponents:View components (${count}:count:)`
  }

  /**
   * Names the product attribute section on marketplace details.
   * @returns {string} Translated Other information heading.
   */
  getMarketplaceOtherInformationText(): string {
    return $localize`:Marketplace|Additional product attributes@@marketplace.otherInformation:Other information`
  }

  /**
   * Describes a cart change that is still being saved.
   * @returns {string} Translated saving message.
   */
  getMarketplaceSavingText(): string {
    return $localize`:Marketplace|Cart saving progress@@marketplace.saving:Saving to cart…`
  }

  /**
   * Confirms the quantity already saved in the cart.
   * @param {number} quantity Saved buyer request quantity.
   * @param {string} unit Translated request unit displayed beside the quantity.
   * @returns {string} Translated confirmation containing the saved quantity and unit.
   */
  getMarketplaceAddedQuantityText(quantity: number, unit: string): string {
    return $localize`:Marketplace|Saved cart quantity@@marketplace.addedQuantity:Added · ${quantity}:quantity: ${unit}:unit:`
  }

  /**
   * Labels the previous-card button for assistive technology.
   * @returns {string} Translated previous-components label.
   */
  getMarketplacePreviousComponentsText(): string {
    return $localize`:Marketplace|Previous cards@@marketplace.previousComponents:Previous components`
  }

  /**
   * Labels the next-card button for assistive technology.
   * @returns {string} Translated next-components label.
   */
  getMarketplaceNextComponentsText(): string {
    return $localize`:Marketplace|Next cards@@marketplace.nextComponents:Next components`
  }

  /**
   * Labels the cart link shown after a product has been added.
   * @returns {string} Translated View Cart label.
   */
  getMarketplaceViewCartText(): string {
    return $localize`:Marketplace|View saved requests@@marketplace.viewCart:View Cart`
  }

  /**
   * Names the direct component product section.
   * @returns {string} Translated Components heading.
   */
  getMarketplaceComponentsText(): string {
    return $localize`:Marketplace|Linked products section@@marketplace.components:Components`
  }

  /**
   * Explains that component prices and requests are separate from the main product.
   * @returns {string} Translated component section guidance.
   */
  getMarketplaceComponentsHelpText(): string {
    return $localize`:Marketplace|Separate component requests@@marketplace.componentsHelp:Explore linked products and request them separately. Component prices are separate from the main product price.`
  }

  /**
   * Explains a failed component load while the main listing remains usable.
   * @returns {string} Translated component loading error.
   */
  getMarketplaceComponentsLoadFailedText(): string {
    return $localize`:Marketplace|Component loading error@@marketplace.componentsLoadFailed:Components could not be loaded. You can still view and request the main product.`
  }

  /**
   * Names the marketplace product specifications section.
   * @returns {string} Translated Specifications heading.
   */
  getMarketplaceSpecificationsText(): string {
    return $localize`:Marketplace|Specifications section@@marketplace.specifications:Specifications`
  }

  /**
   * Explains why a buyer cannot request their own company's product.
   * @returns {string} Translated own-company restriction message.
   */
  getMarketplaceOwnRequestUnavailableText(): string {
    return $localize`:Marketplace|Own company request restriction@@marketplace.ownRequestUnavailable:You cannot request products from your own company.`
  }

  /**
   * Explains which roles can request marketplace products.
   * @returns {string} Translated request-role restriction message.
   */
  getMarketplaceRequestRoleRequiredText(): string {
    return $localize`:Marketplace|Request role restriction@@marketplace.requestRoleRequired:An Admin or Compliance Officer can request products.`
  }
  getMarketplaceBackText(): string {
    return $localize`:Marketplace|Back to browsing@@marketplace.back:Back to marketplace`
  }
  getMarketplaceNotFoundText(): string {
    return $localize`:Marketplace|Unavailable product@@marketplace.notFound:This product is unavailable.`
  }
  getMarketplacePhotoUnavailableText(): string {
    return $localize`:Marketplace|Missing image@@marketplace.photoUnavailable:Product image unavailable`
  }
  getMarketplaceSupplierText(): string {
    return $localize`:Marketplace|Seller filter@@marketplace.supplier:Seller`
  }
  getMarketplaceBrandText(): string {
    return $localize`:Marketplace|Brand filter@@marketplace.brand:Brand`
  }
  getMarketplacePriceRangeText(): string {
    return $localize`:Marketplace|Price range@@marketplace.priceRange:Price`
  }
  getMarketplaceWeightRangeText(): string {
    return $localize`:Marketplace|Weight range@@marketplace.weightRange:Weight`
  }
  getMarketplacePreviousText(): string {
    return $localize`:Marketplace|Previous page@@marketplace.previous:Previous page`
  }
  getMarketplaceNextText(): string {
    return $localize`:Marketplace|Next page@@marketplace.next:Next page`
  }
  getMarketplacePageText(page: number): string {
    return $localize`:Marketplace|Page number@@marketplace.page:Page ${page}:page:`
  }
  getMarketplaceSelectedNoCartText(): string {
    return $localize`:Marketplace|Selection notice@@marketplace.selectionNotice:Selected for this visit. Cart and requests are not available yet.`
  }
  getSearchCategoriesText(): string {
    return $localize`:Product|Category search placeholder@@product.searchCategories:Search categories...`
  }

  getKeepProductPrivateText(): string {
    return $localize`:Marketplace|Product privacy@@marketplace.keepPrivate:Keep this product private`
  }

  getAllowRequestsText(): string {
    return $localize`:Marketplace|Product requests@@marketplace.allowRequests:Allow requests for this product`
  }

  getMarketplaceSettingsHelpText(): string {
    return $localize`:Marketplace|Product settings help@@marketplace.settingsHelp:Company publication controls visibility. Allow requests is a separate setting.`
  }

  getSupplierCopyMarketplaceNoticeText(): string {
    return $localize`:Marketplace|Supplier copy notice@@marketplace.supplierCopyNotice:Supplier copies cannot be published or requested.`
  }

  getCompanyCountText(count: number): string {
    return count === 1
      ? $localize`:Companies|Singular count@@companies.countOne:${count}:count: company`
      : $localize`:Companies|Plural count@@companies.countMany:${count}:count: companies`
  }

  getCompaniesPrivateByDefaultText(): string {
    return $localize`:Companies|Privacy notice@@companies.privateDefault:Company products are private by default.`
  }

  getCompaniesPublicationNoticeText(): string {
    return $localize`:Companies|Publication notice@@companies.publicationNotice:Turn on public visibility to show eligible products from that company to signed-in users.`
  }

  getSearchCompaniesByNameText(): string {
    return $localize`:Companies|Search label@@companies.searchName:Search companies by name`
  }

  getVisibilityText(): string {
    return $localize`:Companies|Filter label@@companies.visibility:Visibility`
  }

  getAllText(): string {
    return $localize`:Companies|Filter option@@companies.all:All`
  }

  getPublicText(): string {
    return $localize`:Companies|Filter option@@companies.public:Public`
  }

  getPrivateText(): string {
    return $localize`:Companies|Filter option@@companies.private:Private`
  }

  getProductVisibilityText(): string {
    return $localize`:Companies|Column header@@companies.productVisibility:Product visibility`
  }

  getMakeProductsPublicText(): string {
    return $localize`:Companies|Toggle label@@companies.makeProductsPublic:Make products public`
  }

  getOnText(): string {
    return $localize`:Companies|Toggle state@@companies.on:On`
  }

  getOffText(): string {
    return $localize`:Companies|Toggle state@@companies.off:Off`
  }

  getMarketplacePublishedText(): string {
    return $localize`:Companies|Published state@@companies.published:Public · Visible to signed-in users`
  }

  getMarketplacePrivateText(): string {
    return $localize`:Companies|Private state@@companies.privateStatus:Private · Not visible in marketplace`
  }

  getMarketplaceSaveFailedText(): string {
    return $localize`:Marketplace|Save error@@marketplace.saveFailed:Marketplace settings could not be saved. Try again.`
  }

  getMarketplaceDetailsText(): string {
    return $localize`:Marketplace|Section title@@marketplace.details:Marketplace details`
  }

  getMarketplaceCategoryLabelText(): string {
    return $localize`:Marketplace|Field label@@marketplace.category:Marketplace category`
  }

  getMarketplaceSubcategoryLabelText(): string {
    return $localize`:Marketplace|Field label@@marketplace.subcategory:Subcategory`
  }

  getMarketplaceRequestUnitText(): string {
    return $localize`:Marketplace|Field label@@marketplace.requestUnit:Request unit`
  }

  getMarketplaceConditionText(): string {
    return $localize`:Marketplace|Field label@@marketplace.condition:Condition`
  }

  getMarketplacePricePerUnitText(): string {
    return $localize`:Marketplace|Field label@@marketplace.pricePerUnit:Price per request unit`
  }

  getMarketplacePriceText(): string {
    return $localize`:Marketplace|Field label@@marketplace.price:Price`
  }

  getMarketplacePositivePriceText(): string {
    return $localize`:Marketplace|Validation error@@marketplace.positivePrice:Enter a price greater than zero`
  }

  getMarketplaceCurrencyText(): string {
    return $localize`:Marketplace|Field label@@marketplace.currency:Currency`
  }

  getMarketplaceCurrencyRequiredText(): string {
    return $localize`:Marketplace|Validation error@@marketplace.currencyRequired:Enter a three-letter currency code with a price`
  }

  getMarketplaceFlightTimeMinutesText(): string {
    return $localize`:Marketplace|Field label@@marketplace.flightTimeMinutes:Flight time (minutes)`
  }

  getMarketplacePositiveFlightTimeText(): string {
    return $localize`:Marketplace|Validation error@@marketplace.positiveFlightTime:Enter a flight time greater than zero`
  }

  getMarketplaceFlightTimeText(): string {
    return $localize`:Marketplace|Specification label@@marketplace.flightTime:Flight time`
  }

  getMarketplaceMinutesText(): string {
    return $localize`:Marketplace|Measurement unit@@marketplace.minutes:minutes`
  }

  getMarketplaceDroneTypeText(): string {
    return $localize`:Marketplace|Field label@@marketplace.droneType:Drone type`
  }

  getMarketplaceVideoResolutionText(): string {
    return $localize`:Marketplace|Field label@@marketplace.videoResolution:Video resolution`
  }

  getMarketplaceSensorSizeInchesText(): string {
    return $localize`:Marketplace|Field label@@marketplace.sensorSizeInches:Sensor size (inches)`
  }

  getMarketplaceSensorSizeText(): string {
    return $localize`:Marketplace|Specification label@@marketplace.sensorSize:Sensor size`
  }

  getMarketplaceFpvText(): string {
    return $localize`:Marketplace|Field label@@marketplace.fpv:FPV`
  }

  getMarketplaceUnknownText(): string {
    return $localize`:Marketplace|Missing value@@marketplace.unknown:Unknown`
  }

  getMarketplaceUncategorizedText(): string {
    return $localize`:Marketplace|Missing category@@marketplace.uncategorized:Uncategorized`
  }

  getMarketplaceCopiedFromSupplierText(): string {
    return $localize`:Marketplace|Copied product indicator@@marketplace.copiedFromSupplier:Copied from supplier`
  }

  getMarketplaceEachText(): string {
    return $localize`:Marketplace|Request unit@@marketplace.each:each`
  }

  getMarketplaceCategoryText(category: string | null | undefined): string {
    switch (category) {
      case 'Drones':
        return $localize`:Marketplace category@@marketplace.category.drones:Drones`
      case 'Drone accessories':
        return $localize`:Marketplace category@@marketplace.category.droneAccessories:Drone accessories`
      case 'Furniture':
        return $localize`:Marketplace category@@marketplace.category.furniture:Furniture`
      case 'Computer equipment':
        return $localize`:Marketplace category@@marketplace.category.computerEquipment:Computer equipment`
      case 'Food & drink':
        return $localize`:Marketplace category@@marketplace.category.foodDrink:Food & drink`
      case 'Other':
        return $localize`:Marketplace category@@marketplace.category.other:Other`
      default:
        return this.getMarketplaceUncategorizedText()
    }
  }

  getMarketplaceSubcategoryText(subcategory: string): string {
    switch (subcategory) {
      case 'Photography':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.photography:Photography`
      case 'Surveying & mapping':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.surveyingMapping:Surveying & mapping`
      case 'Inspection':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.inspection:Inspection`
      case 'Agriculture':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.agriculture:Agriculture`
      case 'Cameras & payloads':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.camerasPayloads:Cameras & payloads`
      case 'Batteries & power':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.batteriesPower:Batteries & power`
      case 'Parts & accessories':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.partsAccessories:Parts & accessories`
      case 'Chairs':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.chairs:Chairs`
      case 'Other furniture':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.otherFurniture:Other furniture`
      case 'Computers':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.computers:Computers`
      case 'Peripherals & accessories':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.peripheralsAccessories:Peripherals & accessories`
      case 'Food':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.food:Food`
      case 'Drinks':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.drinks:Drinks`
      case 'General supplies':
        return $localize`:Marketplace subcategory@@marketplace.subcategory.generalSupplies:General supplies`
      default:
        return this.getMarketplaceUnknownText()
    }
  }

  getMarketplaceRequestUnitValueText(unit: string | null | undefined): string {
    switch (unit?.trim().toLowerCase()) {
      case 'each':
        return this.getMarketplaceEachText()
      case 'pack':
        return this.getMarketplacePackText()
      case 'box':
        return $localize`:Marketplace|Request unit@@marketplace.box:box`
      case 'set':
        return $localize`:Marketplace|Request unit@@marketplace.set:set`
      case 'pair':
        return $localize`:Marketplace|Request unit@@marketplace.pair:pair`
      case 'case':
        return $localize`:Marketplace|Request unit@@marketplace.case:case`
      case 'kg':
        return $localize`:Marketplace|Request unit@@marketplace.kg:kg`
      case 'litre':
        return $localize`:Marketplace|Request unit@@marketplace.litre:litre`
      default:
        return unit || this.getMarketplaceEachText()
    }
  }

  getMarketplaceConditionValueText(
    condition: string | null | undefined,
  ): string {
    switch (condition?.trim().toLowerCase()) {
      case 'new':
        return $localize`:Marketplace|Product condition@@marketplace.condition.new:New`
      case 'used':
        return $localize`:Marketplace|Product condition@@marketplace.condition.used:Used`
      case 'refurbished':
        return $localize`:Marketplace|Product condition@@marketplace.condition.refurbished:Refurbished`
      default:
        return condition ?? ''
    }
  }

  getMarketplacePackText(): string {
    return $localize`:Marketplace|Request unit@@marketplace.pack:pack`
  }

  /**
   * Returns translated string for Explosive hazard
   * @returns {string} The translated string
   */
  getGhsExplosiveText(): string {
    return $localize`:General|Alt text for explosive hazard @@ghs.alt.explosive:Explosive`
  }

  /**
   * Returns translated string for Flammable hazard
   * @returns {string} The translated string
   */
  getGhsFlammableText(): string {
    return $localize`:General|Alt text for flammable hazard @@ghs.alt.flammable:Flammable`
  }

  /**
   * Returns translated string for Oxidizing hazard
   * @returns {string} The translated string
   */
  getGhsOxidizingText(): string {
    return $localize`:General|Alt text for oxidizing hazard @@ghs.alt.oxidizing:Oxidizing`
  }

  /**
   * Returns translated string for Compressed Gas hazard
   * @returns {string} The translated string
   */
  getGhsCompressedGasText(): string {
    return $localize`:General|Alt text for compressed gas hazard @@ghs.alt.compressedGas:Compressed Gas`
  }

  /**
   * Returns translated string for Corrosive hazard
   * @returns {string} The translated string
   */
  getGhsCorrosiveText(): string {
    return $localize`:General|Alt text for corrosive hazard @@ghs.alt.corrosive:Corrosive`
  }

  /**
   * Returns translated string for Environmental Hazard
   * @returns {string} The translated string
   */
  getGhsEnvironmentalHazardText(): string {
    return $localize`:General|Alt text for environmental hazard @@ghs.alt.environmentalHazard:Environmental Hazard`
  }

  /**
   * Returns translated string for Health Hazard
   * @returns {string} The translated string
   */
  getGhsHealthHazardText(): string {
    return $localize`:General|Alt text for health hazard @@ghs.alt.healthHazard:Health hazard`
  }

  /**
   * Returns translated string for Harmful hazard
   * @returns {string} The translated string
   */
  getGhsHarmfulText(): string {
    return $localize`:General|Alt text for harmful hazard @@ghs.alt.harmful:Harmful`
  }

  /**
   * Returns translated string for Toxic hazard
   * @returns {string} The translated string
   */
  getGhsToxicText(): string {
    return $localize`:General|Alt text for toxic hazard @@ghs.alt.toxic:Toxic`
  }
  /**
   * Returns translated string for substances present disclosure notice
   * @returns {string} The translated string
   */
  getSubstancesPresentMandatoryText(): string {
    return $localize`:General|Text for mandatory substance presence@@general.text.substancesPresentMandatory:Substances are present in this product and are required for disclosure under`
  }

  /**
   * Returns translated string for Environment Canada mandatory regulations
   * @returns {string} The translated string
   */
  getEnvironmentCanadaMandatoryText(): string {
    return $localize`:General|Text for Environment Canada mandatory regulations@@general.text.environmentCanadaMandatory:Environment and Climate Change Canada's Mandatory Disclosure of Substances in products Regulations`
  }

  /**
   * Returns translated string for substances present voluntary notice
   * @returns {string} The translated string
   */
  getSubstancesPresentVoluntaryText(): string {
    return $localize`:General|Text for voluntary substance presence@@general.text.substancesPresentVoluntary:Substances are present in this product and are recommended for disclosure under`
  }

  /**
   * Returns translated string for Environment Canada voluntary code
   * @returns {string} The translated string
   */
  getEnvironmentCanadaVoluntaryText(): string {
    return $localize`:General|Text for Environment Canada voluntary code@@general.text.environmentCanadaVoluntary:Environment and Climate Change Canada's Voluntary Disclosure of Substances in products Code of Practice`
  }
  /**
   * Returns the guidance for a photo that cannot fit the upload limit.
   * @returns The translated photo size guidance.
   */
  getImageTooLargeText(): string {
    return $localize`:General|Text for image size validation message@@general.text.imageTooLarge:This photo is too large. Try a smaller photo or save it as JPG.`
  }

  /**
   * Returns the message for an unsupported or unreadable product photo.
   * @returns The translated photo validation message.
   */
  getInvalidProductImageText(): string {
    return $localize`:General|Product image validation@@general.text.invalidProductImage:Choose a valid JPEG or PNG image.`
  }

  /**
   * Returns the status shown while the browser prepares a selected photo.
   * @returns The translated photo preparation status.
   */
  getPreparingPhotoText(): string {
    return $localize`:General|Product image preparation@@general.text.preparingPhoto:Preparing photo…`
  }

  /**
   * Returns the product photo format, resizing and upload-limit guidance.
   * @returns The translated photo upload instructions.
   */
  getProductImageUploadHelpText(): string {
    return $localize`:General|Product image upload help@@general.text.productImageUploadHelp:Choose a JPG or PNG photo. We’ll resize it before uploading (max 5 MB).`
  }

  /**
   * Returns translated string for 'Product name and product identifier need to be different from the original product.'
   * @returns {string} The translated string
   */
  getProductNameIdentifierDifferentText(): string {
    return $localize`:General|Text for validation message@@general.text.productNameIdentifierDifferent:Product name and product identifier need to be different from the original product.`
  }
  /**
   * Returns translated string for 'Account'
   * @returns {string} The translated string
   */
  getAccountText(): string {
    return $localize`:General|Text for input/information@@general.text.account:Account`
  }

  /**
   * Returns translated string for 'Advanced Search'
   * @returns {string} The translated string
   */
  getAdvancedSearchText(): string {
    return $localize`:General|Text for input/information@@general.text.advancedSearch:Advanced Search`
  }

  /**
   * Returns translated string for 'Open location settings icon'
   * @returns {string} The translated string
   */
  getOpenLocationSettingsIconText(): string {
    return $localize`:General|Text for icon tooltip@@general.text.openLocationSettingsIcon:Open location settings icon`
  }

  /**
   * Returns translated string for 'Location'
   * @returns {string} The translated string
   */
  getLocationText(): string {
    return $localize`:General|Text for input/information@@general.text.location:Location`
  }

  /**
   * Returns translated string for 'Choose Your Location'
   * @returns {string} The translated string
   */
  getChooseYourLocationText(): string {
    return $localize`:General|Text for input/information@@general.text.chooseYourLocation:Choose Your Location`
  }

  /**
   * Returns translated string for 'Select Province or Territory'
   * @returns {string} The translated string
   */
  getSelectProvinceOrTerritoryText(): string {
    return $localize`:General|Text for province or territory select dropdown@@general.text.selectProvinceOrTerritory:Select Province or Territory`
  }

  /**
   * Returns translated string for 'Login'
   * @returns {string} The translated string
   */
  getLoginText(): string {
    return $localize`:General|Text for input/information@@general.text.login:Login`
  }

  /**
   * Returns translated string for 'Logout'
   * @returns {string} The translated string
   */
  getLogoutText(): string {
    return $localize`:General|Text for input/information@@general.text.logout:Logout`
  }

  /**
   * Returns translated string for 'Search for a product'
   * @returns {string} The translated string
   */
  getSearchForAProductText(): string {
    return $localize`:General|Text for input/information@@general.text.searchForAProduct:Search for a product`
  }

  /**
   * Returns translated string for 'Search for a component'
   * @returns {string} The translated string
   */
  getSearchForAComponentText(): string {
    return $localize`:General|Text for input/information@@general.text.searchForAComponent:Search for a component`
  }

  /**
   * Returns translated string for 'No results'
   * @returns {string} The translated string
   */
  getNoResultsText(): string {
    return $localize`:General|Text for input/information@@general.text.noResults:No results`
  }

  /**
   * Returns translated string for 'Integrations'
   * @returns {string} The translated string
   */
  getIntegrationsText(): string {
    return $localize`:General|Text for input/information@@general.text.integrations:Integrations`
  }

  /**
   * Returns translated string for 'Create'
   * @returns {string} The translated string
   */
  getCreateText(): string {
    return $localize`:General|Text for input/information@@general.text.create:Create`
  }

  /**
   * Returns translated string for 'Api key for new integration generated and copied to your clipboard successfully.'
   * @returns {string} The translated string
   */
  getIntegrationKeyCreatedMsg(): string {
    return $localize`:General|Text for input/information@@general.text.integrationKeyCreatedMsg:Api key for new integration generated and copied to your clipboard successfully.`
  }

  /**
   * Returns translated string for 'Reveal API Keys'
   * @returns {string} The translated string
   */
  getRevealAPIKeysText(): string {
    return $localize`:General|Text for input/information@@general.text.revealApiKeys:Reveal API Keys`
  }

  /**
   * Returns translated string for 'API Key'
   * @returns {string} The translated string
   */
  getApiKeyText(): string {
    return $localize`:General|Text for input/information@@general.text.apiKey:API Key`
  }

  /**
   * Returns translated string for 'Client'
   * @returns {string} The translated string
   */
  getClientText(): string {
    return $localize`:General|Text for input/information@@general.text.client:Client`
  }

  /**
   * Returns translated string for 'Expires At'
   * @returns {string} The translated string
   */
  getExpiresAtText(): string {
    return $localize`:General|Text for input/information@@general.text.expiresAt:Expires At`
  }

  /**
   * Returns translated string for 'Users'
   * @returns {string} The translated string
   */
  getUsersText(): string {
    return $localize`:General|Text for input/information@@general.text.users:Users`
  }

  /**
   * Returns translated string for 'Create User'
   * @returns {string} The translated string
   */
  getCreateUserText(): string {
    return $localize`:General|Text for input/information@@general.text.createUser:Create User`
  }

  /**
   * Returns translated string for 'First Name'
   * @returns {string} The translated string
   */
  getFirstNameText(): string {
    return $localize`:General|Text for input/information@@general.text.firstName:First Name`
  }

  /**
   * Returns translated string for 'Last Name'
   * @returns {string} The translated string
   */
  getLastNameText(): string {
    return $localize`:General|Text for input/information@@general.text.lastName:Last Name`
  }

  /**
   * Returns translated string for 'Email Address'
   * @returns {string} The translated string
   */
  getEmailAddressText(): string {
    return $localize`:General|Text for input/information@@general.text.emailAddress:Email Address`
  }

  /**
   * Returns translated string for 'Role'
   * @returns {string} The translated string
   */
  getRoleText(): string {
    return $localize`:General|Text for input/information@@general.text.role:Role`
  }

  /**
   * Returns translated string for 'Company'
   * @returns {string} The translated string
   */
  getCompanyText(): string {
    return $localize`:General|Text for input/information@@general.text.company:Company`
  }

  /**
   * Returns translated string for 'Submit'
   * @returns {string} The translated string
   */
  getSubmitText(): string {
    return $localize`:General|Text for input/information@@general.text.submit:Submit`
  }

  /**
   * Returns translated string for 'Reset'
   * @returns {string} The translated string
   */
  getResetText(): string {
    return $localize`:General|Text for input/information@@general.text.reset:Reset`
  }

  /**
   * Returns translated string for 'User created successfully.'
   * @returns {string} The translated string
   */
  getUserCreateSuccessMessage(): string {
    return $localize`:General|Text for input/information@@general.text.userCreatedSuccessfully:User created successfully.`
  }

  /**
   * Returns translated string for 'Filters'
   * @returns {string} The translated string
   */
  getFiltersText(): string {
    return $localize`:General|Text for input/information@@general.text.filters:Filters`
  }

  /**
   * Returns translated string for 'Reset Filter'
   * @returns {string} The translated string
   */
  getResetFilterText(): string {
    return $localize`:General|Text for input/information@@general.text.resetFilter:Reset Filter`
  }

  /**
   * Returns translated string for 'Filter Results'
   * @returns {string} The translated string
   */
  getFilterResultsText(): string {
    return $localize`:General|Text for input/information@@general.text.filterResults:Filter Results`
  }

  /**
   * Returns translated string for 'Results'
   * @returns {string} The translated string
   */
  getResultsText(): string {
    return $localize`:General|Text for input/information@@general.text.results:Results`
  }

  /**
   * Returns translated string for 'Password'
   * @returns {string} The translated string
   */
  getPasswordText(): string {
    return $localize`:General|Text for input/information@@general.text.password:Password`
  }

  /**
   * Returns translated string for 'Password and confirmation password must match.'
   * @returns {string} The translated string
   */
  getPasswordAndConfirmationMustMatchText(): string {
    return $localize`:General|Text for input/information@@general.text.passwordAndConfirmationPasswordMustMatch:Password and confirmation password must match.`
  }

  /**
   * Returns translated string for 'Password must be at least 10 characters long.'
   * @returns {string} The translated string
   */
  getPasswordMustBeAtleastTenCharactersLongText(): string {
    return $localize`:General|Text for input/information@@general.text.passwordMustBeAtleastTenCharactersLong:Password must be at least 10 characters long.`
  }

  /**
   * Returns translated string for 'Password must contain at least one lower case character.'
   * @returns {string} The translated string
   */
  getLowerCaseRequiredText(): string {
    return $localize`:General|Text for password requirement@@general.text.passwordMustContainLowerCase:Password must contain at least one lower case character.`
  }

  /**
   * Returns translated string for 'Password must contain at least one upper case character.'
   * @returns {string} The translated string
   */
  getUpperCaseRequiredText(): string {
    return $localize`:General|Text for password requirement@@general.text.passwordMustContainUpperCase:Password must contain at least one upper case character.`
  }

  /**
   * Returns translated string for 'Password must contain at least one number.'
   * @returns {string} The translated string
   */
  getNumberRequiredText(): string {
    return $localize`:General|Text for password requirement@@general.text.passwordMustContainNumber:Password must contain at least one number.`
  }

  /**
   * Returns translated string for 'Password must contain at least one symbol.'
   * @returns {string} The translated string
   */
  getSymbolRequiredText(): string {
    return $localize`:General|Text for password requirement@@general.text.passwordMustContainSymbol:Password must contain at least one symbol.`
  }

  /**
   * Returns translated string for 'Forgot your password?'
   * @returns {string} The translated string
   */
  getForgotPasswordText(): string {
    return $localize`:General|Text for input/information@@general.text.forgotPassword:Forgot your password?`
  }

  /**
   * Returns translated string for 'Reset Password'
   * @returns {string} The translated string
   */
  getResetPasswordText(): string {
    return $localize`:General|Text for input/information@@general.text.resetPassword:Reset Password`
  }

  /**
   * Returns translated string for 'Already have an account?'
   * @returns {string} The translated string
   */
  getAlreadyHaveAccountText(): string {
    return $localize`:General|Text for input/information@@general.text.alreadyHaveAccount:Already have an account?`
  }

  /**
   * Returns translated string for 'Request to reset password sent successfully'
   * @returns {string} The translated string
   */
  getRequestResetPasswordSuccessText(): string {
    return $localize`:General|Text for input/information@@general.text.requestResetPasswordSuccess:Request to reset password sent successfully`
  }

  /**
   * Returns translated string for 'Error'
   * @returns {string} The translated string
   */
  getErrorText(): string {
    return $localize`:General|Text for input/information@@general.text.error:Error`
  }

  /**
   * Returns translated string for 'Passwords do not match'
   * @returns {string} The translated string
   */
  getPasswordsDoNotMatchText(): string {
    return $localize`:General|Text for input/information@@general.text.passwordsDoNotMatch:Passwords do not match`
  }

  /**
   * Returns translated string for 'Password set successfully'
   * @returns {string} The translated string
   */
  getPasswordSetSuccessfullyText(): string {
    return $localize`:General|Text for input/information@@general.text.passwordSetSuccessfully:Password set successfully`
  }

  /**
   * Returns translated string for 'Set Password'
   * @returns {string} The translated string
   */
  getSetPasswordText(): string {
    return $localize`:General|Text for input/information@@general.text.setPassword:Set Password`
  }

  /**
   * Returns translated string for 'Please enter your desired password. Passwords must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one symbol.'
   * @returns {string} The translated string
   */
  getPasswordInstructionsText(): string {
    return $localize`:General|Text for input/information@@general.text.passwordInstructions:Please enter your desired password. Passwords must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one symbol.`
  }

  /**
   * Returns translated string for 'Confirm Password'
   * @returns {string} The translated string
   */
  getConfirmPasswordText(): string {
    return $localize`:General|Text for input/information@@general.text.confirmPassword:Confirm Password`
  }

  /**
   * Returns translated string for 'Name'
   * @returns {string} The translated string
   */
  getNameText(): string {
    return $localize`:General|Text for input/information@@general.text.name:Name`
  }

  /**
   * Returns translated string for 'Actions'
   * @returns {string} The translated string
   */
  getActionsText(): string {
    return $localize`:General|Text for input/information@@general.text.actions:Actions`
  }

  /**
   * Returns translated string for 'View'
   * @returns {string} The translated string
   */
  getViewText(): string {
    return $localize`:General|Text for input/information@@general.text.view:View`
  }

  /**
   * Returns translated string for 'Clone'
   * @returns {string} The translated string
   */
  getCloneText(): string {
    return $localize`:General|Text for input/information@@general.text.clone:Clone`
  }

  /**
   * Returns translated string for 'Activate'
   * @returns {string} The translated string
   */
  getActivateText(): string {
    return $localize`:General|Text for input/information@@general.text.activate:Activate`
  }

  /**
   * Returns translated string for 'Deactivate'
   * @returns {string} The translated string
   */
  getDeactivateCompanyText(): string {
    return $localize`:General|Text for input/information@@general.text.deactivate:Deactivate`
  }

  /**
   * Returns translated string for 'Your Products'
   * @returns {string} The translated string
   */
  getYourProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.yourProducts:Your Products`
  }

  /**
   * Returns translated string for 'Create Company'
   * @returns {string} The translated string
   */
  getCreateCompanyText(): string {
    return $localize`:General|Text for input/information@@general.text.createCompany:Create Company`
  }

  /**
   * Returns translated string for 'Company Name'
   * @returns {string} The translated string
   */
  getCompanyNameText(): string {
    return $localize`:General|Text for input/information@@general.text.companyName:Company Name`
  }

  /**
   * Returns translated string for 'Company Identification'
   * @returns {string} The translated string
   */
  getCompanyIdentificationText(): string {
    return $localize`:General|Text for input/information@@general.text.companyIdentification:Company Identification`
  }

  /**
   * Returns translated string for 'Phone Number'
   * @returns {string} The translated string
   */
  getPhoneNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.phoneNumber:Phone Number`
  }

  /**
   * Returns translated string for 'Global Location Number'
   * @returns {string} The translated string
   */
  getGlobalLocationNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.globalLocationNumber:Global Location Number`
  }

  /**
   * Returns translated string for 'Data Universal Numbering System'
   * @returns {string} The translated string
   */
  getDataUniversalNumberingSystemText(): string {
    return $localize`:General|Text for input/information@@general.text.dataUniversalNumberingSystem:Data Universal Numbering System`
  }

  /**
   * Returns translated string for 'Legal Entity Identifier'
   * @returns {string} The translated string
   */
  getLegalEntityIdentifierText(): string {
    return $localize`:General|Text for input/information@@general.text.legalEntityIdentifier:Legal Entity Identifier`
  }

  /**
   * Returns translated string for 'Country'
   * @returns {string} The translated string
   */
  getCountryText(): string {
    return $localize`:General|Text for input/information@@general.text.country:Country`
  }

  /**
   * Returns translated string for 'State'
   * @returns {string} The translated string
   */
  getStateText(): string {
    return $localize`:General|Text for input/information@@general.text.state:State`
  }

  /**
   * Returns translated string for 'City'
   * @returns {string} The translated string
   */
  getCityText(): string {
    return $localize`:General|Text for input/information@@general.text.city:City`
  }

  /**
   * Returns translated string for 'Something went wrong...'
   * @returns {string} The translated string
   */
  getSomethingWentWrongText(): string {
    return $localize`:General|Text for input/information@@general.text.somethingWentWrong:Something went wrong...`
  }

  /**
   * Returns translated string for 'An error has occurred. Please try again.'
   * @returns {string} The translated string
   */
  getErrorOccurredText(): string {
    return $localize`:General|Text for input/information@@general.text.errorOccurred:An error has occurred. Please try again.`
  }

  /**
   * Returns translated string for 'Companies'
   * @returns {string} The translated string
   */
  getCompaniesText(): string {
    return $localize`:General|Text for input/information@@general.text.companies:Companies`
  }

  /**
   * Returns translated string for 'Company status toggled successfully.'
   * @returns {string} The translated string
   */
  getCompanyStatusChangedText(): string {
    return $localize`:General|Text for input/information@@general.text.companyStatusChanged:Company status toggled successfully.`
  }

  /**
   * Returns translated string for 'Failed to toggle company status. Please try again.'
   * @returns {string} The translated string
   */
  getFailedToToggleCompanyStatusText(): string {
    return $localize`:General|Text for input/information@@general.text.failedToToggleCompanyStatus:'Failed to toggle company status. Please try again.'`
  }

  /**
   * Returns translated string for 'Company created successfully.'
   * @returns {string} The translated string
   */
  getCompanyCreatedSuccessfullyText(): string {
    return $localize`:General|Text for input/information@@general.text.companyCreatedSuccessfully:Company created successfully.`
  }

  /**
   * Returns translated string for 'Liability Disclaimer'
   * @returns {string} The translated string
   */
  getLiabilityDisclaimerText(): string {
    return $localize`:General|Text for input/information@@general.text.liabilityDisclaimer:Liability Disclaimer`
  }

  /**
   * Returns translated string for 'Popular Products'
   * @returns {string} The translated string
   */
  getPopularProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.popularProducts:Popular Products`
  }

  /**
   * Returns translated string for 'Other Name(s)'
   * @returns {string} The translated string
   */
  getOtherNamesText(): string {
    return $localize`:General|Text for input/information@@general.text.otherNames:Other Name(s)`
  }

  /**
   * Returns translated string for 'Enter other names'
   * @returns {string} The translated string
   */
  getEnterOtherNamesText(): string {
    return $localize`:General|Text for input/information@@general.text.enterOtherNames:Enter other names`
  }

  /**
   * Returns translated string for 'press enter to edit'
   * @returns {string} The translated string
   */
  getPressEnterToEditText(): string {
    return $localize`:General|Text for input/information@@general.text.pressEnterToEdit:press enter to edit`
  }

  /**
   * Returns translated string for 'Remove'
   * @returns {string} The translated string
   */
  getRemoveText(): string {
    return $localize`:General|Text for input/information@@general.text.remove:Remove`
  }

  /**
   * Returns translated string for 'New alias...'
   * @returns {string} The translated string
   */
  getNewAliasText(): string {
    return $localize`:General|Text for input/information@@general.text.newAlias:New alias...`
  }

  /**
   * Returns translated string for 'CAS Number(s)'
   * @returns {string} The translated string
   */
  getCasNumbersText(): string {
    return $localize`:General|Text for input/information@@general.text.casNumbers:CAS Number(s)`
  }

  /**
   * Returns translated string for 'New CAS number...'
   * @returns {string} The translated string
   */
  getNewCasNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.newCasNumber:New CAS number...`
  }

  /**
   * Returns translated string for 'Create Ingredient'
   * @returns {string} The translated string
   */
  getCreateIngredientText(): string {
    return $localize`:General|Text for input/information@@general.text.createIngredient:Create Ingredient`
  }

  /**
   * Returns translated string for 'Edit Ingredient'
   * @returns {string} The translated string
   */
  getEditIngredientText(): string {
    return $localize`:General|Text for input/information@@general.text.editIngredient:Edit Ingredient`
  }

  /**
   * Returns translated string for 'Ingredient created successfully.'
   * @returns {string} The translated string
   */
  getIngredientCreatedSuccessfullyText(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientCreatedSuccessfully:Ingredient created successfully.`
  }

  /**
   * Returns translated string for 'Ingredient updated successfully.'
   * @returns {string} The translated string
   */
  getIngredientUpdatedSuccessfullyText(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientUpdatedSuccessfully:Ingredient updated successfully.`
  }

  /**
   * Returns translated string for 'Ingredients'
   * @returns {string} The translated string
   */
  getIngredientsText(): string {
    return $localize`:General|Text for input/information@@general.text.ingredients:Ingredients`
  }

  /**
   * Returns translated string for 'Edit'
   * @returns {string} The translated string
   */
  getEditText(): string {
    return $localize`:General|Text for input/information@@general.text.edit:Edit`
  }

  /**
   * Returns translated string for 'CAS Number'
   * @returns {string} The translated string
   */
  getCasNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.casNumber:CAS Number`
  }

  /**
   * Returns translated string for 'Result(s)'
   * @returns {string} The translated string
   */
  getResultOptionalSText(): string {
    return $localize`:General|Text for input/information@@general.text.resultOptionalS:Result(s)`
  }

  /**
   * Returns translated string for 'First Page'
   * @returns {string} The translated string
   */
  getFirstPageText(): string {
    return $localize`:General|Text for input/information@@general.text.firstPage:First Page`
  }

  /**
   * Returns translated string for 'Records per page:'
   * @returns {string} The translated string
   */
  getItemsPerPageText(): string {
    return $localize`:General|Text for input/information@@general.text.itemsPerPage:Records per page:`
  }

  /**
   * Returns translated string for 'Last Page'
   * @returns {string} The translated string
   */
  getLastPageText(): string {
    return $localize`:General|Text for input/information@@general.text.lastPage:Last Page`
  }

  /**
   * Returns translated string for 'Next Page'
   * @returns {string} The translated string
   */
  getNextPageText(): string {
    return $localize`:General|Text for input/information@@general.text.nextPage:Next Page`
  }

  /**
   * Returns translated string for 'Previous Page'
   * @returns {string} The translated string
   */
  getPreviousPageText(): string {
    return $localize`:General|Text for input/information@@general.text.previousPage:Previous Page`
  }

  /**
   * Returns translated string for 'Page {page} of {amountPages}'
   * @param page The current page number
   * @param amountPages The total number of pages
   * @returns {string} The translated string
   */
  getRangeLabelText(page: number, amountPages: number): string {
    return $localize`:General|Text for input/information@@general.text.rangeLabel:Page ${page} of ${amountPages}`
  }

  /**
   * Returns translated string for 'Legal Disclaimer'
   * @returns {string} The translated string
   */
  getLegalDisclaimerText(): string {
    return $localize`:General|Text for input/information@@general.text.legalDisclaimer:Legal Disclaimer`
  }

  /**
   * Returns translated string for Vite Marketplace's Legal Disclaimer
   * @returns {string} The translated string
   */
  getLegalDisclaimerFullText(): string {
    return $localize`:General|Text for input/information@@general.text.legalDisclaimerText: The contents of the Vite Marketplace database are provided for informational support
    purposes only. While every effort has been made to ensure the accuracy and
    completeness of the information contained herein, users should not rely
    solely on the contents of this database for decision-making purposes. The
    information in this database is subject to change without notice and may not
    be current or complete. Users are advised to verify any information obtained
    from this database independently and to consult with appropriate
    professionals or experts before making any decisions based on its
    information. By accessing and using this database, you acknowledge and agree
    that neither the creators, maintainers, nor any affiliated entities or
    individuals are responsible for any errors or omissions, or for any loss or
    damage resulting from reliance on the contents of this database. All users
    assume full responsibility for any actions taken based on the information
    provided herein.`
  }

  /**
   * Returns translated string for 'Company Products'
   * @returns {string} The translated string
   */
  getCompanyProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.companyProducts:Company Products`
  }

  /**
   * Returns translated string for 'Products'
   * @returns {string} The translated string
   */
  getProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.products:Products`
  }

  /**
   * Returns translated string for 'Substances'
   * @returns {string} The translated string
   */
  getSubstancesText(): string {
    return $localize`:General|Text for input/information@@general.text.substances:Substances`
  }

  /**
   * Returns translated string for 'Components'
   * @returns {string} The translated string
   */
  getComponentsText(): string {
    return $localize`:General|Text for input/information@@general.text.components:Components`
  }

  /**
   * Returns translated string for 'Element of Concern'
   * @returns {string} The translated string
   */
  getElementOfConcernText(): string {
    return $localize`:General|Text for input/information@@general.text.elementOfConcern:Element of Concern`
  }

  /**
   * Returns translated string for 'Candidate List Version'
   * @returns {string} The translated string
   */
  getCandidateListVersionText(): string {
    return $localize`:General|Text for input/information@@general.text.candidateListVersion:Candidate List Version`
  }

  /**
   * Returns translated string for 'Candidate List Entry'
   * @returns {string} The translated string
   */
  getCandidateListEntryText(): string {
    return $localize`:General|Text for input/information@@general.text.candidateListEntry:Candidate List Entry`
  }

  /**
   * Returns translated string for 'Name of Substance'
   * @returns {string} The translated string
   */
  getNameOfSubstanceText(): string {
    return $localize`:General|Text for input/information@@general.text.nameOfSubstance:Name of Substance`
  }

  /**
   * Returns translated string for 'CAS #'
   * @returns {string} The translated string
   */
  getCasHashtagText(): string {
    return $localize`:General|Text for input/information@@general.text.casHashtag:CAS #`
  }

  /**
   * Returns translated string for 'UN #'
   * @returns {string} The translated string
   */
  getUnNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.unNumber:UN #`
  }

  /**
   * Returns translated string for 'Concentration / % mass'
   * @returns {string} The translated string
   */
  getConcentrationMassText(): string {
    return $localize`:General|Text for input/information@@general.text.concentrationMass:Concentration / % mass`
  }

  /**
   * Returns translated string for 'Min. Qty (tonnes)'
   * @returns {string} The translated string
   */
  getMinQtyText(): string {
    return $localize`:General|Text for input/information@@general.text.minQty:Min. Qty (tonnes)`
  }

  /**
   * Returns translated string for 'Reporting Threshold'
   * @returns {string} The translated string
   */
  getReportingThresholdText(): string {
    return $localize`:General|Text for input/information@@general.text.reportingThreshold:Reporting Threshold`
  }

  /**
   * Returns translated string for 'Molecular Weight'
   * @returns {string} The translated string
   */
  getMolecularWeightText(): string {
    return $localize`:General|Text for input/information@@general.text.molecularWeight:Molecular Weight`
  }

  /**
   * Returns translated string for 'EC Number'
   * @returns {string} The translated string
   */
  getEcNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.ecNumber:EC Number`
  }

  /**
   * Returns translated string for 'ChEMBL ID'
   * @returns {string} The translated string
   */
  getChemblIdText(): string {
    return $localize`:General|Text for input/information@@general.text.chemblId:ChEMBL ID`
  }

  /**
   * Returns translated string for 'Hazard Category'
   * @returns {string} The translated string
   */
  getHazardCategoryText(): string {
    return $localize`:General|Text for input/information@@general.text.hazardCategory:Hazard Category`
  }

  /**
   * Returns translated string for 'GHS Classifications'
   * @returns {string} The translated string
   */
  getGhsClassificationsText(): string {
    return $localize`:General|Text for input/information@@general.text.ghsClassifications:GHS Classifications`
  }

  /**
   * Returns translated string for 'N/A'
   * @returns {string} The translated string
   */
  getNaText(): string {
    return $localize`:General|Text for input/information@@general.text.na:N/A`
  }
  /**
   * Returns translated string for 'Other'
   * @returns {string} The translated string
   */
  getOtherText(): string {
    return $localize`:General|Text for input/information@@general.text.other:Other`
  }

  /**
   * Returns translated string for 'Component'
   * @returns {string} The translated string
   */
  getComponentText(): string {
    return $localize`:General|Text for input/information@@general.text.component:Component`
  }

  /**
   * Returns translated string for 'Identifiers'
   * @returns {string} The translated string
   */
  getIdentifiersText(): string {
    return $localize`:General|Text for input/information@@general.text.identifiers:Identifiers`
  }

  /**
   * Returns translated string for 'Article Name'
   * @returns {string} The translated string
   */
  getArticleNameText(): string {
    return $localize`:General|Text for input/information@@general.text.articleName:Article Name`
  }

  /**
   * Returns translated string for 'Brand'
   * @returns {string} The translated string
   */
  getBrandText(): string {
    return $localize`:General|Text for input/information@@general.text.brand:Brand`
  }

  /**
   * Returns translated string for 'Model/SKU'
   * @returns {string} The translated string
   */
  getModelSkuText(): string {
    return $localize`:General|Text for input/information@@general.text.modelSku:Model/SKU`
  }

  /**
   * Returns translated string for 'Serial Number'
   * @returns {string} The translated string
   */
  getSerialNumberText(): string {
    return $localize`:General|Text for input/information@@general.text.serialNumber:Serial Number`
  }

  /**
   * Returns translated string for 'Type'
   * @returns {string} The translated string
   */
  getTypeText(): string {
    return $localize`:General|Text for input/information@@general.text.type:Type`
  }

  /**
   * Returns translated string for 'GS1 Identifier'
   * @returns {string} The translated string
   */
  getGs1IdentifierText(): string {
    return $localize`:General|Text for input/information@@general.text.gs1Identifier:GS1 Identifier`
  }

  /**
   * Returns translated string for 'Barcode Identifier'
   * @returns {string} The translated string
   */
  getBarcodeIdentifierText(): string {
    return $localize`:General|Text for input/information@@general.text.barcodeIdentifier:Barcode Identifier`
  }

  /**
   * Returns translated string for 'Other Article Identifier'
   * @returns {string} The translated string
   */
  getOtherArticleIdentifierText(): string {
    return $localize`:General|Text for input/information@@general.text.otherArticleIdentifier:Other Article Identifier`
  }

  /**
   * Returns translated string for 'Article Category'
   * @returns {string} The translated string
   */
  getArticleCategoryText(): string {
    return $localize`:General|Text for input/information@@general.text.articleCategory:Article Category`
  }

  /**
   * Returns translated string for 'Product Category'
   * @returns {string} The translated string
   */
  getProductCategoryText(): string {
    return $localize`:General|Text for input/information@@general.text.productCategory:Product Category`
  }

  /**
   * Returns translated string for 'Find Category'
   * @returns {string} The translated string
   */
  getFindCategoryText(): string {
    return $localize`:General|Text for input/information@@general.text.findCategory:Find Category`
  }

  /**
   * Returns translated string for 'Category Name'
   * @returns {string} The translated string
   */
  getCategoryNameText(): string {
    return $localize`:General|Text for input/information@@general.text.categoryName:Category Name`
  }

  /**
   * Returns translated string for 'Category Code'
   * @returns {string} The translated string
   */
  getCategoryCodeText(): string {
    return $localize`:General|Text for input/information@@general.text.categoryCode:Category Code`
  }

  /**
   * Returns translated string for 'Code Description'
   * @returns {string} The translated string
   */
  getCodeDescriptionText(): string {
    return $localize`:General|Text for input/information@@general.text.codeDescription:Code Description`
  }

  /**
   * Returns translated string for 'Produced in Canada'
   * @returns {string} The translated string
   */
  getProducedInCanadaText(): string {
    return $localize`:General|Text for input/information@@general.text.producedInCanada:Produced in Canada`
  }

  /**
   * Returns translated string for 'Characteristics'
   * @returns {string} The translated string
   */
  getCharacteristicsText(): string {
    return $localize`:General|Text for input/information@@general.text.characteristics:Characteristics`
  }

  /**
   * Returns translated string for 'Diameter'
   * @returns {string} The translated string
   */
  getDiameterText(): string {
    return $localize`:General|Text for input/information@@general.text.diameter:Diameter`
  }

  /**
   * Returns translated string for 'Width'
   * @returns {string} The translated string
   */
  getWidthText(): string {
    return $localize`:General|Text for input/information@@general.text.width:Width`
  }

  /**
   * Returns translated string for 'Height'
   * @returns {string} The translated string
   */
  getHeightText(): string {
    return $localize`:General|Text for input/information@@general.text.height:Height`
  }

  /**
   * Returns translated string for 'Weight'
   * @returns {string} The translated string
   */
  getWeightText(): string {
    return $localize`:General|Text for input/information@@general.text.weight:Weight`
  }

  /**
   * Returns translated string for 'Safe Use Instructions'
   * @returns {string} The translated string
   */
  getSafeUseInstructionsText(): string {
    return $localize`:General|Text for input/information@@general.text.safeUseInstructions:Safe Use Instructions`
  }

  /**
   * Returns translated string for 'Recycling Instructions'
   * @returns {string} The translated string
   */
  getRecyclingInstructionsText(): string {
    return $localize`:General|Text for input/information@@general.text.recyclingInstructions:Recycling Instructions`
  }

  /**
   * Returns translated string for 'Safety Data Sheet'
   * @returns {string} The translated string
   */
  getSafetyDataSheetText(): string {
    return $localize`:General|Text for input/information@@general.text.safetyDataSheet:Safety Data Sheet`
  }

  /**
   * Returns translated string for 'Chemistry'
   * @returns {string} The translated string
   */
  getChemistryText(): string {
    return $localize`:General|Text for input/information@@general.text.chemistry:Chemistry`
  }

  /**
   * Returns translated string for the description of the navigation tree
   * @returns {string} The translated string
   */
  getNavigationTreeDescriptionText(): string {
    return $localize`:General|Description for navigation tree@@general.text.navigationTreeDescription:This navigation tree shows the hierarchial structure of a product and its components. You can expand or collapse nodes to navigate through the hierarchy and click on a node to display its information.`
  }

  /**
   * Returns translated string for 'This ingredient was optionally disclosed.'
   * @returns {string} The translated string
   */
  getOptionalDisclosureText(): string {
    return $localize`:General|Text for optional disclosure@@general.text.optionalDisclosure:This ingredient was optionally disclosed.`
  }

  /**
   * Returns translated string for 'Concentration Range'
   * @returns {string} The translated string
   */
  getConcentrationRangeText(): string {
    return $localize`:General|Text for concentration range@@general.text.concentrationRange:Concentration Range`
  }

  /**
   * Returns translated string for 'May Contain'
   * @returns {string} The translated string
   */
  getMayContainText(): string {
    return $localize`:General|Text for may contain@@general.text.mayContain:May Contain`
  }

  /**
   * Returns translated string for 'Product Picture'
   * @returns {string} The translated string
   */
  getProductPictureText(): string {
    return $localize`:General|Text for product picture@@general.text.productPicture:Product Picture`
  }

  /**
   * Returns translated string for 'CAS'
   * @returns {string} The translated string
   */
  getCasText(): string {
    return $localize`:General|Text for CAS number@@general.text.cas:CAS`
  }

  /**
   * Returns translated string for 'Ingredient-Related Certifications'
   * @returns {string} The translated string
   */
  getIngredientRelatedCertificationsText(): string {
    return $localize`:General|Text for ingredient-related certifications@@general.text.ingredientRelatedCertifications:Ingredient-Related Certifications`
  }

  /**
   * Returns translated string for 'Tap each icon to learn more'
   * @returns {string} The translated string
   */
  getTapEachIconText(): string {
    return $localize`:General|Text for tapping each icon to learn more@@general.text.tapEachIcon:Tap each icon to learn more`
  }

  /**
   * Returns translated string for 'Full Ingredient Disclosure'
   * @returns {string} The translated string
   */
  getFullIngredientDisclosureText(): string {
    return $localize`:General|Text for full ingredient disclosure@@general.text.fullIngredientDisclosure:Full Ingredient Disclosure`
  }

  /**
   * Returns translated string for 'This product has full ingredient disclosure.'
   * @returns {string} The translated string
   */
  getFullIngredientDisclosureDescriptionText(): string {
    return $localize`:General|Description for full ingredient disclosure@@general.text.fullIngredientDisclosureDescription:This product has full ingredient disclosure.`
  }

  /**
   * Returns translated string for 'Partial Ingredient Disclosure'
   * @returns {string} The translated string
   */
  getPartialIngredientDisclosureText(): string {
    return $localize`:General|Text for partial ingredient disclosure@@general.text.partialIngredientDisclosure:Partial Ingredient Disclosure`
  }

  /**
   * Returns translated string for 'This product does not have full ingredient disclosure.'
   * @returns {string} The translated string
   */
  getPartialIngredientDisclosureDescriptionText(): string {
    return $localize`:General|Description for partial ingredient disclosure@@general.text.partialIngredientDisclosureDescription:This product does not have full ingredient disclosure.`
  }

  /**
   * Returns translated string for 'SKU'
   * @returns {string} The translated string
   */
  getSKUText(): string {
    return $localize`:General|Text for SKU@@general.text.sku:SKU`
  }

  /**
   * Returns translated string for 'Batch'
   * @returns {string} The translated string
   */
  getBatchText(): string {
    return $localize`:General|Text for batch@@general.text.batch:Batch`
  }

  /**
   * Returns translated string for 'Upper Batch Range'
   * @returns {string} The translated string
   */
  getUpperBatchRangeText(): string {
    return $localize`:General|Text for upper batch range@@general.text.upperBatchRange:Upper Batch Range`
  }

  /**
   * Returns translated string for 'Lower Batch Range'
   * @returns {string} The translated string
   */
  getLowerBatchRangeText(): string {
    return $localize`:General|Text for lower batch range@@general.text.lowerBatchRange:Lower Batch Range`
  }

  /**
   * Returns translated string for 'Upper Serial Range'
   * @returns {string} The translated string
   */
  getUpperSerialRangeText(): string {
    return $localize`:General|Text for upper serial range@@general.text.upperSerialRange:Upper Serial Range`
  }

  /**
   * Returns translated string for 'Lower Serial Range'
   * @returns {string} The translated string
   */
  getLowerSerialRangeText(): string {
    return $localize`:General|Text for lower serial range@@general.text.lowerSerialRange:Lower Serial Range`
  }

  /**
   * Returns translated string for 'to'
   * @returns {string} The translated string
   */
  getToText(): string {
    return $localize`:General|Text for to@@general.text.to:to`
  }

  /**
   * Returns translated string for 'Serial'
   * @returns {string} The translated string
   */
  getSerialText(): string {
    return $localize`:General|Text for serial@@general.text.serial:Serial`
  }

  /**
   * Returns translated string for 'View Full Chemical Breakdown'
   * @returns {string} The translated string
   */
  getViewFullComponentBreakdownText(): string {
    return $localize`:General|Text for view full component breakdown@@general.text.viewFullChemicalBreakdown:View Full Component Breakdown`
  }

  /**
   * Returns translated string for 'Sustainability Highlights'
   * @returns {string} The translated string
   */
  getSustainabilityHighlightsText(): string {
    return $localize`:General|Text for sustainability highlights@@general.text.sustainabilityHighlights:Sustainability Highlights`
  }

  /**
   * Returns translated string for 'The last row represents aggregated results (where applicable) for the product from all substances.'
   * @returns {string} The translated string
   */
  getLastRowAggregatedResultsText(): string {
    return $localize`:General|Text for last row aggregated results@@general.text.lastRowAggregatedResults:The last row represents aggregated results (where applicable) for the product from all substances.`
  }

  /**
   * Returns translated string for 'Regulator Info'
   * @returns {string} The translated string
   */
  getRegulatorInfoText(): string {
    return $localize`:General|Text for regulator info@@general.text.regulatorInfo:Regulator Info`
  }

  /**
   * Returns translated string for 'Canada Domestic Substances List'
   * @returns {string} The translated string
   */
  getCanadaDomesticSubstancesListText(): string {
    return $localize`:General|Text for Canada Domestic Substances List@@general.text.canadaDomesticSubstancesList:Canada Domestic Substances List`
  }

  /**
   * Returns translated string for 'CEPA'
   * @returns {string} The translated string
   */
  getCepaText(): string {
    return $localize`:General|Text for CEPA@@general.text.cepa:CEPA`
  }

  /**
   * Returns translated string for 'CBSA'
   * @returns {string} The translated string
   */
  getCbsaText(): string {
    return $localize`:General|Text for CBSA@@general.text.cbsa:CBSA`
  }

  /**
   * Returns translated string for 'US'
   * @returns {string} The translated string
   */
  getUsText(): string {
    return $localize`:General|Text for US@@general.text.us:US`
  }

  /**
   * Returns translated string for 'DOT/TDG for Product'
   * @returns {string} The translated string
   */
  getDotTdgForProductText(): string {
    return $localize`:General|Text for DOT/TDG for Product@@general.text.dotTdgForProduct:DOT/TDG for Product`
  }

  /**
   * Returns translated string for 'WHMIS each ingredient'
   * @returns {string} The translated string
   */
  getWhmisEachIngredientText(): string {
    return $localize`:General|Text for WHMIS each ingredient@@general.text.whmisEachIngredient:WHMIS each ingredient`
  }

  /**
   * Returns translated string for 'Precursor'
   * @returns {string} The translated string
   */
  getPrecursorText(): string {
    return $localize`:General|Text for Precursor@@general.text.precursor:Precursor`
  }

  /**
   * Returns translated string for 'Waste'
   * @returns {string} The translated string
   */
  getWasteText(): string {
    return $localize`:General|Text for Waste@@general.text.waste:Waste`
  }

  /**
   * Returns translated string for 'Is Industrial, Commercial and Institutional Waste'
   * @returns {string} The translated string
   */
  getIsIciWasteText(): string {
    return $localize`:General|Text for Is ICI Waste@@general.text.isIciWaste:Is Industrial, Commercial and Institutional Waste`
  }

  /**
   * Returns translated string for 'Is Construction, Demolition, and Renovation Waste'
   * @returns {string} The translated string
   */
  getIsCdrWasteText(): string {
    return $localize`:General|Text for Is CDR Waste@@general.text.isCdrWaste:Is Construction, Demolition, and Renovation Waste`
  }

  /**
   * Returns translated string for 'WHMIS Class for Product'
   * @returns {string} The translated string
   */
  getWhmisClassForProductText(): string {
    return $localize`:General|Text for WHMIS Class for Product@@general.text.whmisClassForProduct:WHMIS Class for Product`
  }

  /**
   * Returns translated string for 'HS'
   * @returns {string} The translated string
   */
  getHsText(): string {
    return $localize`:General|Text for HS@@general.text.hs:HS`
  }

  /**
   * Returns translated string for 'Comments'
   * @returns {string} The translated string
   */
  getCommentsText(): string {
    return $localize`:General|Text for Comments@@general.text.comments:Comments`
  }

  /**
   * Returns translated string for 'DSL (Y/N)'
   * @returns {string} The translated string
   */
  getDslText(): string {
    return $localize`:General|Text for DSL (Y/N)@@general.text.dsl:DSL (Y/N)`
  }

  /**
   * Returns translated string for 'NDSL (Y/N)'
   * @returns {string} The translated string
   */
  getNdslText(): string {
    return $localize`:General|Text for NDSL (Y/N)@@general.text.ndsl:NDSL (Y/N)`
  }

  /**
   * Returns translated string for 'R-ICL (Y/N)'
   * @returns {string} The translated string
   */
  getRicLText(): string {
    return $localize`:General|Text for R-ICL (Y/N)@@general.text.ricl:R-ICL (Y/N)`
  }

  /**
   * Returns translated string for 'TSL (Y/N)'
   * @returns {string} The translated string
   */
  getTslText(): string {
    return $localize`:General|Text for TSL (Y/N)@@general.text.tsl:TSL (Y/N)`
  }

  /**
   * Returns translated string for 'Max %'
   * @returns {string} The translated string
   */
  getMaxPercentageText(): string {
    return $localize`:General|Text for Max %@@general.text.maxPercentage:Max %`
  }

  /**
   * Returns translated string for 'Trigger Qty in Kg'
   * @returns {string} The translated string
   */
  getTriggerQtyInKgText(): string {
    return $localize`:General|Text for Trigger Qty in Kg@@general.text.triggerQtyInKg:Trigger Qty in Kg`
  }

  /**
   * Returns translated string for 'Next Schedule'
   * @returns {string} The translated string
   */
  getNextScheduleText(): string {
    return $localize`:General|Text for Next Schedule@@general.text.nextSchedule:Next Schedule`
  }

  /**
   * Returns translated string for 'Schedule VI Precursor'
   * @returns {string} The translated string
   */
  getScheduleViPrecursorText(): string {
    return $localize`:General|Text for Schedule VI Precursor@@general.text.scheduleViPrecursor:Schedule VI Precursor`
  }

  /**
   * Returns translated string for 'NSN #'
   * @returns {string} The translated string
   */
  getNsnNumberText(): string {
    return $localize`:General|Text for NSN #@@general.text.nsnNumber:NSN #`
  }

  /**
   * Returns translated string for 'SNAc (Y/N)'
   * @returns {string} The translated string
   */
  getSnacText(): string {
    return $localize`:General|Text for SNAc (Y/N)@@general.text.snac:SNAc (Y/N)`
  }

  /**
   * Returns translated string for 'Ministerial Condition (Y/N)'
   * @returns {string} The translated string
   */
  getMinisterialConditionText(): string {
    return $localize`:General|Text for Ministerial Condition (Y/N)@@general.text.ministerialCondition:Ministerial Condition (Y/N)`
  }

  /**
   * Returns translated string for 'NPRI (Y/N)'
   * @returns {string} The translated string
   */
  getNpriText(): string {
    return $localize`:General|Text for NPRI (Y/N)@@general.text.npri:NPRI (Y/N)`
  }

  /**
   * Returns translated string for 'E2 (Y/N)'
   * @returns {string} The translated string
   */
  getE2Text(): string {
    return $localize`:General|Text for E2 (Y/N)@@general.text.e2:E2 (Y/N)`
  }

  /**
   * Returns translated string for 'Export Control (Y/N)'
   * @returns {string} The translated string
   */
  getExportControlText(): string {
    return $localize`:General|Text for Export Control (Y/N)@@general.text.exportControl:Export Control (Y/N)`
  }

  /**
   * Returns translated string for 'Export Control Permit'
   * @returns {string} The translated string
   */
  getExportControlPermitText(): string {
    return $localize`:General|Text for Export Control Permit@@general.text.exportControlPermit:Export Control Permit`
  }

  /**
   * Returns translated string for 'Import Control List (Y/N)'
   * @returns {string} The translated string
   */
  getImportControlListText(): string {
    return $localize`:General|Text for Import Control List (Y/N)@@general.text.importControlList:Import Control List (Y/N)`
  }

  /**
   * Returns translated string for 'Import Control List Permit'
   * @returns {string} The translated string
   */
  getImportControlListPermitText(): string {
    return $localize`:General|Text for Import Control List Permit@@general.text.importControlListPermit:Import Control List Permit`
  }

  /**
   * Returns translated string for 'TSCA INV (Y/N)'
   * @returns {string} The translated string
   */
  getTscaInvText(): string {
    return $localize`:General|Text for TSCA INV (Y/N)@@general.text.tscaInv:TSCA INV (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity IARC (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityIARCText(): string {
    return $localize`:General|Text for Carcinogenicity IARC (Y/N)@@general.text.carcinogenicityIARC:Carcinogenicity IARC (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity OSHA (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityOSHAText(): string {
    return $localize`:General|Text for Carcinogenicity OSHA (Y/N)@@general.text.carcinogenicityOSHA:Carcinogenicity OSHA (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity NTP (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityNTPText(): string {
    return $localize`:General|Text for Carcinogenicity NTP (Y/N)@@general.text.carcinogenicityNTP:Carcinogenicity NTP (Y/N)`
  }

  /**
   * Returns translated string for 'TSCA 4 (Y/N)'
   * @returns {string} The translated string
   */
  getTsca4Text(): string {
    return $localize`:General|Text for TSCA 4 (Y/N)@@general.text.tsca4:TSCA 4 (Y/N)`
  }

  /**
   * Returns translated string for 'SNUR (Y/N)'
   * @returns {string} The translated string
   */
  getSnurText(): string {
    return $localize`:General|Text for SNUR (Y/N)@@general.text.snur:SNUR (Y/N)`
  }

  /**
   * Returns translated string for 'TSCA 5e (Y/N)'
   * @returns {string} The translated string
   */
  getTsca5EText(): string {
    return $localize`:General|Text for TSCA 5e (Y/N)@@general.text.tsca5e:TSCA 5e (Y/N)`
  }

  /**
   * Returns translated string for 'PMN #'
   * @returns {string} The translated string
   */
  getPmnNumberText(): string {
    return $localize`:General|Text for PMN #@@general.text.pmnNumber:PMN # (Y/N)`
  }

  /**
   * Returns translated string for 'Notice of Commencement'
   * @returns {string} The translated string
   */
  getNoticeOfCommencementText(): string {
    return $localize`:General|Text for Notice of Commencement@@general.text.noticeOfCommencement:Notice of Commencement`
  }

  /**
   * Returns translated string for 'TSCA 6 (Y/N)'
   * @returns {string} The translated string
   */
  getTsca6Text(): string {
    return $localize`:General|Text for TSCA 6 (Y/N)@@general.text.tsca6:TSCA 6 (Y/N)`
  }

  /**
   * Returns translated string for 'CDR'
   * @returns {string} The translated string
   */
  getCdrText(): string {
    return $localize`:General|Text for CDR@@general.text.cdr:Construction, Demolition, and Renovation Waste`
  }

  /**
   * Returns translated string for 'ICI'
   * @returns {string} The translated string
   */
  getIciText(): string {
    return $localize`:General|Text for ICI@@general.text.ici:Industrial, Commercial and Institutional Waste`
  }

  /**
   * Returns translated string for 'CERCLA (lb)'
   * @returns {string} The translated string
   */
  getCerclaText(): string {
    return $localize`:General|Text for CERCLA (lb)@@general.text.cercla:CERCLA (lb)`
  }

  /**
   * Returns translated string for 'SARA 302 (TPQ)'
   * @returns {string} The translated string
   */
  getSara302Text(): string {
    return $localize`:General|Text for SARA 302 (TPQ)@@general.text.sara302:SARA 302 (TPQ)`
  }

  /**
   * Returns translated string for 'SARA 311/312'
   * @returns {string} The translated string
   */
  getSara311312Text(): string {
    return $localize`:General|Text for SARA 311/312@@general.text.sara311312:SARA 311/312`
  }

  /**
   * Returns translated string for 'SARA 313'
   * @returns {string} The translated string
   */
  getSara313Text(): string {
    return $localize`:General|Text for SARA 313@@general.text.sara313:SARA 313`
  }

  /**
   * Returns translated string for 'RCRA (code)'
   * @returns {string} The translated string
   */
  getRcraText(): string {
    return $localize`:General|Text for RCRA (code)@@general.text.rcra:RCRA (code)`
  }

  /**
   * Returns translated string for 'Prop 65 (type)'
   * @returns {string} The translated string
   */
  getProp65Text(): string {
    return $localize`:General|Text for Prop 65 (type)@@general.text.prop65:Prop 65 (type)`
  }

  /**
   * Returns translated string for 'R-ICL (Y/N)'
   * @returns {string} The translated string
   */
  getRiclText(): string {
    return $localize`:General|Text for R-ICL (Y/N)@@general.text.ricl:R-ICL (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity IARC (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityIarcText(): string {
    return $localize`:General|Text for Carcinogenicity IARC (Y/N)@@general.text.carcinogenicityIARC:Carcinogenicity IARC (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity OSHA (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityOshaText(): string {
    return $localize`:General|Text for Carcinogenicity OSHA (Y/N)@@general.text.carcinogenicityOSHA:Carcinogenicity OSHA (Y/N)`
  }

  /**
   * Returns translated string for 'Carcinogenicity NTP (Y/N)'
   * @returns {string} The translated string
   */
  getCarcinogenicityNtpText(): string {
    return $localize`:General|Text for Carcinogenicity NTP (Y/N)@@general.text.carcinogenicityNTP:Carcinogenicity NTP (Y/N)`
  }

  /**
   * Returns translated string for 'Control'
   * @returns {string} The translated string
   */
  getControlText(): string {
    return $localize`:General|Text for Control@@general.text.control:Control`
  }

  /**
   * Returns translated string for 'TRI (Y/N)'
   * @returns {string} The translated string
   */
  getTriText(): string {
    return $localize`:General|Text for TRI (Y/N)@@general.text.tri:TRI (Y/N)`
  }

  /**
   * Returns translated string for 'Proper Shipping Name'
   * @returns {string} The translated string
   */
  getProperShippingNameText(): string {
    return $localize`:General|Text for Proper Shipping Name@@general.text.properShippingName:Proper Shipping Name`
  }

  /**
   * Returns translated string for 'Classification'
   * @returns {string} The translated string
   */
  getClassificationText(): string {
    return $localize`:General|Text for Classification@@general.text.classification:Classification`
  }

  /**
   * Returns translated string for 'Packing Group'
   * @returns {string} The translated string
   */
  getPackingGroupText(): string {
    return $localize`:General|Text for Packing Group@@general.text.packingGroup:Packing Group`
  }

  /**
   * Returns translated string for 'Limited Quantity Ground (LQ Ground)'
   * @returns {string} The translated string
   */
  getLtdQtyGroundText(): string {
    return $localize`:General|Text for Limited Quantity Ground (LQ Ground)@@general.text.ltdQtyGround:Limited Quantity Ground (LQ Ground)`
  }

  /**
   * Returns translated string for 'Limited Quantity Air (LQ Air)'
   * @returns {string} The translated string
   */
  getLtdQtyAirText(): string {
    return $localize`:General|Text for Limited Quantity Air (LQ Air)@@general.text.ltdQtyAir:Limited Quantity Air (LQ Air)`
  }

  /**
   * Returns translated string for 'Limited Quantity Cargo (LQ Cargo)'
   * @returns {string} The translated string
   */
  getLtdQtyCargoText(): string {
    return $localize`:General|Text for Limited Quantity Cargo (LQ Cargo)@@general.text.ltdQtyCargo:Limited Quantity Cargo (LQ Cargo)`
  }

  /**
   * Returns translated string for 'Marine Pollutant (Y/N)'
   * @returns {string} The translated string
   */
  getMarinePollutantText(): string {
    return $localize`:General|Text for Marine Pollutant (Y/N)@@general.text.marinePollutant:Marine Pollutant (Y/N)`
  }

  /**
   * Returns translated string for 'Emergency Response Assistance Plan (ERAP)'
   * @returns {string} The translated string
   */
  getErapText(): string {
    return $localize`:General|Text for Emergency Response Assistance Plan (ERAP)@@general.text.erap:Emergency Response Assistance Plan (ERAP)`
  }

  /**
   * Returns translated string for 'ERAP Number'
   * @returns {string} The translated string
   */
  getErapNumberText(): string {
    return $localize`:General|Text for ERAP Number@@general.text.erapNumber:ERAP Number`
  }

  /**
   * Returns translated string for 'ERAP Expiry Date'
   * @returns {string} The translated string
   */
  getErapExpiryDateText(): string {
    return $localize`:General|Text for ERAP Expiry Date@@general.text.erapExpiryDate:ERAP Expiry Date`
  }

  /**
   * Returns translated string for 'Transport Publication (TP) / Canadian General Standards Board (CGSB) Standard'
   * @returns {string} The translated string
   */
  getTpOrCgsbStdText(): string {
    return $localize`:General|Text for Transport Publication (TP) / Canadian General Standards Board (CGSB) Standard@@general.text.tpOrCgsbStd:Transport Publication (TP) / Canadian General Standards Board (CGSB) Standard`
  }

  /**
   * Returns translated string for 'Temperature Control (Y/N)'
   * @returns {string} The translated string
   */
  getTemperatureControlText(): string {
    return $localize`:General|Text for Temperature Control (Y/N)@@general.text.temperatureControl:Temperature Control (Y/N)`
  }

  /**
   * Returns translated string for 'Flash Point (°C)'
   * @returns {string} The translated string
   */
  getFlashPointCText(): string {
    return $localize`:General|Text for Flash Point (°C)@@general.text.flashPointC:Flash Point (°C)`
  }

  /**
   * Returns translated string for 'Freeze Protection (Y/N)'
   * @returns {string} The translated string
   */
  getFreezeProtectText(): string {
    return $localize`:General|Text for Freeze Protection (Y/N)@@general.text.freezeProtect:Freeze Protection (Y/N)`
  }

  /**
   * Returns translated string for 'WHMIS Classes'
   * @returns {string} The translated string
   */
  getWhmisClassesText(): string {
    return $localize`:General|Text for WHMIS Classes@@general.text.whmisClasses:WHMIS Classes`
  }

  /**
   * Returns translated string for 'Supplier SDS #'
   * @returns {string} The translated string
   */
  getSupplierSdsNumberText(): string {
    return $localize`:General|Text for Supplier SDS #@@general.text.supplierSdsNumber:Supplier SDS #`
  }

  /**
   * Returns translated string for 'Supplier SDS Date'
   * @returns {string} The translated string
   */
  getSupplierSdsDateText(): string {
    return $localize`:General|Text for Supplier SDS Date@@general.text.supplierSdsDate:Supplier SDS Date`
  }

  /**
   * Returns translated string for 'Drug Precursor (Y/N)'
   * @returns {string} The translated string
   */
  getDrugPrecursorText(): string {
    return $localize`:General|Text for Drug Precursor (Y/N)@@general.text.drugPrecursor:Drug Precursor (Y/N)`
  }

  /**
   * Returns translated string for 'DEA #'
   * @returns {string} The translated string
   */
  getDeaNumberText(): string {
    return $localize`:General|Text for DEA #@@general.text.deaNumber:DEA #`
  }

  /**
   * Returns translated string for 'Restricted Component (Y/N)'
   * @returns {string} The translated string
   */
  getRestrictedComponentText(): string {
    return $localize`:General|Text for Restricted Component (Y/N)@@general.text.restrictedComponent:Restricted Component (Y/N)`
  }

  /**
   * Returns translated string for 'Security Plan (Y/N)'
   * @returns {string} The translated string
   */
  getSecurityPlanText(): string {
    return $localize`:General|Text for Security Plan (Y/N)@@general.text.securityPlan:Security Plan (Y/N)`
  }

  /**
   * Returns translated string for 'CWC (Chemical Weapons Convention)'
   * @returns {string} The translated string
   */
  getCwcText(): string {
    return $localize`:General|Text for CWC (Chemical Weapons Convention)@@general.text.cwc:CWC (Chemical Weapons Convention)`
  }

  /**
   * Returns translated string for 'Waste Code'
   * @returns {string} The translated string
   */
  getWasteCodeText(): string {
    return $localize`:General|Text for Waste Code@@general.text.wasteCode:Waste Code`
  }

  /**
   * Returns translated string for 'PH Class'
   * @returns {string} The translated string
   */
  getPhClassText(): string {
    return $localize`:General|Text for PH Class@@general.text.phClass:PH Class`
  }

  /**
   * Returns translated string for 'PH SDS #'
   * @returns {string} The translated string
   */
  getPhSdsNumberText(): string {
    return $localize`:General|Text for PH SDS #@@general.text.phSdsNumber:PH SDS #`
  }

  /**
   * Returns translated string for 'PH SDS Date'
   * @returns {string} The translated string
   */
  getPhSdsDateText(): string {
    return $localize`:General|Text for PH SDS Date@@general.text.phSdsDate:PH SDS Date`
  }

  /**
   * Returns translated string for 'Label #'
   * @returns {string} The translated string
   */
  getLabelNumberText(): string {
    return $localize`:General|Text for Label #@@general.text.labelNumber:Label #`
  }

  /**
   * Returns translated string for 'Label Date'
   * @returns {string} The translated string
   */
  getLabelDateText(): string {
    return $localize`:General|Text for Label Date@@general.text.labelDate:Label Date`
  }

  /**
   * Returns translated string for 'Importer of Record'
   * @returns {string} The translated string
   */
  getImporterOfRecordText(): string {
    return $localize`:General|Text for Importer of Record@@general.text.importerOfRecord:Importer of Record`
  }

  /**
   * Returns translated string for 'HS Code (Canada)'
   * @returns {string} The translated string
   */
  getHsCodeCanadaText(): string {
    return $localize`:General|Text for HS Code (Canada)@@general.text.hsCodeCanada:HS Code (Canada)`
  }

  /**
   * Returns translated string for 'Duty Rate % (Canada)'
   * @returns {string} The translated string
   */
  getDutyRateCanadaText(): string {
    return $localize`:General|Text for Duty Rate % (Canada)@@general.text.dutyRateCanada:Duty Rate % (Canada)`
  }

  /**
   * Returns translated string for 'HS Code (USA)'
   * @returns {string} The translated string
   */
  getUsaHsCodeText(): string {
    return $localize`:General|Text for HS Code (USA)@@general.text.usaHsCode:HS Code (USA)`
  }

  /**
   * Returns translated string for 'Duty Rate % (USA)'
   * @returns {string} The translated string
   */
  getDutyRateUsaText(): string {
    return $localize`:General|Text for Duty Rate % (USA)@@general.text.dutyRateUsa:Duty Rate % (USA)`
  }

  /**
   * Returns translated string for 'CFIA AIRS Code'
   * @returns {string} The translated string
   */
  getCfiaAirsCodeText(): string {
    return $localize`:General|Text for CFIA AIRS Code@@general.text.cfiaAirsCode:CFIA AIRS Code`
  }

  /**
   * Returns translated string for 'Tariff'
   * @returns {string} The translated string
   */
  getTariffText(): string {
    return $localize`:General|Text for Tariff@@general.text.tariff:Tariff`
  }

  /**
   * Returns translated string for 'Country of Origin'
   * @returns {string} The translated string
   */
  getCountryOfOriginText(): string {
    return $localize`:General|Text for Country of Origin@@general.text.countryOfOrigin:Country of Origin`
  }

  /**
   * Returns translated string for 'Certificate on File (Y/N)'
   * @returns {string} The translated string
   */
  getCertificateOnFileText(): string {
    return $localize`:General|Text for Certificate on File (Y/N)@@general.text.certificateOnFile:Certificate on File (Y/N)`
  }

  /**
   * Returns translated string for 'Y'
   * @returns {string} The translated string
   */
  getYText(): string {
    return $localize`:General|Text for shortened Yes (Y)@@general.text.y:Y`
  }

  /**
   * Returns translated string for 'N'
   * @returns {string} The translated string
   */
  getNText(): string {
    return $localize`:General|Text for shortened No (N)@@general.text.n:N`
  }

  /**
   * Returns translated string for 'General Product Overview'
   * @returns {string} The translated string
   */
  getGeneralProductOverviewText(): string {
    return $localize`:General|Text for Product Name@@general.text.generalProductOverview:General Product Overview`
  }

  /**
   * Returns translated string for 'Product Name'
   * @returns {string} The translated string
   */
  getProductNameText(): string {
    return $localize`:General|Text for Product Name@@general.text.productName:Product Name`
  }

  /**
   * Returns translated string for 'Product Identifier'
   * @returns {string} The translated string
   */
  getProductIdentifierText(): string {
    return $localize`:General|Text for Product Identifier@@general.text.productIdentifier:Product Identifier`
  }

  /**
   * Returns translated string for 'Other Product Names'
   * @returns {string} The translated string
   */
  getOtherProductNamesText(): string {
    return $localize`:General|Text for Other Product Names@@general.text.otherProductNames:Other Product Names`
  }

  /**
   * Returns translated string for 'Unwilling to disclose'
   * @returns {string} The translated string
   */
  getUnwillingToDiscloseText(): string {
    return $localize`:General|Text for Unwilling to disclose@@general.text.unwillingToDisclose:Unwilling to disclose`
  }

  /**
   * Returns translated string for 'Picture'
   * @returns {string} The translated string
   */
  getPictureText(): string {
    return $localize`:General|Text for Picture@@general.text.picture:Picture`
  }

  /**
   * Returns translated string for 'Product Type'
   * @returns {string} The translated string
   */
  getProductTypeText(): string {
    return $localize`:General|Text for Product Type@@general.text.productType:Product Type`
  }

  /**
   * Returns translated string for 'EU Product Category'
   * @returns {string} The translated string
   */
  getEUProductCategoryText(): string {
    return $localize`:General|Text for EU Product Category@@general.text.euProductCategory:EU Product Category`
  }

  /**
   * Returns translated string for 'SKU'
   * @returns {string} The translated string
   */
  getSkuText(): string {
    return $localize`:General|Text for SKU@@general.text.sku:SKU`
  }

  /**
   * Returns translated string for 'Measurement Unit'
   * @returns {string} The translated string
   */
  getMeasurementUnitText(): string {
    return $localize`:General|Text for Measurement Unit@@general.text.measurementUnit:Measurement Unit`
  }

  /**
   * Returns translated string for 'Measurement'
   * @returns {string} The translated string
   */
  getMeasurementText(): string {
    return $localize`:General|Text for Measurement@@general.text.measurement:Measurement`
  }

  /**
   * Returns translated string for 'Quantity'
   * @returns {string} The translated string
   */
  getQuantityText(): string {
    return $localize`:General|Text for Quantity@@general.text.quantity:Quantity`
  }

  /**
   * Returns translated string for 'Quantities'
   * @returns {string} The translated string
   */
  getQuantitiesText(): string {
    return $localize`:General|Text for Quantities@@general.text.quantities:Quantities`
  }

  /**
   * Returns translated string for 'Usage Instructions'
   * @returns {string} The translated string
   */
  getUsageInstructionsText(): string {
    return $localize`:General|Text for Usage Instructions@@general.text.usageInstructions:Usage Instructions`
  }

  /**
   * Returns translated string for 'Description'
   * @returns {string} The translated string
   */
  getDescriptionText(): string {
    return $localize`:General|Text for Description@@general.text.description:Description`
  }

  /**
   * Returns translated string for 'Disassembling Instructions'
   * @returns {string} The translated string
   */
  getDisassemblingInstructionsText(): string {
    return $localize`:General|Text for Disassembling Instructions@@general.text.disassemblingInstructions:Disassembling Instructions`
  }

  /**
   * Returns translated string for 'Next'
   * @returns {string} The translated string
   */
  getNextText(): string {
    return $localize`:General|Text for Next@@general.text.next:Next`
  }

  /**
   * Returns translated string for 'Back'
   * @returns {string} The translated string
   */
  getBackText(): string {
    return $localize`:General|Text for Next@@general.text.back:Back`
  }

  /**
   * Returns translated string for 'Product Ingredient Information'
   * @returns {string} The translated string
   */
  getProductIngredientInformationText(): string {
    return $localize`:General|Text for Product Ingredient Information@@general.text.productIngredientInformation:Product Ingredient Information`
  }

  /**
   * Returns translated string for 'Product Component Information'
   * @returns {string} The translated string
   */
  getProductComponentInformationText(): string {
    return $localize`:General|Text for Product Component Information@@general.text.productComponentInformation:Product Component Information`
  }

  /**
   * Returns translated string for 'Are you providing full ingredient disclosure for this product?'
   * @returns {string} The translated string
   */
  getFullIngredientDisclosureQuestionText(): string {
    return $localize`:General|Text for full ingredient disclosure question@@general.text.fullIngredientDisclosureQuestion:Are you providing full ingredient disclosure for this product?`
  }

  /**
   * Returns translated string for 'Range Code'
   * @returns {string} The translated string
   */
  getRangeCodeText(): string {
    return $localize`:General|Text for Range Code@@general.text.rangeCode:Range Code`
  }

  /**
   * Returns translated string for 'Upper Concentration'
   * @returns {string} The translated string
   */
  getUpperConcentrationText(): string {
    return $localize`:General|Text for Upper Concentration@@general.text.upperConcentration:Upper Concentration`
  }

  /**
   * Returns translated string for 'Lower Concentration'
   * @returns {string} The translated string
   */
  getLowerConcentrationText(): string {
    return $localize`:General|Text for Lower Concentration@@general.text.lowerConcentration:Lower Concentration`
  }

  /**
   * Returns translated string for 'Add Ingredient'
   * @returns {string} The translated string
   */
  getAddIngredientText(): string {
    return $localize`:General|Text for Add Ingredient@@general.text.addIngredient:Add Ingredient`
  }

  /**
   * Returns translated string for 'Yes
   * @returns {string} The translated string
   */
  getYesText(): string {
    return $localize`:General|Text for Yes@@general.text.yes:Yes`
  }

  /**
   * Returns translated string for 'No'
   * @returns {string} The translated string
   */
  getNoText(): string {
    return $localize`:General|Text for No@@general.text.no:No`
  }

  /**
   * Returns translated string for 'Overview'
   * @returns {string} The translated string
   */
  getOverviewText(): string {
    return $localize`:General|Text for Overview@@general.text.overview:Overview`
  }

  /**
   * Returns translated string for 'Product created successfully.'
   * @returns {string} The translated string
   */
  getProductCreatedSuccessfullyText(): string {
    return $localize`:General|Text for Product created successfully@@general.text.productCreatedSuccessfully:Product created successfully.`
  }

  getProductUpdatedSuccessfullyText(): string {
    return $localize`:General|Text for Product updated successfully@@general.text.productUpdatedSuccessfully:Product updated successfully.`
  }

  /**
   * Returns translated string for 'Close'
   * @returns {string} The translated string
   */
  getCloseText(): string {
    return $localize`:General|Text for Close@@general.text.close:Close`
  }

  /**
   * Returns translated string for 'Return'
   * @returns {string} The translated string
   */
  getReturnText(): string {
    return $localize`:General|Text for Return@@general.text.return:Return`
  }

  /**
   * Returns translated string for 'Set Location'
   * @returns {string} The translated string
   */
  getSetLocationText(): string {
    return $localize`:General|Text for Set Location@@general.text.setLocation:Set Location`
  }

  /**
   * Returns translated string for 'Regulation'
   * @returns {string} The translated string
   */
  getRegulationText(): string {
    return $localize`:General|Text for Regulation@@general.text.regulation:Regulation`
  }

  /**
   * Returns translated string for 'Kilogram'
   * @returns {string} The translated string
   */
  getKilogramText(): string {
    return $localize`:General|Text for Kilogram@@general.text.kilogram:Kilogram`
  }

  /**
   * Returns translated string for 'Pound'
   * @returns {string} The translated string
   */
  getPoundText(): string {
    return $localize`:General|Text for Pound@@general.text.pound:Pound`
  }

  /**
   * Returns translated string for 'kg'
   * @returns {string} The translated string
   */
  getKgText(): string {
    return $localize`:General|Text for kg@@general.text.kg:kg`
  }

  /**
   * Returns translated string for 'lb'
   * @returns {string} The translated string
   */
  getLbText(): string {
    return $localize`:General|Text for lb@@general.text.lb:lb`
  }

  /**
   * Returns translated string for 'Home'
   * @returns {string} The translated string
   */
  getHomeText(): string {
    return $localize`:General|Text for Home@@general.text.home:Home`
  }

  /**
   * Returns translated string for 'Product'
   * @returns {string} The translated string
   */
  getProductText(): string {
    return $localize`:General|Text for Product@@general.text.product:Product`
  }

  /**
   * Returns translated string for 'Supply Chain'
   * @returns {string} The translated string
   */
  getSupplyChainText(): string {
    return $localize`:General|Text for Supply Chain@@general.text.supplyChain:Supply Chain`
  }

  /**
   * Returns translated string for 'Sustainability'
   * @returns {string} The translated string
   */
  getSustainabilityText(): string {
    return $localize`:General|Text for Sustainability@@general.text.sustainability:Sustainability`
  }

  /**
   * Returns translated string for 'Scores'
   * @returns {string} The translated string
   */
  getScoresText(): string {
    return $localize`:General|Text for Scores@@general.text.scores:Scores`
  }

  /**
   * Returns translated string for 'Regulations'
   * @returns {string} The translated string
   */
  getRegulationsText(): string {
    return $localize`:General|Text for Regulations@@general.text.regulations:Regulations`
  }

  /**
   * Returns translated string for 'Admin'
   * @returns {string} The translated string
   */
  getAdminText(): string {
    return $localize`:General|Text for Admin@@general.text.admin:Admin`
  }

  /**
   * Returns translated string for 'Auth'
   * @returns {string} The translated string
   */
  getAuthText(): string {
    return $localize`:General|Text for Auth@@general.text.auth:Auth`
  }

  /**
   * Returns translated string for 'Product Add'
   * @returns {string} The translated string
   */
  getProductAddText(): string {
    return $localize`:General|Text for Product Add@@general.text.productAdd:Product Add`
  }

  getAddProductText(): string {
    return $localize`:General|Product form heading@@general.text.addProduct:Add Product`
  }

  getEditProductText(): string {
    return $localize`:General|Product form heading@@general.text.editProduct:Edit Product`
  }

  /**
   * Returns translated string for 'Something went wrong processing your request...'
   * @returns {string} The translated string
   */
  getInternalServerErrorText(): string {
    return $localize`:API Error|Internal Server Error@@apiError.internalServerError:Something went wrong processing your request...`
  }

  /**
   * Returns translated string for 'The specified email address is already in use.'
   * @returns {string} The translated string
   */
  getEmailAlreadyExistsText(): string {
    return $localize`:API Error|Email Already Exists@@apiError.emailAlreadyExists:The specified email address is already in use.`
  }

  /**
   * Returns translated string for 'Password token has either expired or does not exist. Please attempt to reset your password again.'
   * @returns {string} The translated string
   */
  getPasswordTokenNotValidText(): string {
    return $localize`:API Error|Password Token Not Valid@@apiError.passwordTokenNotValid:Password token has either expired or does not exist. Please attempt to reset your password again.`
  }

  /**
   * Returns translated string for 'Passwords must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one symbol.'
   * @returns {string} The translated string
   */
  getPasswordDoesNotMatchCriteriaText(): string {
    return $localize`:API Error|Password Does Not Match Criteria@@apiError.passwordDoesNotMatchCriteria:Passwords must be at least 10 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one symbol.`
  }

  /**
   * Returns translated string for 'You have entered an invalid username or password.'
   * @returns {string} The translated string
   */
  getInvalidEmailOrPasswordText(): string {
    return $localize`:API Error|Invalid Email Or Password@@apiError.invalidEmailOrPassword:You have entered an invalid username or password.`
  }

  /**
   * Returns translated string for 'The company associated with this user is inactive.'
   * @returns {string} The translated string
   */
  getCompanyAssociatedWithUserNotActiveText(): string {
    return $localize`:API Error|The company associated with this user is inactive.@@apiError.companyAssociatedWithUserNotActive:The company associated with this user is inactive.`
  }

  /**
   * Returns translated string for 'Invalid access token.'
   * @returns {string} The translated string
   */
  getInvalidAccessTokenText(): string {
    return $localize`:API Error|Invalid Access Token@@apiError.invalidAccessToken:Invalid access token.`
  }

  /**
   * Returns translated string for 'Your session has expired. Please login again to continue.'
   * @returns {string} The translated string
   */
  getSessionExpiredText(): string {
    return $localize`:API Error|Session Expired@@apiError.sessionExpired:Your session has expired. Please login again to continue.`
  }

  /**
   * Returns translated string for 'You do not have the permission to perform the specified action.'
   * @returns {string} The translated string
   */
  getInsufficientPermissionsText(): string {
    return $localize`:API Error|Insufficient Permissions@@apiError.insufficientPermissions:You do not have the permission to perform the specified action.`
  }

  /**
   * Returns translated string for 'This product has not been shared with your company. Contact the product owner to request access.'
   * @returns {string} The translated string
   */
  getProductDetailsRestrictedText(): string {
    return $localize`:API Error|Product Details Restricted@@apiError.productDetailsRestricted:This product has not been shared with your company. Contact the product owner to request access.`
  }

  /**
   * Returns translated string for 'Data could not be retrieved for the given product id.'
   * @returns {string} The translated string
   */
  getProductNotFoundText(): string {
    return $localize`:API Error|Product Not Found@@apiError.productNotFound:Data could not be retrieved for the given product id.`
  }

  /**
   * Returns translated string for 'Company with that identification, phone number or email already exists.'
   * @returns {string} The translated string
   */
  getCompanyWithIdentificationPhoneNumberOrEmailAlreadyExistsText(): string {
    return $localize`:API Error|Company With Identification Phone Number Or Email Already Exists@@apiError.companyWithIdentificationPhoneNumberOrEmailAlreadyExists:Company with that identification, phone number or email already exists.`
  }

  /**
   * Returns translated string for 'The specified company does not exist.'
   * @returns {string} The translated string
   */
  getCompanyDoesNotExistText(): string {
    return $localize`:API Error|Company Does Not Exist@@apiError.companyDoesNotExist:The specified company does not exist.`
  }

  /**
   * Returns translated string for 'An ingredient already exists with the given name.'
   * @returns {string} The translated string
   */
  getIngredientAlreadyExistsWithNameText(): string {
    return $localize`:API Error|Ingredient Already Exists With Name@@apiError.ingredientAlreadyExistsWithName:An ingredient already exists with the given name.`
  }

  /**
   * Returns translated string for 'Language'
   * @returns {string} The translated string
   */
  getLanguageText(): string {
    return $localize`:General|Text for input/information@@general.text.language:Language`
  }

  /**
   * Returns translated string for 'Settings'
   * @returns {string} The translated string
   */
  getSettingsText(): string {
    return $localize`:General|Text for input/information@@general.text.settings:Settings`
  }

  /**
   * Returns translated string for 'English'
   * @returns {string} The translated string
   */
  getEnglishText(): string {
    return $localize`:General|Language name@@general.language.english:English`
  }

  /**
   * Returns translated string for 'French'
   * @returns {string} The translated string
   */
  getFrenchText(): string {
    return $localize`:General|Language name@@general.language.french:French`
  }

  /**
   * Returns the translated string for 'An article picture is required, please upload a picture of the product to continue.'
   * @returns {string} The translated string
   */
  getArticlePictureRequiredText(): string {
    return $localize`:General|Text for input/information@@general.text.articlePictureRequired:An article picture is required, please upload a picture of the product to continue.`
  }

  /**
   * Returns the translated string for 'A product category is required, please select the product category.'
   * @returns {string} The translated string
   */
  getProductCategoryRequiredText(): string {
    return $localize`:General|Text for input/information@@general.text.productCategoryRequired:A product category is required, please select the product category.`
  }

  /**
   * Returns the translated string for 'A safety data sheet is required, please upload a safety data sheet to continue.'
   * @returns {string} The translated string
   */
  getSafetyDataSheetRequiredText(): string {
    return $localize`:General|Text for input/information@@general.text.safetyDataSheetRequired:A safety data sheet is required, please upload a safety data sheet to continue.`
  }

  /**
   * Returns the translated string for 'A recycling instructions document is required, please upload a document with recycling instructions to continue.'
   * @returns {string} The translated string
   */
  getRecyclingInstructionsDocumentRequiredText(): string {
    return $localize`:General|Text for input/information@@general.text.recyclingInstructionsDocumentRequired:A recycling instructions document is required, please upload a document with recycling instructions to continue.`
  }

  /**
   * Returns the translated string for 'Product Substance Information'
   * @returns {string} The translated string
   */
  getProductSubstanceInformationText(): string {
    return $localize`:General|Text for input/information@@general.text.productSubstanceInformation:Product Substance Information`
  }

  /**
   * Returns the translated string for 'Search for a substance'
   * @returns {string} The translated string
   */
  getSearchForSubstanceText(): string {
    return $localize`:General|Text for input/information@@general.text.searchForSubstance:Search for a substance`
  }

  /**
   * Returns the translated string for 'ID'
   * @returns {string} The translated string
   */
  getIdText(): string {
    return $localize`:General|Text for input/information@@general.text.id:ID`
  }

  /**
   * Returns the translated string for 'Remove Substance'
   * @returns {string} The translated string
   */
  getRemoveSubstanceText(): string {
    return $localize`:General|Text for input/information@@general.text.removeSubstance:Remove Substance`
  }

  /**
   * Returns the translated string for 'Add Substance'
   * @returns {string} The translated string
   */
  getAddSubstanceText(): string {
    return $localize`:General|Text for input/information@@general.text.addSubstance:Add Substance`
  }

  /**
   * Returns the translated string for 'Remove Component'
   * @returns {string} The translated string
   */
  getRemoveComponentText(): string {
    return $localize`:General|Text for input/information@@general.text.removeComponent:Remove Component`
  }

  /**
   * Returns the translated string for 'Add Component'
   * @returns {string} The translated string
   */
  getAddComponentText(): string {
    return $localize`:General|Text for input/information@@general.text.addComponent:Add Component`
  }

  /**
   * Returns the translated string for 'No Substances were found.'
   * @returns {string} The translated string
   */
  getNoSubstancesFoundText(): string {
    return $localize`:General|Text for input/information@@general.text.noSubstancesFound:No Substances were found.`
  }

  /**
   * Returns the translated string for 'No Components were found.'
   * @returns {string} The translated string
   */
  getNoComponentsFoundText(): string {
    return $localize`:General|Text for input/information@@general.text.noComponentsFound:No Components were found.`
  }

  /**
   * Returns the translated string for 'No Ingredients were found.'
   * @returns {string} The translated string
   */
  getNoIngredientsFoundText(): string {
    return $localize`:General|Text for input/information@@general.text.noIngredientsFound:No Ingredients were found.`
  }

  /**
   * Returns translated string for 'An error occurred retrieving product information.'
   * @returns {string} The translated string
   */
  getFailedToGetProductDetailsError(): string {
    return $localize`:API Error|Internal Server Error@@apiError.errorRetrievingProductInfo:An error occurred retrieving product information.`
  }

  /**
   * Returns the translated string for 'Download'
   * @returns {string} The translated string
   */
  getDownloadText(): string {
    return $localize`:General|Text for input/information@@general.text.download:Download`
  }

  /**
   * Returns the translated string for 'Please select the user group(s) this ingredient should be disclosed to below. All groups are selected by default.'
   * @returns {string} The translated string
   */
  getSelectiveDisclosureMessage(): string {
    return $localize`:General|Text for input/information@@general.text.selectiveDisclosureMessage:Please select the user group(s) this ingredient should be
                disclosed to below. All groups are selected by default.`
  }

  /**
   * Returns the translated string for 'No ingredients disclosed.'
   * @returns {string} The translated string
   */
  getNoIngredientsDisclosedText(): string {
    return $localize`:General|Text for input/information@@general.text.noIngredientsDisclosed:No ingredients disclosed.`
  }

  /**
   * Returns the translated string for 'Does Not Contain'
   * @returns {string}
   */
  getDoesNotContainText(): string {
    return $localize`:General|Text for does not contain@@general.text.doesNotContain:Does Not Contain`
  }

  /**
   * Returns translated string for 'Please specify the ingredients associated with the product. If there are no ingredients, please select 'No' for Full Ingredient Disclosure.'
   * @returns {string} The translated string
   */
  getIngredientsRequiredForFullIngredientDisclosureMsg(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientsRequiredForFullIngredientDisclosure:
            Please specify the ingredients associated with the product. If there are no ingredients, please select 'No' for Full Ingredient Disclosure.`
  }

  /**
   * Returns the translated string for 'Optionally Disclosed'
   * @returns {string}
   */
  getOptionallyDisclosedText(): string {
    return $localize`:General|Text for optionally disclosed@@general.text.optionallyDisclosed:Optionally Disclosed`
  }

  /**
   * Returns translated string for 'An ingredient already exists with one of the specified aliases.'
   * @returns {string} The translated string
   */
  getIngredientAlreadyExistsWithAliasText(): string {
    return $localize`:API Error|Text for api error@@apiError.ingredientAlreadyExistsWithAlias:An ingredient already exists with one of the specified aliases.`
  }

  /**
   * Returns translated string for 'An ingredient already exists with one of the specified cas numbers.'
   * @returns {string} The translated string
   */
  getIngredientAlreadyExistsWithCasNumberText(): string {
    return $localize`:API Error|Text for api error@@apiError.ingredientAlreadyExistsWithCasNumber:An ingredient already exists with one of the specified CAS Numbers.`
  }

  /**
   * Returns translated string for 'Recyclability'
   * @returns {string} The translated string
   */
  getRecyclabilityText(): string {
    return $localize`:General|Text for input/information@@general.text.recyclability:Recyclability`
  }

  /**
   * Returns translated string for 'Product Recyclability Information'
   * @returns {string} The translated string
   */
  getProductRecyclabilityInformation(): string {
    return $localize`:General|Text for input/information@@general.text.productRecyclabilityInformation:Product Recyclability Information`
  }

  /**
   * Returns translated string for 'Product GHS Classification Information'
   * @returns {string} The translated string
   */
  getProductGhsClassificationInformation(): string {
    return $localize`:General|Text for input/information@@general.text.productGhsClassificationInformation:Product GHS Classification Information`
  }

  /**
   * Returns translated string for 'Is this a recyclable product?'
   * @returns {string} The translated string
   */
  getIsThisARecyclableProductText(): string {
    return $localize`:General|Text for input/information@@general.text.isThisAPlasticProduct:Is this a recyclable product?`
  }

  /**
   * Returns translated string for 'Please select product GHS Classification symbol(s)'
   * @returns {string} The translated string
   */
  getPleaseSelectProjectGhsSymbols(): string {
    return $localize`:General|Text for input/information@@general.text.pleaseSelectProjectGhsSymbols:Please select product GHS Classification symbol(s)`
  }

  /**
   * Returns translated string for 'Is this product a type of packaging?'
   * @returns {string} The translated string
   */
  getIsThisProductAPackageText(): string {
    return $localize`:General|Text for input/information@@general.text.isThisProductAPackage:Is this product a type of packaging?`
  }

  /**
   * Returns translated string for 'Recycled Content Percentage'
   * @returns {string} The translated string
   */
  getRecycledContentPercentage(): string {
    return $localize`:General|Text for input/information@@general.text.recycledContentPercentage:Recycled Content Percentage`
  }

  /**
   * Returns translated string for 'Packaging Type'
   * @returns {string} The translated string
   */
  getPackagingTypeText(): string {
    return $localize`:General|Text for input/information@@general.text.packagingType:Packaging Type`
  }

  /**
   * Returns translated string for 'Primary'
   * @returns {string} The translated string
   */
  getPrimaryText(): string {
    return $localize`:General|Text for input/information@@general.text.primary:Primary`
  }

  /**
   * Returns translated string for 'Secondary'
   * @returns {string} The translated string
   */
  getSecondaryText(): string {
    return $localize`:General|Text for input/information@@general.text.secondary:Secondary`
  }

  /**
   * Returns translated string for 'Tertiary'
   * @returns {string} The translated string
   */
  getTertiaryText(): string {
    return $localize`:General|Text for input/information@@general.text.tertiary:Tertiary`
  }

  /**
   * Returns translated string for recycled content percentage error.
   * @returns {string} The translated string
   */
  getRecycledContentPercentageError(): string {
    return $localize`:API Error|Recycled Content Percentage Error@@apiError.recycledContentPercentageError:Recycled content percentage must be provided and no less than 0 when the product is plastic.`
  }

  /**
   * Returns translated string for package type error.
   * @returns {string} The translated string
   */
  getPackageTypeError(): string {
    return $localize`:API Error|Package Type Error@@apiError.packageTypeError:Package type is required when the product is a package.`
  }

  /**
   * Returns translated string for package not allowed error.
   * @returns {string} The translated string
   */
  getPackageNotAllowedError(): string {
    return $localize`:API Error|Package Not Allowed Error@@apiError.packageNotAllowedError:A product must be plastic in order to be set as a package.`
  }

  /**
   * Returns translated string for invalid package type error.
   * @returns {string} The translated string
   */
  getInvalidPackageTypeError(): string {
    return $localize`:API Error|Invalid Package Type Error@@apiError.invalidPackageTypeError:Invalid package type. Must be one of: Primary, Secondary, Tertiary.`
  }

  /**
   * Returns translated string for recycled content percentage.
   * @param recycledPercentage - The recycled content percentage
   * @returns {string} The translated string
   */
  getRecycledContentPercentageWithValueText(
    recycledPercentage: number,
  ): string {
    return $localize`:General|Text for input/information@@general.text.recycledContent:This product has ${recycledPercentage}% recycled content.`
  }

  /**
   * Returns translated string for 'Show Recycled Content'.
   * @returns {string} The translated string
   */
  getShowRecycledContentText(): string {
    return $localize`:General|Text for input/information@@general.text.showRecycledContent:Show Recycled Content`
  }

  /**
   * Returns translated string for 'No recycled content available to show.'
   * @returns {string} The translated string
   */
  getNoRecycledContentAvailableText(): string {
    return $localize`:General|Text for indicating no recycled content available@@general.text.noRecycledContentAvailable:No recycled content available to show.`
  }

  /**
   * Returns translated string for 'Recycled Content'
   * @returns {string} The translated string
   */
  getRecycledContentText(): string {
    return $localize`:General|Label for recycled content@@general.label.recycledContent:Recycled Content`
  }

  /**
   * Returns translated string for 'Recycled content percentage not available.'
   * @returns {string} The translated string
   */
  getRecycledContentPercentageNaText(): string {
    return $localize`:General|Label for recycled content not available@@general.label.recycledContentPercentageNa:Recycled content percentage not available.`
  }

  /**
   * Returns translated string for 'Packaging box'
   * @returns {string} The translated string
   */
  getPackagingBoxText(): string {
    return $localize`:General|Label for packaging box@@general.label.packagingBox:Packaging box`
  }

  /**
   * Returns translated string for 'Packaging'
   * @returns {string} The translated string
   */
  getPackagingText(): string {
    return $localize`:General|Label for packaging@@general.label.packaging:Packaging`
  }

  /**
   * Returns translated string for 'This product is a'.
   * @returns {string} The translated string
   */
  getProductPackageIntroText(): string {
    return $localize`:Product Information|Introductory text for package@@product.package.intro:This product is`
  }

  /**
   * Returns translated string for 'package'.
   * @returns {string} The translated string
   */
  getProductPackagingSuffixText(): string {
    return $localize`:Product Information|Suffix text for package@@product.package.suffix:packaging`
  }

  /**
   * Returns translated string for 'English Short Name'
   * @returns {string} The translated string
   */
  getEnglishShortNameText(): string {
    return $localize`:General|Text for English Short Name@@general.label.english:English Short Name`
  }

  /**
   * Returns translated string for 'French Short Name'
   * @returns {string} The translated string
   */
  getFrenchShortNameText(): string {
    return $localize`:General|Text for French Short Name@@general.label.french:French Short Name`
  }

  /**
   * Returns translated string for invalid province error.
   * @returns {string} The translated string
   */
  getInvalidProvinceError(): string {
    return $localize`:API Error|Invalid Province Error@@apiError.invalidProvinceError:Invalid province provided.`
  }

  /**
   * Returns translated string for invalid recyclability status error.
   * @returns {string} The translated string
   */
  getInvalidRecyclabilityStatusError(): string {
    return $localize`:API Error|Invalid Recyclability Status Error@@apiError.invalidRecyclabilityStatusError:Invalid recyclability status provided.`
  }

  /**
   * Returns translated string for missing recyclability status error.
   * @returns {string} The translated string
   */
  getRecyclabilityStatusMissingError(): string {
    return $localize`:API Error|Recyclability Status Missing Error@@apiError.recyclabilityStatusMissingError:Recyclability status is required for each specified province.`
  }

  /**
   * Returns translated string for provincial recyclability required for plastic products error.
   * @returns {string} The translated string
   */
  getProvincialOrCountryRecyclabilityRequiredForPlasticProductsError(): string {
    return $localize`:API Error|Provincial Recyclability Required Error@@apiError.ProvincialOrCountryRecyclabilityRequiredForPlasticProductsError:Provincial or Country Recyclability information is required for products that are plastic.`
  }

  /**
   * Returns translated string for selecting product recyclability status.
   * @returns {string} The translated string
   */
  getSelectProductRecyclabilityStatusText(): string {
    return $localize`:General|Text for input/information@@general.text.selectProductRecyclabilityStatus:Please select the product recyclability status for`
  }

  /**
   * Returns translated string for selecting a recyclability option.
   * @returns {string} The translated string
   */
  getSelectRecyclabilityOptionText(): string {
    return $localize`:General|Text for input/information@@general.text.selectRecyclabilityOption:Please select a recyclability option.`
  }

  /**
   * Returns translated string for 'Recyclable'.
   * @returns {string} The translated string
   */
  getRecyclableText(): string {
    return $localize`:General|Text for input/information@@general.text.recyclable:Recyclable`
  }

  /**
   * Returns translated string for 'Non-Recyclable'.
   * @returns {string} The translated string
   */
  getNonRecyclableText(): string {
    return $localize`:General|Text for input/information@@general.text.nonRecyclable:Non-Recyclable`
  }

  /**
   * Returns translated string for 'Collected'.
   * @returns {string} The translated string
   */
  getCollectedText(): string {
    return $localize`:General|Text for input/information@@general.text.collected:Collected`
  }

  /**
   * Returns translated string for entering provincial recyclability information.
   * @returns {string} The translated string
   */
  getEnterProvincialRecyclabilityInfoText(): string {
    return $localize`:General|Text for input/information@@general.text.enterProvincialRecyclabilityInfo:Please enter the provincial and territorial recyclability information for the product. Select the province(s) and any territory the product is being sold in and the recyclability status for each. See below for the recyclability statuses and their corresponding icons.`
  }

  /**
   * Returns translated string for 'Select Provinces and Territories'.
   * @returns {string} The translated string
   */
  getSelectProvincesAndTerritoriesText(): string {
    return $localize`:General|Text for input/information@@general.text.selectProvinces:Select Provinces and Territories`
  }

  /**
   * Returns translated string for 'At least one province or territory is required'.
   * @returns {string} The translated string
   */
  getAtLeastOneProvinceRequiredText(): string {
    return $localize`:General|Text for input/information@@general.text.atLeastOneProvinceRequired:At least one province or territory is required.`
  }

  /**
   * Returns translated string for 'Canadian Provincial & Territoritial Recyclability'.
   * @returns {string} The translated string
   */
  getProvincialRecyclabilityText(): string {
    return $localize`:General|Text for input/information@@general.text.provincialRecyclability:Canadian Provincial & Territorial Recyclability`
  }

  /**
   * Returns translated string for 'Recyclable Icon'.
   * @returns {string} The translated string
   */
  getRecyclableIconText(): string {
    return $localize`:General|Text for input/information@@general.text.recyclableIcon:Recyclable Icon`
  }

  /**
   * Returns translated string for 'Non Recyclable Icon'.
   * @returns {string} The translated string
   */
  getNonRecyclableIconText(): string {
    return $localize`:General|Text for input/information@@general.text.nonRecyclableIcon:Non Recyclable Icon`
  }

  /**
   * Returns translated string for 'Collected Icon'.
   * @returns {string} The translated string
   */
  getCollectedIconText(): string {
    return $localize`:General|Text for input/information@@general.text.collectedIcon:Collected Icon`
  }

  /**
   * Returns translated string for 'Change Location'
   * @returns {string} The translated string
   */
  getChangeLocationText(): string {
    return $localize`:General|Text for input/information@@general.text.changeLocation:Change Location`
  }

  /**
   * Returns translated string for 'You are viewing from ${locationName}'
   * @param locationName - The name of the location
   * @returns {string} The translated string
   */
  getYouAreViewingFromLocationText(locationName: string): string {
    return $localize`:General|Text for input/information@@general.text.youAreViewingFromLocation:You are viewing from: ${locationName}`
  }

  /**
   * Returns translated string for 'Substances of Concern'
   * @returns {string} The translated string
   */
  getSubstancesOfConcernText(): string {
    return $localize`:General|Text for input/information@@general.text.substancesOfConcern:Substances of Concern`
  }

  /**
   * Returns translated string for 'No substances of concern reported for this product.'
   * @returns {string} The translated string
   */
  getNoSubstancesOfConcernReportedText(): string {
    return $localize`:General|Text for indicating no substances of concern reported for a product@@general.text.noSubstancesOfConcernReported:No substances of concern reported for this product.`
  }

  /**
   * Returns translated string for 'This ingredient is a substance of concern.'
   * @returns {string} The translated string
   */
  getIngredientIsSubstanceOfConcernText(): string {
    return $localize`:General|Text for indicating an ingredient is a substance of concern@@general.text.ingredientIsSubstanceOfConcern:This ingredient is a substance of concern.`
  }

  /**
   * Returns translated string for 'Gram'
   * @returns {string} The translated string
   */
  getGramText(): string {
    return $localize`:General|Text for Gram@@general.text.gram:Gram`
  }

  /**
   * Returns translated string for 'g'
   * @returns {string} The translated string
   */
  getGText(): string {
    return $localize`:General|Text for g@@general.text.g:g`
  }

  /**
   * Returns translated string for 'Tons'
   * @returns {string} The translated string
   */
  getTonsText(): string {
    return $localize`:General|Text for Tons@@general.text.tons:Tons`
  }

  /**
   * Returns translated string for 'tn'
   * @returns {string} The translated string
   */
  getTnText(): string {
    return $localize`:General|Text for tn@@general.text.tn:tn`
  }

  /**
   * Returns translated string for 'Metric Ton'
   * @returns {string} The translated string
   */
  getMetricTonText(): string {
    return $localize`:General|Text for Metric Ton@@general.text.metricTon:Metric Ton`
  }

  /**
   * Returns translated string for 'mt'
   * @returns {string} The translated string
   */
  getMpText(): string {
    return $localize`:General|Text for mt@@general.text.mt:mt`
  }

  /**
   * Returns translated string for 'Liter'
   * @returns {string} The translated string
   */
  getLiterText(): string {
    return $localize`:General|Text for Liter@@general.text.liter:Liter`
  }

  /**
   * Returns translated string for 'L'
   * @returns {string} The translated string
   */
  getLtText(): string {
    return $localize`:General|Text for L@@general.text.lt:L`
  }

  /**
   * Returns translated string for 'Gallon'
   * @returns {string} The translated string
   */
  getGallonText(): string {
    return $localize`:General|Text for Gallon@@general.text.gallon:Gallon`
  }

  /**
   * Returns translated string for 'gal'
   * @returns {string} The translated string
   */
  getGaText(): string {
    return $localize`:General|Text for gal@@general.text.gal:gal`
  }

  /**
   * Returns translated string for 'Ounce'
   * @returns {string} The translated string
   */
  getOunceText(): string {
    return $localize`:General|Text for Ounce@@general.text.ounce:Ounce`
  }

  /**
   * Returns translated string for 'oz'
   * @returns {string} The translated string
   */
  getOzText(): string {
    return $localize`:General|Text for oz@@general.text.oz:oz`
  }

  /**
   * Returns translated string for 'Troy Ounce'
   * @returns {string} The translated string
   */
  getTroyOunceText(): string {
    return $localize`:General|Text for Troy Ounce@@general.text.troyOunce:Troy Ounce`
  }

  /**
   * Returns translated string for 'toz'
   * @returns {string} The translated string
   */
  getTozText(): string {
    return $localize`:General|Text for toz@@general.text.toz:toz`
  }

  /**
   * Returns translated string for 'Pint'
   * @returns {string} The translated string
   */
  getPintText(): string {
    return $localize`:General|Text for Pint@@general.text.pint:Pint`
  }

  /**
   * Returns translated string for 'pt'
   * @returns {string} The translated string
   */
  getPtText(): string {
    return $localize`:General|Text for pt@@general.text.pt:pt`
  }

  /**
   * Returns translated string for 'Quart'
   * @returns {string} The translated string
   */
  getQuartText(): string {
    return $localize`:General|Text for Quart@@general.text.quart:Quart`
  }

  /**
   * Returns translated string for 'qt'
   * @returns {string} The translated string
   */
  getQtText(): string {
    return $localize`:General|Text for qt@@general.text.qt:qt`
  }

  /**
   * Returns translated string for 'Centimeter'
   * @returns {string} The translated string
   */
  getCentimeterText(): string {
    return $localize`:General|Text for Centimeter@@general.text.centimeter:Centimeter`
  }

  /**
   * Returns translated string for 'cm'
   * @returns {string} The translated string
   */
  getCmText(): string {
    return $localize`:General|Text for cm@@general.text.cm:cm`
  }

  /**
   * Returns translated string for 'Kilometer'
   * @returns {string} The translated string
   */
  getKilometersText(): string {
    return $localize`:General|Text for Kilometer@@general.text.kilometer:Kilometer`
  }

  /**
   * Returns translated string for 'km'
   * @returns {string} The translated string
   */
  getDkText(): string {
    return $localize`:General|Text for km@@general.text.km:km`
  }

  /**
   * Returns translated string for 'Milligram'
   * @returns {string} The translated string
   */
  getMilligramText(): string {
    return $localize`:General|Text for Milligram@@general.text.milligram:Milligram`
  }

  /**
   * Returns translated string for 'mg'
   * @returns {string} The translated string
   */
  getMgText(): string {
    return $localize`:General|Text for mg@@general.text.mg:mg`
  }

  /**
   * Returns translated string for 'Milliliter'
   * @returns {string} The translated string
   */
  getMilliliterText(): string {
    return $localize`:General|Text for Milliliter@@general.text.milliliter:Milliliter`
  }

  /**
   * Returns translated string for 'ml'
   * @returns {string} The translated string
   */
  getMlText(): string {
    return $localize`:General|Text for ml@@general.text.ml:ml`
  }

  /**
   * Returns translated string for 'Foot'
   * @returns {string} The translated string
   */
  getFootText(): string {
    return $localize`:General|Text for Foot@@general.text.foot:Foot`
  }

  /**
   * Returns translated string for 'ft'
   * @returns {string} The translated string
   */
  getFtText(): string {
    return $localize`:General|Text for ft@@general.text.ft:ft`
  }

  /**
   * Returns translated string for 'Inch'
   * @returns {string} The translated string
   */
  getInchText(): string {
    return $localize`:General|Text for Inch@@general.text.inch:Inch`
  }

  /**
   * Returns translated string for 'in'
   * @returns {string} The translated string
   */
  getInText(): string {
    return $localize`:General|Text for in@@general.text.in:in`
  }

  /**
   * Returns translated string for 'Yard'
   * @returns {string} The translated string
   */
  getYardText(): string {
    return $localize`:General|Text for Yard@@general.text.yard:Yard`
  }

  /**
   * Returns translated string for 'yd'
   * @returns {string} The translated string
   */
  getYdText(): string {
    return $localize`:General|Text for yd@@general.text.yd:yd`
  }

  /**
   * Returns translated string for 'Mile'
   * @returns {string} The translated string
   */
  getMileText(): string {
    return $localize`:General|Text for Mile@@general.text.mile:Mile`
  }

  /**
   * Returns translated string for 'mi'
   * @returns {string} The translated string
   */
  getMiText(): string {
    return $localize`:General|Text for mi@@general.text.mi:mi`
  }

  /**
   * Returns translated string for 'Meter'
   * @returns {string} The translated string
   */
  getMeterText(): string {
    return $localize`:General|Text for Meter@@general.text.meter:Meter`
  }

  /**
   * Returns translated string for 'm'
   * @returns {string} The translated string
   */
  getMText(): string {
    return $localize`:General|Text for m@@general.text.m:m`
  }

  /**
   * Returns translated string for 'Square Meter'
   * @returns {string} The translated string
   */
  getSquareMeterText(): string {
    return $localize`:General|Text for Square Meter@@general.text.squareMeter:Square Meter`
  }

  /**
   * Returns translated string for 'sq m'
   * @returns {string} The translated string
   */
  getSqMText(): string {
    return $localize`:General|Text for sq m@@general.text.sqM:sq m`
  }

  /**
   * Returns translated string for 'Cubic Meter'
   * @returns {string} The translated string
   */
  getCubicMeterText(): string {
    return $localize`:General|Text for Cubic Meter@@general.text.cubicMeter:Cubic Meter`
  }

  /**
   * Returns translated string for 'cu m'
   * @returns {string} The translated string
   */
  getCuMText(): string {
    return $localize`:General|Text for cu m@@general.text.cuM:cu m`
  }

  /**
   * Returns translated string for 'Hectare'
   * @returns {string} The translated string
   */
  getHectareText(): string {
    return $localize`:General|Text for Hectare@@general.text.hectare:Hectare`
  }

  /**
   * Returns translated string for 'ha'
   * @returns {string} The translated string
   */
  getHaText(): string {
    return $localize`:General|Text for ha@@general.text.ha:ha`
  }

  /**
   * Returns translated string for 'Acre'
   * @returns {string} The translated string
   */
  getAcreText(): string {
    return $localize`:General|Text for Acre@@general.text.acre:Acre`
  }

  /**
   * Returns translated string for 'ac'
   * @returns {string} The translated string
   */
  getAcText(): string {
    return $localize`:General|Text for ac@@general.text.ac:ac`
  }

  /**
   * Returns translated string for 'l'
   * @returns {string} The translated string
   */
  getLText(): string {
    return $localize`:General|Text for l@@general.text.l:l`
  }

  /**
   * Returns translated string for 'gal'
   * @returns {string} The translated string
   */
  getGalText(): string {
    return $localize`:General|Text for gal@@general.text.gal:gal`
  }

  /**
   * Returns translated string for 'Cup'
   * @returns {string} The translated string
   */
  getCupText(): string {
    return $localize`:General|Text for Cup@@general.text.cup:Cup`
  }

  /**
   * Returns translated string for 'Fluid Ounce'
   * @returns {string} The translated string
   */
  getFluidOunceText(): string {
    return $localize`:General|Text for Fluid Ounce@@general.text.fluidOunce:Fluid Ounce`
  }

  /**
   * Returns translated string for 'fl oz'
   * @returns {string} The translated string
   */
  getFlOzText(): string {
    return $localize`:General|Text for fl oz@@general.text.flOz:fl oz`
  }

  /**
   * Returns translated string for 'Teaspoon'
   * @returns {string} The translated string
   */
  getTeaspoonText(): string {
    return $localize`:General|Text for Teaspoon@@general.text.teaspoon:Teaspoon`
  }

  /**
   * Returns translated string for 'tsp'
   * @returns {string} The translated string
   */
  getTspText(): string {
    return $localize`:General|Text for tsp@@general.text.tsp:tsp`
  }

  /**
   * Returns translated string for 'Tablespoon'
   * @returns {string} The translated string
   */
  getTablespoonText(): string {
    return $localize`:General|Text for Tablespoon@@general.text.tablespoon:Tablespoon`
  }

  /**
   * Returns translated string for 'tbsp'
   * @returns {string} The translated string
   */
  getTbspText(): string {
    return $localize`:General|Text for tbsp@@general.text.tbsp:tbsp`
  }

  /**
   * Returns translated string for 'Second'
   * @returns {string} The translated string
   */
  getSecondText(): string {
    return $localize`:General|Text for Second@@general.text.second:Second`
  }

  /**
   * Returns translated string for 'sec'
   * @returns {string} The translated string
   */
  getSecText(): string {
    return $localize`:General|Text for sec@@general.text.sec:sec`
  }

  /**
   * Returns translated string for 'Minute'
   * @returns {string} The translated string
   */
  getMinuteText(): string {
    return $localize`:General|Text for Minute@@general.text.minute:Minute`
  }

  /**
   * Returns translated string for 'min'
   * @returns {string} The translated string
   */
  getMinText(): string {
    return $localize`:General|Text for min@@general.text.min:min`
  }

  /**
   * Returns translated string for 'Hour'
   * @returns {string} The translated string
   */
  getHourText(): string {
    return $localize`:General|Text for Hour@@general.text.hour:Hour`
  }

  /**
   * Returns translated string for 'hr'
   * @returns {string} The translated string
   */
  getHrText(): string {
    return $localize`:General|Text for hr@@general.text.hr:hr`
  }

  /**
   * Returns translated string for 'Thousand'
   * @returns {string} The translated string
   */
  getThousandText(): string {
    return $localize`:General|Text for Thousand@@general.text.thousand:Thousand`
  }

  /**
   * Returns translated string for 'Th'
   * @returns {string} The translated string
   */
  getThText(): string {
    return $localize`:General|Text for Th@@general.text.th:Th`
  }

  /**
   * Returns translated string for 'Cubic Centimeter'
   * @returns {string} The translated string
   */
  getCubicCentimeterText(): string {
    return $localize`:General|Text for Cubic Centimeter@@general.text.cubicCentimeter:Cubic Centimeter`
  }

  /**
   * Returns translated string for 'cc'
   * @returns {string} The translated string
   */
  getCcText(): string {
    return $localize`:General|Text for cc@@general.text.cc:cc`
  }

  /**
   * Returns translated string for 'Cubic Meters'
   * @returns {string} The translated string
   */
  getCubicMetersText(): string {
    return $localize`:General|Text for Cubic Meters@@general.text.cubicMeters:Cubic Meters`
  }

  /**
   * Returns translated string for 'Cr'
   * @returns {string} The translated string
   */
  getCrText(): string {
    return $localize`:General|Text for Cr@@general.text.cr:Cr`
  }

  /**
   * Returns translated string for 'Cubic Foot'
   * @returns {string} The translated string
   */
  getCubicFootText(): string {
    return $localize`:General|Text for Cubic Foot@@general.text.cubicFoot:Cubic Foot`
  }

  /**
   * Returns translated string for 'cf'
   * @returns {string} The translated string
   */
  getCfText(): string {
    return $localize`:General|Text for cf@@general.text.cf:cf`
  }

  /**
   * Returns translated string for 'Square Foot'
   * @returns {string} The translated string
   */
  getSquareFootText(): string {
    return $localize`:General|Text for Square Foot@@general.text.squareFoot:Square Foot`
  }

  /**
   * Returns translated string for 'sf'
   * @returns {string} The translated string
   */
  getSfText(): string {
    return $localize`:General|Text for sf@@general.text.sf:sf`
  }

  /**
   * Returns translated string for 'Barrel'
   * @returns {string} The translated string
   */
  getBarrelText(): string {
    return $localize`:General|Text for Barrel@@general.text.barrel:Barrel`
  }

  /**
   * Returns translated string for 'Br'
   * @returns {string} The translated string
   */
  getBrText(): string {
    return $localize`:General|Text for Br@@general.text.br:Br`
  }

  /**
   * Returns translated string for 'Centiliter'
   * @returns {string} The translated string
   */
  getCentiliterText(): string {
    return $localize`:General|Text for Centiliter@@general.text.centiliter:Centiliter`
  }

  /**
   * Returns translated string for 'C3'
   * @returns {string} The translated string
   */
  getC3Text(): string {
    return $localize`:General|Text for C3@@general.text.c3:C3`
  }

  /**
   * Returns translated string for 'Millimeter'
   * @returns {string} The translated string
   */
  getMillimeterText(): string {
    return $localize`:General|Text for Millimeter@@general.text.millimeter:Millimeter`
  }

  /**
   * Returns translated string for 'Mm'
   * @returns {string} The translated string
   */
  getMmText(): string {
    return $localize`:General|Text for Mm@@general.text.mm:Mm`
  }

  /**
   * Returns translated string for 'Meters'
   * @returns {string} The translated string
   */
  getMetersText(): string {
    return $localize`:General|Text for Meters@@general.text.meters:Meters`
  }

  /**
   * Returns translated string for 'Mr'
   * @returns {string} The translated string
   */
  getMrText(): string {
    return $localize`:General|Text for Mr@@general.text.mr:Mr`
  }

  /**
   * Returns translated string for '10 Pack'
   * @returns {string} The translated string
   */
  get10PackText(): string {
    return $localize`:General|Text for 10 Pack@@general.text.tenPack:10 Pack`
  }

  /**
   * Returns translated string for 'Tp'
   * @returns {string} The translated string
   */
  getTpText(): string {
    return $localize`:General|Text for Tp@@general.text.tp:Tp`
  }

  /**
   * Returns translated string for '100 Pack'
   * @returns {string} The translated string
   */
  get100PackText(): string {
    return $localize`:General|Text for 100 Pack@@general.text.hundredPack:100 Pack`
  }

  /**
   * Returns translated string for '4f'
   * @returns {string} The translated string
   */
  get4fText(): string {
    return $localize`:General|Text for 4f@@general.text.fourF:4f`
  }

  /**
   * Returns translated string for '1000 Pack'
   * @returns {string} The translated string
   */
  get1000PackText(): string {
    return $localize`:General|Text for 1000 Pack@@general.text.thousandPack:1000 Pack`
  }

  /**
   * Returns translated string for 'Zr'
   * @returns {string} The translated string
   */
  getZrText(): string {
    return $localize`:General|Text for Zr@@general.text.zr:Zr`
  }

  /**
   * Returns translated string for '120 Pack'
   * @returns {string} The translated string
   */
  get120PackText(): string {
    return $localize`:General|Text for 120 Pack@@general.text.oneHundredTwentyPack:120 Pack`
  }

  /**
   * Returns translated string for 'Zn'
   * @returns {string} The translated string
   */
  getZnText(): string {
    return $localize`:General|Text for Zn@@general.text.zn:Zn`
  }

  /**
   * Returns translated string for '13 Pack'
   * @returns {string} The translated string
   */
  get13PackText(): string {
    return $localize`:General|Text for 13 Pack@@general.text.thirteenPack:13 Pack`
  }

  /**
   * Returns translated string for 'Z7'
   * @returns {string} The translated string
   */
  getZ7Text(): string {
    return $localize`:General|Text for Z7@@general.text.z7:Z7`
  }

  /**
   * Returns translated string for '14 Pack'
   * @returns {string} The translated string
   */
  get14PackText(): string {
    return $localize`:General|Text for 14 Pack@@general.text.fourteenPack:14 Pack`
  }

  /**
   * Returns translated string for 'Z9'
   * @returns {string} The translated string
   */
  getZ9Text(): string {
    return $localize`:General|Text for Z9@@general.text.z9:Z9`
  }

  /**
   * Returns translated string for '15 Pack'
   * @returns {string} The translated string
   */
  get15PackText(): string {
    return $localize`:General|Text for 15 Pack@@general.text.fifteenPack:15 Pack`
  }

  /**
   * Returns translated string for 'Zs'
   * @returns {string} The translated string
   */
  getZsText(): string {
    return $localize`:General|Text for Zs@@general.text.zs:Zs`
  }

  /**
   * Returns translated string for '150 Pack'
   * @returns {string} The translated string
   */
  get150PackText(): string {
    return $localize`:General|Text for 150 Pack@@general.text.oneHundredFiftyPack:150 Pack`
  }

  /**
   * Returns translated string for 'Zo'
   * @returns {string} The translated string
   */
  getZoText(): string {
    return $localize`:General|Text for Zo@@general.text.zo:Zo`
  }

  /**
   * Returns translated string for '16 Pack'
   * @returns {string} The translated string
   */
  get16PackText(): string {
    return $localize`:General|Text for 16 Pack@@general.text.sixteenPack:16 Pack`
  }

  /**
   * Returns translated string for 'Zf'
   * @returns {string} The translated string
   */
  getZfText(): string {
    return $localize`:General|Text for Zf@@general.text.zf:Zf`
  }

  /**
   * Returns translated string for '18 Pack'
   * @returns {string} The translated string
   */
  get18PackText(): string {
    return $localize`:General|Text for 18 Pack@@general.text.eighteenPack:18 Pack`
  }

  /**
   * Returns translated string for 'H3'
   * @returns {string} The translated string
   */
  getH3Text(): string {
    return $localize`:General|Text for H3@@general.text.h3:H3`
  }

  /**
   * Returns translated string for '2 Pack'
   * @returns {string} The translated string
   */
  get2PackText(): string {
    return $localize`:General|Text for 2 Pack@@general.text.twoPack:2 Pack`
  }

  /**
   * Returns translated string for 'Op'
   * @returns {string} The translated string
   */
  getOpText(): string {
    return $localize`:General|Text for Op@@general.text.op:Op`
  }

  /**
   * Returns translated string for '20 Pack'
   * @returns {string} The translated string
   */
  get20PackText(): string {
    return $localize`:General|Text for 20 Pack@@general.text.twentyPack:20 Pack`
  }

  /**
   * Returns translated string for '4e'
   * @returns {string} The translated string
   */
  get4eText(): string {
    return $localize`:General|Text for 4e@@general.text.fourE:4e`
  }

  /**
   * Returns translated string for '200 Pack'
   * @returns {string} The translated string
   */
  get200PackText(): string {
    return $localize`:General|Text for 200 Pack@@general.text.twoHundredPack:200 Pack`
  }

  /**
   * Returns translated string for 'Zq'
   * @returns {string} The translated string
   */
  getZqText(): string {
    return $localize`:General|Text for Zq@@general.text.zq:Zq`
  }

  /**
   * Returns translated string for '22 Pack'
   * @returns {string} The translated string
   */
  get22PackText(): string {
    return $localize`:General|Text for 22 Pack@@general.text.twentyTwoPack:22 Pack`
  }

  /**
   * Returns translated string for 'H5'
   * @returns {string} The translated string
   */
  getH5Text(): string {
    return $localize`:General|Text for H5@@general.text.h5:H5`
  }

  /**
   * Returns translated string for '24 Pack'
   * @returns {string} The translated string
   */
  get24PackText(): string {
    return $localize`:General|Text for 24 Pack@@general.text.twentyFourPack:24 Pack`
  }

  /**
   * Returns translated string for 'Q7'
   * @returns {string} The translated string
   */
  getQ7Text(): string {
    return $localize`:General|Text for Q7@@general.text.q7:Q7`
  }

  /**
   * Returns translated string for '25 Pack'
   * @returns {string} The translated string
   */
  get25PackText(): string {
    return $localize`:General|Text for 25 Pack@@general.text.twentyFivePack:25 Pack`
  }

  /**
   * Returns translated string for 'Q5'
   * @returns {string} The translated string
   */
  getQ5Text(): string {
    return $localize`:General|Text for Q5@@general.text.q5:Q5`
  }

  /**
   * Returns translated string for '250 Pack'
   * @returns {string} The translated string
   */
  get250PackText(): string {
    return $localize`:General|Text for 250 Pack@@general.text.twoHundredFiftyPack:250 Pack`
  }

  /**
   * Returns translated string for 'U5'
   * @returns {string} The translated string
   */
  getU5Text(): string {
    return $localize`:General|Text for U5@@general.text.u5:U5`
  }

  /**
   * Returns translated string for '3 Pack'
   * @returns {string} The translated string
   */
  get3PackText(): string {
    return $localize`:General|Text for 3 Pack@@general.text.threePack:3 Pack`
  }

  /**
   * Returns translated string for 'P3'
   * @returns {string} The translated string
   */
  getP3Text(): string {
    return $localize`:General|Text for P3@@general.text.p3:P3`
  }

  /**
   * Returns translated string for '30 Pack'
   * @returns {string} The translated string
   */
  get30PackText(): string {
    return $localize`:General|Text for 30 Pack@@general.text.thirtyPack:30 Pack`
  }

  /**
   * Returns translated string for 'H6'
   * @returns {string} The translated string
   */
  getH6Text(): string {
    return $localize`:General|Text for H6@@general.text.h6:H6`
  }

  /**
   * Returns translated string for '36 Pack'
   * @returns {string} The translated string
   */
  get36PackText(): string {
    return $localize`:General|Text for 36 Pack@@general.text.thirtySixPack:36 Pack`
  }

  /**
   * Returns translated string for 'Q6'
   * @returns {string} The translated string
   */
  getQ6Text(): string {
    return $localize`:General|Text for Q6@@general.text.q6:Q6`
  }

  /**
   * Returns translated string for '38 Pack'
   * @returns {string} The translated string
   */
  get38PackText(): string {
    return $localize`:General|Text for 38 Pack@@general.text.thirtyEightPack:38 Pack`
  }

  /**
   * Returns translated string for 'H7'
   * @returns {string} The translated string
   */
  getH7Text(): string {
    return $localize`:General|Text for H7@@general.text.h7:H7`
  }

  /**
   * Returns translated string for '4 Pack'
   * @returns {string} The translated string
   */
  get4PackText(): string {
    return $localize`:General|Text for 4 Pack@@general.text.fourPack:4 Pack`
  }

  /**
   * Returns translated string for 'P4'
   * @returns {string} The translated string
   */
  getP4Text(): string {
    return $localize`:General|Text for P4@@general.text.p4:P4`
  }

  /**
   * Returns translated string for '48 Pack'
   * @returns {string} The translated string
   */
  get48PackText(): string {
    return $localize`:General|Text for 48 Pack@@general.text.fortyEightPack:48 Pack`
  }

  /**
   * Returns translated string for 'Zg'
   * @returns {string} The translated string
   */
  getZgText(): string {
    return $localize`:General|Text for Zg@@general.text.zg:Zg`
  }

  /**
   * Returns translated string for '5 Pack'
   * @returns {string} The translated string
   */
  get5PackText(): string {
    return $localize`:General|Text for 5 Pack@@general.text.fivePack:5 Pack`
  }

  /**
   * Returns translated string for 'P5'
   * @returns {string} The translated string
   */
  getP5Text(): string {
    return $localize`:General|Text for P5@@general.text.p5:P5`
  }

  /**
   * Returns translated string for '50 Pack'
   * @returns {string} The translated string
   */
  get50PackText(): string {
    return $localize`:General|Text for 50 Pack@@general.text.fiftyPack:50 Pack`
  }

  /**
   * Returns translated string for 'Q4'
   * @returns {string} The translated string
   */
  getQ4Text(): string {
    return $localize`:General|Text for Q4@@general.text.q4:Q4`
  }

  /**
   * Returns translated string for '500 Pack'
   * @returns {string} The translated string
   */
  get500PackText(): string {
    return $localize`:General|Text for 500 Pack@@general.text.fiveHundredPack:500 Pack`
  }

  /**
   * Returns translated string for 'Vc'
   * @returns {string} The translated string
   */
  getVcText(): string {
    return $localize`:General|Text for Vc@@general.text.vc:Vc`
  }

  /**
   * Returns translated string for '5000 Pack'
   * @returns {string} The translated string
   */
  get5000PackText(): string {
    return $localize`:General|Text for 5000 Pack@@general.text.fiveThousandPack:5000 Pack`
  }

  /**
   * Returns translated string for 'J8'
   * @returns {string} The translated string
   */
  getJ8Text(): string {
    return $localize`:General|Text for J8@@general.text.j8:J8`
  }

  /**
   * Returns translated string for '52 Pack'
   * @returns {string} The translated string
   */
  get52PackText(): string {
    return $localize`:General|Text for 52 Pack@@general.text.fiftyTwoPack:52 Pack`
  }

  /**
   * Returns translated string for 'Zi'
   * @returns {string} The translated string
   */
  getZiText(): string {
    return $localize`:General|Text for Zi@@general.text.zi:Zi`
  }

  /**
   * Returns translated string for '6 Pack'
   * @returns {string} The translated string
   */
  get6PackText(): string {
    return $localize`:General|Text for 6 Pack@@general.text.sixPack:6 Pack`
  }

  /**
   * Returns translated string for 'P6'
   * @returns {string} The translated string
   */
  getP6Text(): string {
    return $localize`:General|Text for P6@@general.text.p6:P6`
  }

  /**
   * Returns translated string for '60 Pack'
   * @returns {string} The translated string
   */
  get60PackText(): string {
    return $localize`:General|Text for 60 Pack@@general.text.sixtyPack:60 Pack`
  }

  /**
   * Returns translated string for 'Zj'
   * @returns {string} The translated string
   */
  getZjText(): string {
    return $localize`:General|Text for Zj@@general.text.zj:Zj`
  }

  /**
   * Returns translated string for '62 Pack'
   * @returns {string} The translated string
   */
  get62PackText(): string {
    return $localize`:General|Text for 62 Pack@@general.text.sixtyTwoPack:62 Pack`
  }

  /**
   * Returns translated string for 'H8'
   * @returns {string} The translated string
   */
  getH8Text(): string {
    return $localize`:General|Text for H8@@general.text.h8:H8`
  }

  /**
   * Returns translated string for '7 Pack'
   * @returns {string} The translated string
   */
  get7PackText(): string {
    return $localize`:General|Text for 7 Pack@@general.text.sevenPack:7 Pack`
  }

  /**
   * Returns translated string for 'P7'
   * @returns {string} The translated string
   */
  getP7Text(): string {
    return $localize`:General|Text for P7@@general.text.p7:P7`
  }

  /**
   * Returns translated string for '72 Pack'
   * @returns {string} The translated string
   */
  get72PackText(): string {
    return $localize`:General|Text for 72 Pack@@general.text.seventyTwoPack:72 Pack`
  }

  /**
   * Returns translated string for '75 Pack'
   * @returns {string} The translated string
   */
  get75PackText(): string {
    return $localize`:General|Text for 75 Pack@@general.text.seventyFivePack:75 Pack`
  }

  /**
   * Returns translated string for 'H9'
   * @returns {string} The translated string
   */
  getH9Text(): string {
    return $localize`:General|Text for H9@@general.text.h9:H9`
  }

  /**
   * Returns translated string for '8 Pack'
   * @returns {string} The translated string
   */
  get8PackText(): string {
    return $localize`:General|Text for 8 Pack@@general.text.eightPack:8 Pack`
  }

  /**
   * Returns translated string for 'P8'
   * @returns {string} The translated string
   */
  getP8Text(): string {
    return $localize`:General|Text for P8@@general.text.p8:P8`
  }

  /**
   * Returns translated string for '9 Pack'
   * @returns {string} The translated string
   */
  get9PackText(): string {
    return $localize`:General|Text for 9 Pack@@general.text.ninePack:9 Pack`
  }

  /**
   * Returns translated string for 'P9'
   * @returns {string} The translated string
   */
  getP9Text(): string {
    return $localize`:General|Text for P9@@general.text.p9:P9`
  }

  /**
   * Returns translated string for '96 Pack'
   * @returns {string} The translated string
   */
  get96PackText(): string {
    return $localize`:General|Text for 96 Pack@@general.text.ninetySixPack:96 Pack`
  }

  /**
   * Returns translated string for 'J7'
   * @returns {string} The translated string
   */
  getJ7Text(): string {
    return $localize`:General|Text for J7@@general.text.j7:J7`
  }

  /**
   * Returns translated string for 'Assembly'
   * @returns {string} The translated string
   */
  getAssemblyText(): string {
    return $localize`:General|Text for Assembly@@general.text.assembly:Assembly`
  }

  /**
   * Returns translated string for 'Ay'
   * @returns {string} The translated string
   */
  getAyText(): string {
    return $localize`:General|Text for Ay@@general.text.ay:Ay`
  }

  /**
   * Returns translated string for 'Assortment'
   * @returns {string} The translated string
   */
  getAssortmentText(): string {
    return $localize`:General|Text for Assortment@@general.text.assortment:Assortment`
  }

  /**
   * Returns translated string for 'As'
   * @returns {string} The translated string
   */
  getAsText(): string {
    return $localize`:General|Text for As@@general.text.as:As`
  }

  /**
   * Returns translated string for 'Bag'
   * @returns {string} The translated string
   */
  getBagText(): string {
    return $localize`:General|Text for Bag@@general.text.bag:Bag`
  }

  /**
   * Returns translated string for 'Bg'
   * @returns {string} The translated string
   */
  getBgText(): string {
    return $localize`:General|Text for Bg@@general.text.bg:Bg`
  }

  /**
   * Returns translated string for 'Bale'
   * @returns {string} The translated string
   */
  getBaleText(): string {
    return $localize`:General|Text for Bale@@general.text.bale:Bale`
  }

  /**
   * Returns translated string for 'Ba'
   * @returns {string} The translated string
   */
  getBaText(): string {
    return $localize`:General|Text for Ba@@general.text.ba:Ba`
  }

  /**
   * Returns translated string for '5b'
   * @returns {string} The translated string
   */
  get5bText(): string {
    return $localize`:General|Text for 5b@@general.text.5b:5b`
  }

  /**
   * Returns translated string for 'Bin'
   * @returns {string} The translated string
   */
  getBinText(): string {
    return $localize`:General|Text for Bin@@general.text.bin:Bin`
  }

  /**
   * Returns translated string for '2w'
   * @returns {string} The translated string
   */
  get2wText(): string {
    return $localize`:General|Text for 2w@@general.text.2w:2w`
  }

  /**
   * Returns translated string for 'Board Feet'
   * @returns {string} The translated string
   */
  getBoardFeetText(): string {
    return $localize`:General|Text for Board Feet@@general.text.boardFeet:Board Feet`
  }

  /**
   * Returns translated string for 'Bf'
   * @returns {string} The translated string
   */
  getBfText(): string {
    return $localize`:General|Text for Bf@@general.text.bf:Bf`
  }

  /**
   * Returns translated string for 'Bottle'
   * @returns {string} The translated string
   */
  getBottleText(): string {
    return $localize`:General|Text for Bottle@@general.text.bottle:Bottle`
  }

  /**
   * Returns translated string for 'Bo'
   * @returns {string} The translated string
   */
  getBoText(): string {
    return $localize`:General|Text for Bo@@general.text.bo:Bo`
  }

  /**
   * Returns translated string for 'Box'
   * @returns {string} The translated string
   */
  getBoxText(): string {
    return $localize`:General|Text for Box@@general.text.box:Box`
  }

  /**
   * Returns translated string for 'Bx'
   * @returns {string} The translated string
   */
  getBxText(): string {
    return $localize`:General|Text for Bx@@general.text.bx:Bx`
  }

  /**
   * Returns translated string for 'Bucket'
   * @returns {string} The translated string
   */
  getBucketText(): string {
    return $localize`:General|Text for Bucket@@general.text.bucket:Bucket`
  }

  /**
   * Returns translated string for 'Bc'
   * @returns {string} The translated string
   */
  getBcText(): string {
    return $localize`:General|Text for Bc@@general.text.bc:Bc`
  }

  /**
   * Returns translated string for 'Bundle'
   * @returns {string} The translated string
   */
  getBundleText(): string {
    return $localize`:General|Text for Bundle@@general.text.bundle:Bundle`
  }

  /**
   * Returns translated string for 'Bd'
   * @returns {string} The translated string
   */
  getBdText(): string {
    return $localize`:General|Text for Bd@@general.text.bd:Bd`
  }

  /**
   * Returns translated string for 'Can'
   * @returns {string} The translated string
   */
  getCanText(): string {
    return $localize`:General|Text for Can@@general.text.can:Can`
  }

  /**
   * Returns translated string for 'Cn'
   * @returns {string} The translated string
   */
  getCnText(): string {
    return $localize`:General|Text for Cn@@general.text.cn:Cn`
  }

  /**
   * Returns translated string for 'Card'
   * @returns {string} The translated string
   */
  getCardText(): string {
    return $localize`:General|Text for Card@@general.text.card:Card`
  }

  /**
   * Returns translated string for 'Cg'
   * @returns {string} The translated string
   */
  getCgText(): string {
    return $localize`:General|Text for Cg@@general.text.cg:Cg`
  }

  /**
   * Returns translated string for 'Carton'
   * @returns {string} The translated string
   */
  getCartonText(): string {
    return $localize`:General|Text for Carton@@general.text.carton:Carton`
  }

  /**
   * Returns translated string for 'Ct'
   * @returns {string} The translated string
   */
  getCtText(): string {
    return $localize`:General|Text for Ct@@general.text.ct:Ct`
  }

  /**
   * Returns translated string for 'Case'
   * @returns {string} The translated string
   */
  getCaseText(): string {
    return $localize`:General|Text for Case@@general.text.case:Case`
  }

  /**
   * Returns translated string for 'Ca'
   * @returns {string} The translated string
   */
  getCaText(): string {
    return $localize`:General|Text for Ca@@general.text.ca:Ca`
  }

  /**
   * Returns translated string for 'Cassette'
   * @returns {string} The translated string
   */
  getCassetteText(): string {
    return $localize`:General|Text for Cassette@@general.text.cassette:Cassette`
  }

  /**
   * Returns translated string for 'Cs'
   * @returns {string} The translated string
   */
  getCsText(): string {
    return $localize`:General|Text for Cs@@general.text.cs:Cs`
  }

  /**
   * Returns translated string for 'Coil'
   * @returns {string} The translated string
   */
  getCoilText(): string {
    return $localize`:General|Text for Coil@@general.text.coil:Coil`
  }

  /**
   * Returns translated string for 'Cx'
   * @returns {string} The translated string
   */
  getCxText(): string {
    return $localize`:General|Text for Cx@@general.text.cx:Cx`
  }

  /**
   * Returns translated string for 'Cone'
   * @returns {string} The translated string
   */
  getConeText(): string {
    return $localize`:General|Text for Cone@@general.text.cone:Cone`
  }

  /**
   * Returns translated string for 'Cj'
   * @returns {string} The translated string
   */
  getCjText(): string {
    return $localize`:General|Text for Cj@@general.text.cj:Cj`
  }

  /**
   * Returns translated string for 'Container'
   * @returns {string} The translated string
   */
  getContainerText(): string {
    return $localize`:General|Text for Container@@general.text.container:Container`
  }

  /**
   * Returns translated string for 'Ch'
   * @returns {string} The translated string
   */
  getChText(): string {
    return $localize`:General|Text for Ch@@general.text.ch:Ch`
  }

  /**
   * Returns translated string for 'Crate'
   * @returns {string} The translated string
   */
  getCrateText(): string {
    return $localize`:General|Text for Crate@@general.text.crate:Crate`
  }

  /**
   * Returns translated string for 'Cp'
   * @returns {string} The translated string
   */
  getCpText(): string {
    return $localize`:General|Text for Cp@@general.text.cp:Cp`
  }

  /**
   * Returns translated string for 'Cubic Decimeter'
   * @returns {string} The translated string
   */
  getCubicDecimeterText(): string {
    return $localize`:General|Text for Cubic Decimeter@@general.text.cubic_decimeter:Cubic Decimeter`
  }

  /**
   * Returns translated string for 'C8'
   * @returns {string} The translated string
   */
  getC8Text(): string {
    return $localize`:General|Text for C8@@general.text.c8:C8`
  }

  /**
   * Returns translated string for 'Cubic Yard'
   * @returns {string} The translated string
   */
  getCubicYardText(): string {
    return $localize`:General|Text for Cubic Yard@@general.text.cubic_yard:Cubic Yard`
  }

  /**
   * Returns translated string for 'Cy'
   * @returns {string} The translated string
   */
  getCyText(): string {
    return $localize`:General|Text for Cy@@general.text.cy:Cy`
  }

  /**
   * Returns translated string for 'cylinder'
   * @returns {string} The translated string
   */
  getCylinderText(): string {
    return $localize`:General|Text for cylinder@@general.text.cylinder:Cylinder`
  }

  /**
   * Returns translated string for 'cl'
   * @returns {string} The translated string
   */
  getClText(): string {
    return $localize`:General|Text for cl@@general.text.cl:cl`
  }

  /**
   * Returns translated string for 'days'
   * @returns {string} The translated string
   */
  getDaysText(): string {
    return $localize`:General|Text for days@@general.text.days:Days`
  }

  /**
   * Returns translated string for 'da'
   * @returns {string} The translated string
   */
  getDaText(): string {
    return $localize`:General|Text for da@@general.text.da:da`
  }

  /**
   * Returns translated string for 'deal'
   * @returns {string} The translated string
   */
  getDealText(): string {
    return $localize`:General|Text for deal@@general.text.deal:Deal`
  }

  /**
   * Returns translated string for 'de'
   * @returns {string} The translated string
   */
  getDeText(): string {
    return $localize`:General|Text for de@@general.text.de:de`
  }

  /**
   * Returns translated string for 'deciliter'
   * @returns {string} The translated string
   */
  getDeciliterText(): string {
    return $localize`:General|Text for deciliter@@general.text.deciliter:Deciliter`
  }

  /**
   * Returns translated string for 'dl'
   * @returns {string} The translated string
   */
  getDlText(): string {
    return $localize`:General|Text for dl@@general.text.dl:dl`
  }

  /**
   * Returns translated string for 'Decimeter'
   * @returns {string} The translated string
   */
  getDecimeterText(): string {
    return $localize`:General|Text for decimeter@@general.text.decimeter:Decimeter`
  }

  /**
   * Returns translated string for 'Dm'
   * @returns {string} The translated string
   */
  getDmText(): string {
    return $localize`:General|Text for dm@@general.text.dm:dm`
  }

  /**
   * Returns translated string for 'Dosage Form'
   * @returns {string} The translated string
   */
  getDosageFormText(): string {
    return $localize`:General|Text for dosage form@@general.text.dosageForm:Dosage Form`
  }

  /**
   * Returns translated string for 'Dozen'
   * @returns {string} The translated string
   */
  getDozenText(): string {
    return $localize`:General|Text for dozen@@general.text.dozen:Dozen`
  }

  /**
   * Returns translated string for 'dz'
   * @returns {string} The translated string
   */
  getDzText(): string {
    return $localize`:General|Text for dz@@general.text.dz:dz`
  }

  /**
   * Returns translated string for 'Drum'
   * @returns {string} The translated string
   */
  getDrumText(): string {
    return $localize`:General|Text for drum@@general.text.drum:Drum`
  }

  /**
   * Returns translated string for 'dr'
   * @returns {string} The translated string
   */
  getDrText(): string {
    return $localize`:General|Text for dr@@general.text.dr:dr`
  }

  /**
   * Returns translated string for 'Cubic Unit'
   * @returns {string} The translated string
   */
  getCubicUnitText(): string {
    return $localize`:General|Text for cubic unit@@general.text.cubicUnit:Cubic Unit`
  }

  /**
   * Returns translated string for 'cu'
   * @returns {string} The translated string
   */
  getCuText(): string {
    return $localize`:General|Text for cu@@general.text.cu:cu`
  }

  /**
   * Returns translated string for 'Zk'
   * @returns {string} The translated string
   */
  getZkText(): string {
    return $localize`:General|Text for 2k@@general.text.Zk:Zk`
  }

  /**
   * Returns translated string for 'Create Template'
   * @returns {string} The translated string
   */
  getCreateTemplateText(): string {
    return $localize`:General|Text for input/information@@general.text.createTemplate:Create Template`
  }

  /**
   * Returns translated string for 'Product CSV Templates'
   * @returns {string} The translated string
   */
  getProductCSVTemplatesText(): string {
    return $localize`:General|Text for input/information@@general.text.productCsvTemplates:Product CSV Templates`
  }

  /**
   * Returns translated string for 'Create CSV Template'
   * @returns {string} The translated string
   */
  getCreateCSVTemplateText(): string {
    return $localize`:General|Text for input/information@@general.text.createCsvTemplate:Create CSV Template`
  }

  /**
   * Returns translated string for template already exists with name.
   * @returns {string} The translated string
   */
  getTemplateAlreadyExistsWithNameForCompanyError(): string {
    return $localize`:API Error|Template Name Exists for Company Error@@apiError.templateNameAlreadyExistsForCompany:A template with the given name already exists for your company.`
  }

  /**
   * Returns translated string for 'CSV Template created successfully.'
   * @returns {string} The translated string
   */
  getCSVTemplateCreatedText(): string {
    return $localize`:General|Text for input/information@@general.text.csvTemplateCreated:CSV Template created successfully.`
  }

  /**
   * Returns translated string for '$templateName Fields'
   * @param {string} name - The name of the template
   * @returns {string} The translated string
   */
  getTemplateNameFieldsText(name: string): string {
    return $localize`:General|Text for input/information@@general.text.templateNameFields:${name} Fields`
  }

  /**
   * Returns translated string for 'System Field'
   * @returns {string} The translated string
   */
  getSystemFieldText(): string {
    return $localize`:General|Text for input/information@@general.text.systemField:System Field`
  }

  /**
   * Returns translated string for 'User Defined Field'
   * @returns {string} The translated string
   */
  getUserDefinedFieldText(): string {
    return $localize`:General|Text for input/information@@general.text.userDefinedField:User Defined Field`
  }

  /**
   * Returns translated string for 'Edit CSV Template'
   * @returns {string} The translated string
   */
  getEditCSVTemplateText(): string {
    return $localize`:General|Text for input/information@@general.text.editCsvTemplate:Edit CSV Template`
  }

  /*
   * Returns translated string for 'CSV Template Name'
   * @returns {string} The translated string
   */
  getCsvTemplateNameText(): string {
    return $localize`:General|Text for input/information@@general.text.csvTemplateName:CSV Template Name`
  }

  /**
   * Returns translated string for prompt to enter field mappings
   * @returns {string} The translated string
   */
  getFieldMappingPromptText(): string {
    return $localize`:General|Text for input/information@@general.text.fieldMappingPrompt:Please enter the corresponding field names used within your files that
    will be uploaded to the system below.`
  }

  /**
   * Returns translated string for 'CSV Template updated successfully.'
   * @returns {string} The translated string
   */
  getCSVTemplateUpdatedText(): string {
    return $localize`:General|Text for input/information@@general.text.csvTemplateUpdated:CSV Template updated successfully.`
  }

  /*
   * Returns translated string for 'Delete'
   * @returns {string} The translated string
   */
  getDeleteText(): string {
    return $localize`:General|Text for input/information@@general.text.delete:Delete`
  }

  /**
   * Returns translated string for 'Template deleted successfully'
   * @returns {string} The translated string
   */
  getTemplateDeletedText(): string {
    return $localize`:General|Text for input/information@@general.text.templateDeletedSuccessfully:Template deleted successfully.`
  }

  /**
   * Returns translated string for 'Confirm'
   * @returns {string} The translated string
   */
  getConfirmText(): string {
    return $localize`:General|Text for input/information@@general.text.confirm:Confirm`
  }

  /**
   * Returns translated string for 'Cancel'
   * @returns {string} The translated string
   */
  getCancelText(): string {
    return $localize`:General|Text for input/information@@general.text.cancel:Cancel`
  }

  /**
   * Returns translated string for 'Import'
   * @returns {string} The translated string
   */
  getImportText(): string {
    return $localize`:General|Text for input/information@@general.text.import:Import`
  }

  /**
   * Returns translated string for 'Step 1: Upload & Validate Files'
   * @returns {string} The translated string
   */
  getUploadAndValidateFilesText(): string {
    return $localize`:General|Text for input/information@@general.text.uploadAndValidateFiles:Step 1: Upload & Validate Files`
  }

  /**
   * Returns translated string for product csv template selection
   * @returns {string} The translated string
   */
  getProductCsvTemplateSelectionMessage(): string {
    return $localize`:General|Text for input/information@@general.text.productCsvTemplateSelectionMessage:Please select the CSV Template to be used for the product importation process.`
  }

  /**
   * Returns translated string for product import message
   * @returns {string} The translated string
   */
  getProductImportFilesMessage(): string {
    return $localize`:General|Text for input/information@@general.text.productImportMessage:Below you will be required to select the product import file as well as the corresponding documents for the products.`
  }

  /**
   * Returns translated string for 'Products Imported Successfully'
   * @returns {string} The translated string
   */
  getImportProductsSuccessText(): string {
    return $localize`:General|Text for input/information@@general.text.importProductsSuccess:Products Imported Successfully.`
  }

  /**
   * Returns translated string for 'A template with the given name already exists for your company.'
   * @returns {string} The translated string
   */
  getTemplateNameAlreadyExistsForCompanyText(): string {
    return $localize`:API Error|Template Name Exists@@apiError.templateNameAlreadyExistsForCompany:A template with the given name already exists for your company.`
  }

  /**
   * Returns translated string for 'The product import file you have uploaded exceeds the limit of 1000 products.'
   * @returns {string} The translated string
   */
  getProductImportLimitExceededText(): string {
    return $localize`:API Error|Product Import Limit@@apiError.productImportLimitExceeded:The product import file you have uploaded exceeds the limit of 1000 products.`
  }

  /**
   * Returns translated string for 'Please specify the product pictures, safety data sheets and recycling instructions for the products you would like to import.'
   * @returns {string} The translated string
   */
  getNoFilesUploadedForProductImportText(): string {
    return $localize`:API Error|No Files Uploaded@@apiError.noFilesUploadedForProductImport:Please specify the product pictures, safety data sheets and recycling instructions for the products you would like to import.`
  }

  /**
   * Returns translated string for 'Please specify the id of the template to use for product importation.'
   * @returns {string} The translated string
   */
  getTemplateRequiredForProductImportText(): string {
    return $localize`:API Error|Template Required@@apiError.templateRequiredForProductImport:Please specify the id of the template to use for product importation.`
  }

  /**
   * Returns translated string for 'Please specify the csv with the products to be imported.'
   * @returns {string} The translated string
   */
  getProductImportFileMissingText(): string {
    return $localize`:API Error|Import File Missing@@apiError.productImportFileMissing:Please specify the csv with the products to be imported.`
  }

  /**
   * Returns translated string for 'Something went wrong opening the product import csv.'
   * @returns {string} The translated string
   */
  getErrorOpeningImportCSVText(): string {
    return $localize`:API Error|Opening Import CSV@@apiError.errorOpeningImportCSV:Something went wrong opening the product import csv.`
  }

  /**
   * Returns translated string for 'Something went wrong parsing the product import csv.'
   * @returns {string} The translated string
   */
  getErrorParsingCSVText(): string {
    return $localize`:API Error|Parsing CSV@@apiError.errorParsingCSV:Something went wrong parsing the product import csv.`
  }

  /**
   * Returns translated string for 'A category code is required for all products.'
   * @returns {string} The translated string
   */
  getCategoryCodeRequiredForAllProductsText(): string {
    return $localize`:API Error|Category Code Missing@@apiError.categoryCodeRequiredForAllProducts:A category code is required for all products.`
  }

  /**
   * Returns translated string for 'One or more products are missing associated files.'
   * @returns {string} The translated string
   */
  getProductsMissingFilesForImportText(): string {
    return $localize`:API Error|Files Missing@@apiError.productsMissingFilesForImport:One or more products are missing associated files.`
  }

  /**
   * Returns translated string for 'An error occurred while attempting to associate the given files with the products.'
   * @returns {string} The translated string
   */
  getErrorAssociatingProductsWithFilesText(): string {
    return $localize`:API Error|Error Associating Files@@apiError.errorAssociatingProductsWithFiles:An error occurred while attempting to associate the given files with the products.`
  }

  /**
   * Returns translated string for 'Product Import File'
   * @returns {string} The translated string
   */
  getProductImportFileText(): string {
    return $localize`:API Error|Product Import Limit@@apiError.productImportFile:Product Import File`
  }

  /**
   * Returns translated string for 'Pictures'
   * @returns {string} The translated string
   */
  getPicturesText(): string {
    return $localize`:General|Text for Picture@@general.text.pictures:Pictures`
  }

  /**
   * Returns translated string for 'Safety Data Sheets'
   * @returns {string} The translated string
   */
  getSafetyDataSheetsText(): string {
    return $localize`:General|Text for input/information@@general.text.safetyDataSheets:Safety Data Sheets`
  }

  /**
   * Returns translated string for template fields missing from the import csv.
   * @param {Array<string>} missingFields The missing template field names
   * @returns {string} The translated string
   */
  getTemplateFieldNotFoundError(missingFields: string[]): string {
    const baseMessage = $localize`:API Error|Error Mapping template fields@@apiError.templateFieldNotFound:One or more fields from your selected template could not be found in the attached import csv.`
    const missingFieldsLabel = $localize`:API Error|Missing template fields label@@apiError.templateFieldNotFound.missingFieldsLabel:Missing fields:`

    if (!missingFields.length) {
      return baseMessage
    }

    return `${baseMessage}\n\n${missingFieldsLabel}\n- ${missingFields.join('\n- ')}`
  }

  /**
   * Returns translated string for 'Validation Errors'
   * @returns {string} The translated string
   */
  getValidationErrorsText(): string {
    return $localize`:General|Text for input/information@@general.text.validationErrors:Validation Errors`
  }

  /**
   * Returns translated string for 'Click to view/hide validation errors for this product'
   * @returns {string} The translated string
   */
  getToggleValidationErrorsMessage(): string {
    return $localize`:General|Text for input/information@@general.text.toggleValidationErrors:Click to view/hide validation errors for this product`
  }

  /**
   * Returns the translated string for the review products message
   * @returns {string} The translated string
   */
  getReviewProductsMessage(): string {
    return $localize`:General|Message to review imported products@@general.text.reviewProducts:Review the imported products below. Products with errors are listed first and marked with a red indicator on the left, while valid products are marked green.`
  }

  /**
   * Returns translated string for 'A primary article identifier is required for all products.'
   * @returns {string} The translated string
   */
  getPrimaryArticleIdentifierRequiredForAllProductsError(): string {
    return $localize`:API Error|Error with Primary Article Identifiers@@apiError.primaryArticleIdentifierRequiredForAllProducts:A primary article identifier is required for all products.`
  }

  /**
   * Returns translated string for 'DuplicatePrimaryIdentifiersFound'
   * @returns {string} The translated string
   */
  getDuplicatePrimaryIdentifiersFoundError(): string {
    return $localize`:API Error|Error with Primary Article Identifiers@@apiError.duplicatePrimaryIdentifiersFound:Products were found with duplicate primary article identifiers.`
  }

  /**
   * Returns translated string for 'Click the checkmark button at the bottom-right to create your products.'
   * @returns {string} The translated string
   */
  getCheckmarkButtonText(): string {
    return $localize`:General|Text for button at bottom-right@@general.text.checkmarkButton:Click the checkmark button at the bottom-right to create your products.`
  }

  /**
   * Returns translated string for 'products valid'
   * @returns {string} The translated string
   */
  getProductsValidText(): string {
    return $localize`:General|Text for valid products count@@general.text.productsValid:products valid`
  }

  /**
   * Returns translated string for 'Validate'
   * @returns {string} The translated string
   */
  getValidateText(): string {
    return $localize`:General|Text for input/information@@general.text.validate:Validate`
  }

  /**
   * Returns translated string for 'Step 2: Review & Submit Products'
   * @returns {string} The translated string
   */
  getReviewAndSubmitProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.reviewAndSubmitProducts:Step 2: Review & Submit Products`
  }

  /**
   * Returns translated string for 'Scroll to the top of the page'
   * @returns {string} The translated string
   */
  getScrollToTopText(): string {
    return $localize`:General|Button/Action Text@@general.text.scrollToTop:Scroll to the top of the page`
  }

  /**
   * Returns translated string for 'Scroll to the bottom of the page'
   * @returns {string} The translated string
   */
  getScrollToBottomText(): string {
    return $localize`:General|Button/Action Text@@general.text.scrollToBottom:Scroll to the bottom of the page`
  }

  /**
   * Returns translated string for 'Submit products'
   * @returns {string} The translated string
   */
  getSubmitProductsText(): string {
    return $localize`:General|Button/Action Text@@general.text.submitProducts:Submit products`
  }

  /**
   * Returns translated string for 'Resolve validation issues before submission'
   * @returns {string} The translated string
   */
  getResolveValidationIssuesText(): string {
    return $localize`:General|Error/Warning Text@@general.text.resolveValidationIssues:Resolve validation issues before submission`
  }

  /**
   * Returns translated string for 'Import Products'
   * @returns {string} The translated string
   */
  getImportProductsText(): string {
    return $localize`:General|Text for input/information@@general.text.importProducts:Import Products`
  }

  /**
   * Returns translated string for 'Validation complete with no errors'
   * @returns {string} The translated string
   */
  getValidationCompleteText(): string {
    return $localize`:Validation Status|Message@@validation.status.complete:Validation complete with no errors`
  }

  /**
   * Returns translated string for 'Validation errors detected'
   * @returns {string} The translated string
   */
  getValidationErrorsDetectedText(): string {
    return $localize`:Validation Status|Message@@validation.status.errors:Validation errors detected. Please address the issue(s) and reupload your files.`
  }

  /**
   * Returns translated string for 'Files successfully uploaded and validated'
   * @returns {string} The translated string
   */
  getFilesUploadedAndValidatedText(): string {
    return $localize`:Validation Status|Message@@validation.status.filesUploaded:Files successfully uploaded and validated`
  }

  /**
   * Returns translated string for 'Select Country'
   * @returns {string} The translated string
   */
  getSelectCountryText(): string {
    return $localize`:General|Text for country select dropdown@@general.text.selectCountry:Select Country`
  }

  /**
   * Returns translated string for selecting product country recyclability status.
   * @returns {string} The translated string
   */
  getSelectProductCountryRecyclabilityStatusText(): string {
    return $localize`:General|Text for input/information@@general.text.selectProductCountryRecyclabilityStatus:Please select the product recyclability status for`
  }

  /**
   * Returns translated string for 'Waste Paper'
   * @returns {string} The translated string
   */
  getWastePaperText(): string {
    return $localize`:Recyclability|Text for waste paper@@recyclability.text.wastePaper:Waste Paper`
  }

  /**
   * Returns translated string for 'Plastic Bottles'
   * @returns {string} The translated string
   */
  getPlasticBottlesText(): string {
    return $localize`:Recyclability|Text for plastic bottles@@recyclability.text.plasticBottles:Plastic Bottles`
  }

  /**
   * Returns translated string for 'Aluminum Cans'
   * @returns {string} The translated string
   */
  getAluminumCansText(): string {
    return $localize`:Recyclability|Text for aluminum cans@@recyclability.text.aluminumCans:Aluminum Cans`
  }

  /**
   * Returns translated string for 'Enter the recyclability status of the product in {{ country }}.
   * See below for the recyclability statuses and their corresponding icons.'
   * @param country {string} The country to include in the message
   * @returns {string} The translated string
   */
  getEnterRecyclabilityStatusText(country: string): string {
    return $localize`:Recyclability|Instruction for entering recyclability status@@recyclability.instruction.enterStatus:Enter the recyclability status of the product in ${country}. See below for the recyclability statuses and their corresponding icons.`
  }

  /**
   * Returns translated string for 'Please select the countries for which recyclability will be specified for the product.'
   * @returns {string} The translated string
   */
  getSelectCountriesText(): string {
    return $localize`:Recyclability|Instruction for selecting countries@@recyclability.instruction.selectCountries:Please select the countries for which recyclability will be specified for the product.`
  }

  /**
   * Returns translated string for 'Product Countries'
   * @returns {string} The translated string
   */
  getProductCountriesText(): string {
    return $localize`:General|Text for input/information@@general.text.productCountries:Product Countries`
  }

  /**
   * Returns translated string for 'Verification Code'
   * @returns {string} The translated string
   */
  getVerificationCodeText(): string {
    return $localize`:General|Text for input/information@@general.text.verificationCode:Verification Code`
  }

  /**
   * Returns translated string for 'A verification code was sent to your email.'
   * @returns {string} The translated string
   */
  getVerificationCodeSentText(): string {
    return $localize`:General|Text for notification/information@@general.text.verificationCodeSent:A verification code was sent to your email.`
  }

  /**
   * Returns translated string for 'The provided MFA code is invalid. Please try again.'
   * @returns {string} The translated string
   */
  getInvalidMfaCodeErrorText(): string {
    return $localize`:API Error|Invalid MFA Code@@apiError.invalidMfaCode:The provided MFA code is invalid. Please try again.`
  }

  /**
   * Returns translated string for 'Verify'
   * @returns {string} The translated string
   */
  getVerifyText(): string {
    return $localize`:General|Text for input/information@@general.text.verify:Verify`
  }

  /**
   * Returns translated string for 'Resend'
   * @returns {string} The translated string
   */
  getResendText(): string {
    return $localize`:General|Text for input/information@@general.text.resend:Resend`
  }

  /**
   * Returns the translated string for 'Didn't receive your code? Click here to have it resent.'
   * @returns {string} The translated string
   */
  getResendCodeText(): string {
    return $localize`:API Error|MFA Resend Code@@apiError.mfaResendCode:Didn't receive your code? Click here to have it resent.`
  }

  /**
   * Returns translated string for 'MFA is always required for master users.'
   * @returns {string} The translated string
   */
  getMfaAlwaysRequiredForMasterText(): string {
    return $localize`:API Error|MFA Always Required for Master@@apiError.mfaAlwaysRequiredForMaster:MFA is always required for master users.`
  }

  /**
   * Returns translated string for 'You are not permitted to change your own MFA status.'
   * @returns {string} The translated string
   */
  getCannotChangeOwnMfaText(): string {
    return $localize`:API Error|Cannot Change Own MFA@@apiError.cannotChangeOwnMfa:You are not permitted to change your own MFA status.`
  }

  /**
   * Returns translated string for 'You are not permitted to change the MFA status of users outside your company.'
   * @returns {string} The translated string
   */
  getCannotChangeMfaOutsideCompanyText(): string {
    return $localize`:API Error|Cannot Change MFA Outside Company@@apiError.cannotChangeMfaOutsideCompany:You are not permitted to change the MFA status of users outside your company.`
  }

  /**
   * Returns translated string for 'You are not permitted to change the MFA status of other admin users.'
   * @returns {string} The translated string
   */
  getCannotChangeMfaForAdminsText(): string {
    return $localize`:API Error|Cannot Change MFA for Admins@@apiError.cannotChangeMfaForAdmins:You are not permitted to change the MFA status of other admin users.`
  }

  /**
   * Returns translated string for 'Multi-Factor Authentication'
   * @returns {string} The translated string
   */
  getMfaText(): string {
    return $localize`:Authentication|Text for multi-factor authentication@@authentication.text.mfa:Multi-Factor Authentication`
  }

  /**
   * Returns translated string for 'Disable'
   * @returns {string} The translated string
   */
  getDisableText(): string {
    return $localize`:General|Text for disabling something@@general.text.disable:Disable`
  }

  /**
   * Returns translated string for 'Enable'
   * @returns {string} The translated string
   */
  getEnableText(): string {
    return $localize`:General|Text for enabling something@@general.text.enable:Enable`
  }

  /**
   * Returns translated string for 'Ingredient CSV Templates'
   * @returns {string} The translated string
   */
  getIngredientCSVTemplatesText(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientCsvTemplates:Ingredient CSV Templates`
  }

  /**
   * Returns translated string for 'Import Ingredients'
   * @returns {string} The translated string
   */
  getImportIngredientsText(): string {
    return $localize`:General|Text for input/information@@general.text.importIngredients:Import Ingredients`
  }

  /**
   * Returns translated string for ingredient csv template selection
   * @returns {string} The translated string
   */
  getIngredientCsvTemplateSelectionMessage(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientCsvTemplateSelectionMessage:Please select the CSV Template to be used for the ingredient importation process.`
  }

  /**
   * Returns translated string for ingredient import message
   * @returns {string} The translated string
   */
  getIngredientImportFilesMessage(): string {
    return $localize`:General|Text for input/information@@general.text.ingredientImportMessage:Below you will be required to select the ingredient import file. Please note that there is a limit of 20 ingredients per csv at this time.`
  }

  /**
   * Returns translated string for 'Ingredient Import File'
   * @returns {string} The translated string
   */
  getIngredientImportFileText(): string {
    return $localize`:API Error|Ingredient Import Limit@@apiError.ingredientImportFile:Ingredient Import File`
  }

  /**
   * Returns translated string for 'A duplicate has been identified with one or more names specified in the CSV.'
   * @returns {string} The translated string
   */
  getIngredientNameDuplicateText(): string {
    return $localize`:API Error|Ingredient Name Duplicate@@apiError.ingredientNameDuplicate:A duplicate has been identified with one or more names specified in the CSV.`
  }

  /**
   * Returns translated string for 'A duplicate has been identified with one or more of the aliases specified in the CSV.'
   * @returns {string} The translated string
   */
  getIngredientOtherNameDuplicateText(): string {
    return $localize`:API Error|Ingredient Other Name Duplicate@@apiError.ingredientOtherNameDuplicate:A duplicate has been identified with one or more of the aliases specified in the CSV.`
  }

  /**
   * Returns translated string for 'A duplicate has been identified with one or more of the CAS Numbers in your CSV.'
   * @returns {string} The translated string
   */
  getIngredientCASNumberDuplicateText(): string {
    return $localize`:API Error|Ingredient CAS Number Duplicate@@apiError.ingredientCASNumberDuplicate:A duplicate has been identified with one or more of the CAS Numbers in your CSV.`
  }

  /**
   * Returns translated string for 'Ingredients Imported Successfully.'
   * @returns {string} The translated string
   */
  getImportIngredientsSuccessText(): string {
    return $localize`:General|Text for input/information@@general.text.importIngredientsSuccess:Ingredients Imported Successfully.`
  }

  /**
   * Returns translated string for 'Something went wrong parsing the ingredient import CSV.'
   * @returns {string} The translated string
   */
  getErrorParsingIngredientCSVText(): string {
    return $localize`:API Error|Parsing CSV Error@@error.parsingCsv:Something went wrong parsing the ingredient import CSV.`
  }

  /**
   * Returns translated string for 'CSV is missing a required field.'
   * @returns {string} The translated string
   */
  getErrorMissingFieldText(): string {
    return $localize`:API Error|Missing Field@@error.missingField:CSV is missing a required field.`
  }

  /**
   * Returns translated string for 'CSV contains more than 20 rows.'
   * @returns {string} The translated string
   */
  getErrorCSVLimitExceededText(): string {
    return $localize`:API Error|CSV Limit Exceeded@@error.csvLimitExceeded:CSV contains more than 20 rows.`
  }

  /**
   * Returns translated string for Ingredient Name
   * @returns {string} The translated string
   */
  getIngredientNameText(): string {
    return $localize`:General|Text for ingredient name@@general.text.ingredientName:Ingredient Name`
  }

  /**
   * Returns translated string for Compliance Officer
   * @returns {string} The translated string
   */
  getComplianceOfficerText(): string {
    return $localize`:General|Text for compliance officer@@general.text.complianceOfficer:Compliance Officer`
  }

  /**
   * Returns translated string for Waste Handler
   * @returns {string} The translated string
   */
  getWasteHandlerText(): string {
    return $localize`:General|Text for waste handler@@general.text.wasteHandler:Waste Handler`
  }

  /**
   * Returns translated string for Consumer
   * @returns {string} The translated string
   */
  getConsumerText(): string {
    return $localize`:General|Text for consumer@@general.text.consumer:Consumer`
  }

  /**
   * Returns translated string for Ingredient-Related Fields section header
   * @returns {string} The translated string
   */
  getIngredientRelatedFieldsText(): string {
    return $localize`:General|Text for ingredient-related fields section header@@general.text.ingredientRelatedFields:Ingredient-Related Fields`
  }

  /**
   * Returns translated string for Ingredients File
   * @returns {string} The translated string
   */
  getIngredientsFileText(): string {
    return $localize`:General|Text for ingredients file@@general.text.ingredientsFile:Ingredients File`
  }

  /**
   * Returns translated string for 'Confirm Action'
   * @returns {string} The translated string
   */
  getConfirmActionHeaderText(): string {
    return $localize`:Confirmation|Header for confirmation dialog@@confirmation.header.confirmAction:Confirm Action`
  }

  /**
   * Returns translated string for the delete template confirmation message
   * @param {string} name - The name of the template to be deleted
   * @returns {string} The translated string
   */
  getDeleteTemplateMessageText(name: string): string {
    return $localize`:Confirmation|Message for confirmation dialog@@confirmation.message.deleteTemplate:Are you sure you would like to delete the template named "<b>${name}</b>"?`
  }

  /**
   * Returns translated string for 'Recyclability-Related Fields'
   * @returns {string} The translated string
   */
  getRecyclabilityRelatedFieldsText(): string {
    return $localize`:General|Text for recyclability-related fields section header@@general.text.recyclabilityRelatedFields:Recyclability-Related Fields`
  }

  /**
   * Returns translated string for 'Plastic'
   * @returns {string} The translated string
   */
  getPlasticText(): string {
    return $localize`:General|Text for plastic field@@general.text.plastic:Plastic`
  }

  /**
   * Returns translated string for 'Plastics'
   * @returns {string} The translated string
   */
  getPlasticsText(): string {
    return $localize`:General|Text for plastics@@general.text.plastics:Plastics`
  }

  /**
   * Returns translated string for 'Recycled Content Percentage'
   * @returns {string} The translated string
   */
  getRecycledContentPercentageText(): string {
    return $localize`:General|Text for recycled content percentage field@@general.text.recycledContentPercentage:Recycled Content Percentage`
  }

  /**
   * Returns translated string for 'Country Recyclability'.
   * @returns {string} The translated string
   */
  getCountryRecyclabilityText(): string {
    return $localize`:General|Text for input/information@@general.text.countryRecyclability:Country Recyclability`
  }

  /**
   * Returns translated string for 'Is Plastic'
   * @returns {string} The translated string
   */
  getIsPlasticText(): string {
    return $localize`:General|Text for is plastic field@@general.text.isRecyclable:Is Plastic`
  }

  /**
   * Returns translated string for 'Is Package'
   * @returns {string} The translated string
   */
  getIsPackageText(): string {
    return $localize`:General|Text for is package field@@general.text.isPackage:Is Package`
  }

  /**
   * Returns translated string for 'GHS Classification-Related Fields'
   * @returns {string} The translated string
   */
  getGHSClassificationRelatedFieldsText(): string {
    return $localize`:General|Text for GHS classification-related fields section header@@general.text.ghsClassificationRelatedFields:GHS Classification-Related Fields`
  }

  /**
   * Returns translated string for 'Plastics Declaration'
   * @returns {string} The translated string
   */
  getPlasticsDeclarationText(): string {
    return $localize`:General|Text for input/information@@general.text.plasticsDeclaration:Plastics Declaration`
  }

  /**
   * Returns translated string for 'Resin Type' (NAPCS type selection)
   * @returns {string} The translated string
   */
  getNAPCSTypeText(): string {
    return $localize`:Form label|NAPCS type selection@@form.label.napcsCode:Resin Type`
  }

  /**
   * Returns translated string for 'Source Type'
   * @returns {string} The translated string
   */
  getSourceTypeText(): string {
    return $localize`:Form label|Source type of plastic@@form.label.sourceType:Source Type`
  }

  /**
   * Returns translated string for 'Packaging Form'
   * @returns {string} The translated string
   */
  getPackagingFormText(): string {
    return $localize`:Form label|Packaging form@@form.label.packagingForm:Packaging Form`
  }

  /**
   * Returns translated string for 'Packaging Composition'
   * @returns {string} The translated string
   */
  getPackagingCompositionText(): string {
    return $localize`:Form label|Packaging status@@form.label.packagingStatus:Packaging Composition`
  }

  /**
   * Returns translated string for 'Is this product intended for residential waste stream?'
   * @returns {string} The translated string
   */
  getResidentialWasteStreamText(): string {
    return $localize`:Form label|Residential waste stream radio question@@form.label.residentialWaste:Is this product intended for residential waste stream?`
  }

  /**
   * Returns translated string for 'Select applicable waste streams'
   * @returns {string} The translated string
   */
  getSelectWasteStreamsText(): string {
    return $localize`:Form label|Select waste streams@@form.label.selectWasteStreams:Select applicable waste streams`
  }

  /**
   * Returns translated string for 'Supplier'
   * @returns {string} The translated string
   */
  getSupplierText(): string {
    return $localize`:Form label|Supplier name or ID@@form.label.supplier:Supplier`
  }

  /**
   * Returns translated string for 'Product Plastics Declaration Information'
   * @returns {string} The translated string
   */
  getProductPlasticsDeclarationText(): string {
    return $localize`:General|Text for Product Plastics Declaration Information@@general.text.productPlasticsDeclaration:Product Plastics Declaration`
  }

  /**
   * Returns translated string for 'Core Plastic Details'
   * @returns {string} The translated string
   */
  getCorePlasticDetailsText(): string {
    return $localize`:Product Plastics|Section heading for core plastic details@@product.plastics.coreDetails:Core Plastic Details`
  }

  /**
   * Returns translated string for 'Packaging Info'
   * @returns {string} The translated string
   */
  getPackagingInfoText(): string {
    return $localize`:Product Plastics|Section heading for packaging info@@product.plastics.packagingInfo:Packaging Info`
  }

  /**
   * Returns translated string for 'Waste Stream & Supplier'
   * @returns {string} The translated string
   */
  getWasteStreamAndSupplierInfoText(): string {
    return $localize`:Product Plastics|Section heading for waste stream and supplier info@@product.plastics.wasteSupplier:Waste Stream & Supplier`
  }

  /**
   * Returns translated string for 'Virgin fossil-based'
   * @returns {string} The translated string
   */
  getVirginFossilBasedText(): string {
    return $localize`:Product Plastics|Option for source type virgin@@product.plastics.sourceType.virgin:Virgin fossil-based`
  }

  /**
   * Returns translated string for 'Post-consumer recycled'
   * @returns {string} The translated string
   */
  getPostConsumerRecycledText(): string {
    return $localize`:Product Plastics|Option for source type recycled@@product.plastics.sourceType.recycled:Post-consumer recycled`
  }

  /**
   * Returns translated string for 'Flexible'
   * @returns {string} The translated string
   */
  getFlexibleText(): string {
    return $localize`:Product Plastics|Option for packaging form flexible@@product.plastics.packagingForm.flexible:Flexible`
  }

  /**
   * Returns translated string for 'Rigid'
   * @returns {string} The translated string
   */
  getRigidText(): string {
    return $localize`:Product Plastics|Option for packaging form rigid@@product.plastics.packagingForm.rigid:Rigid`
  }

  /**
   * Returns translated string for 'Filled'
   * @returns {string} The translated string
   */
  getFilledText(): string {
    return $localize`:Product Plastics|Option for packaging status filled@@product.plastics.packagingStatus.filled:Filled`
  }

  /**
   * Returns translated string for 'Unfilled'
   * @returns {string} The translated string
   */
  getUnfilledText(): string {
    return $localize`:Product Plastics|Option for packaging status unfilled@@product.plastics.packagingStatus.unfilled:Unfilled`
  }

  /**
   * Returns translated string for 'Federal Plastics Registry'
   * @returns {string} The translated string
   */
  getFederalPlasticsRegistryText(): string {
    return $localize`:General|Text for Federal Plastics Registry@@general.text.federalPlasticsRegistry:Federal Plastics Registry`
  }

  /**
   * Returns translated string for 'Product Weight (kg)'
   */
  getProductWeightKgText(): string {
    return $localize`:General|Label for Product Weight (kg)@@general.label.productWeightKg:Product Weight (kg)`
  }

  /**
   * Returns translated string for 'Waste Stream'
   */
  getWasteStreamText(): string {
    return $localize`:General|Label for Waste Stream@@general.label.wasteStream:Waste Stream`
  }

  /**
   * Returns translated string for 'NAPCS Details'
   */
  getNapcsDetailsText(): string {
    return $localize`:General|Label for NAPCS Details@@general.label.napcsDetails:NAPCS Details`
  }

  /**
   * Returns translated string for 'Resin Code'
   */
  getResinCodeText(): string {
    return $localize`:General|Label for Resin Code@@general.label.resinCode:Resin Code`
  }

  /**
   * Returns translated string for 'Full Resin Title'
   */
  getFullResinTitleText(): string {
    return $localize`:General|Label for Full Resin Title@@general.label.fullResinTitle:Full Resin Title`
  }

  /**
   * Returns translated string for 'NAPCS Code'
   */
  getNapcsCodeText(): string {
    return $localize`:General|Label for NAPCS Code@@general.label.napcsCode:NAPCS Code`
  }

  /**
   * Returns translated string for 'Residential'
   * @returns {string} The translated string
   */
  getResidentialText(): string {
    return $localize`:General|Text for Residential@@general.text.residential:Residential`
  }

  /**
   * Returns translated string for 'Non-Residential'
   * @returns {string} The translated string
   */
  getNonResidentialText(): string {
    return $localize`:General|Text for Non-Residential@@general.text.nonResidential:Non-Residential`
  }

  /**
   * Returns translated string for 'Resins'
   * @returns {string} The translated string
   */
  getResinsText(): string {
    return $localize`:General|Text for input/information@@general.text.resins:Resins`
  }

  /*
   * Returns translated string for 'Reports'
   * @returns {string} The translated string
   */
  getReportsText(): string {
    return $localize`:General|Label for Reports@@general.text.reports:Reports`
  }

  /**
   * Returns translated string for 'Started'
   * @returns {string} The translated string
   */
  getStartedText(): string {
    return $localize`:Reports|Label for started date@@reports.text.started:Started`
  }

  /**
   * Returns translated string for 'Status'
   * @returns {string} The translated string
   */
  getStatusText(): string {
    return $localize`:Reports|Label for report status@@reports.text.status:Status`
  }

  /**
   * Returns translated string for 'No reports found'
   * @returns {string} The translated string
   */
  getNoReportsText(): string {
    return $localize`:Reports|Message when no reports exist@@reports.text.noReports:No reports found`
  }

  /**
   * Returns translated string for 'Copy and Modify'
   * @returns {string} The translated string
   */
  getCopyAndModifyText(): string {
    return $localize`:Reports|Copy and modify report action@@reports.text.copyAndModify:Copy and Modify`
  }

  /**
   * Returns translated string for 'Download Published Report'
   * @returns {string} The translated string
   */
  getDownloadPublishedText(): string {
    return $localize`:Reports|Download published report action@@reports.text.downloadPublished:Download Published Report`
  }

  /**
   * Returns translated string for 'Download Raw Data'
   * @returns {string} The translated string
   */
  getDownloadRawDataText(): string {
    return $localize`:Reports|Download raw data action@@reports.text.downloadRawData:Download Raw Data`
  }

  /**
   * Returns translated string for 'Add Report'
   * @returns {string} The translated string
   */
  getAddReportText(): string {
    return $localize`:Reports|Add report button label@@reports.text.addReport:Add Report`
  }

  /**
   * Returns translated string for 'Add to Catalogue'
   * @returns {string} The translated string
   */
  getAddToCatalogueText(): string {
    return $localize`:General|Text for Add to Catalogue@@general.text.addToCatalogue:Add to Catalogue`
  }

  /**
   * Returns translated string for confirming a product was added to the catalogue
   * @param {string} productName The name of the product
   * @param {string} productBrand The brand of the product
   * @returns {string} The translated string
   */
  getProductAddedToCatalogueText(
    productName: string,
    productBrand: string,
  ): string {
    return $localize`:Product|Product added to catalogue message@@product.addedToCatalogue:Product ${productName} from ${productBrand} has been successfully added to your catalogue.`
  }

  /**
   * Returns translated string for 'This product is already a copy of another. Please use the original product to create additional copies.'
   * @returns {string} The translated string
   */
  getProductAlreadyCopiedErrorText(): string {
    return $localize`:API Error|Product Already Copied@@apiError.productAlreadyCopied:This product is already a copy of another. Please use the original product to create additional copies.`
  }

  /**
   * Returns translated string for 'This product already exists in your company's catalogue. You cannot add it again'
   * @returns {string} The translated string
   */
  getProductAlreadyOwnedErrorText(): string {
    return $localize`:API Error|Product Already Owned@@apiError.productAlreadyOwned:This product already exists in your company's catalogue. You cannot add it again`
  }

  /**
   * Returns translated string for 'Create FPR Report'
   * @returns {string} The translated string
   */
  getCreateFprReportText(): string {
    return $localize`:FPR Report|Create FPR Report@@fprReport.create:Create FPR Report`
  }

  /**
   * Returns translated string for 'Year'
   * @returns {string} The translated string
   */
  getYearText(): string {
    return $localize`:FPR Report|Year@@fprReport.year:Year`
  }

  /**
   * Returns translated string for 'Save'
   * @returns {string} The translated string
   */
  getSaveText(): string {
    return $localize`:FPR Report|Save@@fprReport.save:Save`
  }

  /**
   * Returns translated string for 'Publish'
   * @returns {string} The translated string
   */
  getPublishText(): string {
    return $localize`:FPR Report|Publish@@fprReport.publish:Publish`
  }

  /**
   * Returns translated string for 'Auto Populated'
   * @returns {string} The translated string
   */
  getAutoPopulatedText(): string {
    return $localize`:FPR Report|Auto Populated@@fprReport.autoPopulated:Auto Populated`
  }

  /**
   * Returns translated string for 'User Entered'
   * @returns {string} The translated string
   */
  getUserEnteredText(): string {
    return $localize`:FPR Report|User Entered@@fprReport.userEntered:User Entered`
  }

  /**
   * Returns translated string for 'Is this plastic packaging?'
   * @returns {string} The translated string
   */
  getIsThisPlasticPackagingText(): string {
    return $localize`:General|Label asking if the packaging is plastic@@general.text.isThisPlasticPackaging:Is this plastic packaging?`
  }

  /**
   * Returns translated string for 'Is Recyclable'
   * @returns {string} The translated string
   */
  getIsRecyclable(): string {
    return $localize`:General|Text for is recyclable field@@general.text.isRecyclable:Is Recyclable`
  }

  /**
   * Returns translated string for 'If a product is a package, it needs to be indicated whether or not it is plastic packaging.'
   * @returns {string} The translated string
   */
  getIsPlasticPackagingMissingText(): string {
    return $localize`:API Error|Is Plastic Packaging missing@@error.isPlasticPackagingMissing:If a product is a package, it needs to be indicated whether or not it is plastic packaging.`
  }

  /**
   * Returns translated string for 'Product Category Information'
   * @returns {string} The translated string
   */
  getProductCategoryInfoText(): string {
    return $localize`:General|Product category section heading@@general.text.productCategoryInfo:Product Category Information`
  }

  /**
   * Returns translated string for 'Category'
   * @returns {string} The translated string
   */
  getCategoryText(): string {
    return $localize`:FPR Report|Category@@fprReport.category:Category`
  }

  /**
   * Returns translated string for 'Sub Category'
   * @returns {string} The translated string
   */
  getSubCategoryText(): string {
    return $localize`:FPR Report|Sub Category@@fprReport.subCategory:Sub Category`
  }

  /**
   * Returns translated string for 'Resin Type'
   * @returns {string} The translated string
   */
  getResinTypeText(): string {
    return $localize`:FPR Report|Resin Type@@fprReport.resinType:Resin Type`
  }

  /**
   * Returns translated string for 'Resin Source'
   * @returns {string} The translated string
   */
  getResinSourceText(): string {
    return $localize`:FPR Report|Resin Source@@fprReport.resinSource:Resin Source`
  }

  /**
   * Returns translated string for 'Method Used'
   * @returns {string} The translated string
   */
  getMethodUsedText(): string {
    return $localize`:FPR Report|Method Used@@fprReport.methodUsed:Method Used`
  }

  /**
   * Returns translated string for 'Quantity Manufactured in Canada'
   */
  getQtyManufacturedInCanadaText(): string {
    return $localize`:FPR Report|Quantity Manufactured in Canada@@fprReport.qtyManufacturedInCanada:Quantity Manufactured in Canada`
  }

  /**
   * Returns translated string for 'Quantity Imported'
   */
  getQtyImportedText(): string {
    return $localize`:FPR Report|Quantity Imported@@fprReport.qtyImported:Quantity Imported`
  }

  /**
   * Returns translated string for 'Quantity Placed on Market'
   */
  getQtyPlacedOnMarketText(): string {
    return $localize`:FPR Report|Quantity Placed on Market@@fprReport.qtyPlacedOnMarket:Quantity Placed on Market`
  }

  /**
   * Returns translated string for 'Quantity for Alberta'
   */
  getQtyAlbertaText(): string {
    return $localize`:FPR Report|Quantity for Alberta@@fprReport.qtyAlberta:Quantity for Alberta`
  }

  /**
   * Returns translated string for 'Quantity for British Columbia'
   */
  getQtyBritishColumbiaText(): string {
    return $localize`:FPR Report|Quantity for British Columbia@@fprReport.qtyBritishColumbia:Quantity for British Columbia`
  }

  /**
   * Returns translated string for 'Quantity for Manitoba'
   */
  getQtyManitobaText(): string {
    return $localize`:FPR Report|Quantity for Manitoba@@fprReport.qtyManitoba:Quantity for Manitoba`
  }

  /**
   * Returns translated string for 'Quantity for New Brunswick'
   */
  getQtyNewBrunswickText(): string {
    return $localize`:FPR Report|Quantity for New Brunswick@@fprReport.qtyNewBrunswick:Quantity for New Brunswick`
  }

  /**
   * Returns translated string for 'Quantity for Newfoundland and Labrador'
   */
  getQtyNewfoundlandAndLabradorText(): string {
    return $localize`:FPR Report|Quantity for Newfoundland and Labrador@@fprReport.qtyNewfoundlandAndLabrador:Quantity for Newfoundland and Labrador`
  }

  /**
   * Returns translated string for 'Quantity for Nova Scotia'
   */
  getQtyNovaScotiaText(): string {
    return $localize`:FPR Report|Quantity for Nova Scotia@@fprReport.qtyNovaScotia:Quantity for Nova Scotia`
  }

  /**
   * Returns translated string for 'Quantity for Ontario'
   */
  getQtyOntarioText(): string {
    return $localize`:FPR Report|Quantity for Ontario@@fprReport.qtyOntario:Quantity for Ontario`
  }

  /**
   * Returns translated string for 'Quantity for Prince Edward Island'
   */
  getQtyPrinceEdwardIslandText(): string {
    return $localize`:FPR Report|Quantity for Prince Edward Island@@fprReport.qtyPrinceEdwardIsland:Quantity for Prince Edward Island`
  }

  /**
   * Returns translated string for 'Quantity for Quebec'
   */
  getQtyQuebecText(): string {
    return $localize`:FPR Report|Quantity for Quebec@@fprReport.qtyQuebec:Quantity for Quebec`
  }

  /**
   * Returns translated string for 'Quantity for Saskatchewan'
   */
  getQtySaskatchewanText(): string {
    return $localize`:FPR Report|Quantity for Saskatchewan@@fprReport.qtySaskatchewan:Quantity for Saskatchewan`
  }

  /**
   * Returns translated string for 'Quantity for Northwest Territories'
   */
  getQtyNorthwestTerritoriesText(): string {
    return $localize`:FPR Report|Quantity for Northwest Territories@@fprReport.qtyNorthwestTerritories:Quantity for Northwest Territories`
  }

  /**
   * Returns translated string for 'Quantity for Nunavut'
   */
  getQtyNunavutText(): string {
    return $localize`:FPR Report|Quantity for Nunavut@@fprReport.qtyNunavut:Quantity for Nunavut`
  }

  /**
   * Returns translated string for 'Quantity for Yukon'
   */
  getQtyYukonText(): string {
    return $localize`:FPR Report|Quantity for Yukon@@fprReport.qtyYukon:Quantity for Yukon`
  }

  /**
   * Returns translated string for 'No rows found'
   * @returns {string} The translated string
   */
  getNoRowsText(): string {
    return $localize`:FPR Report|No Rows@@fprReport.noRows:No rows found`
  }

  /**
   * Returns translated string for 'Generate Report'
   * @returns {string} The translated string
   */
  getGenerateReportText(): string {
    return $localize`:Reports|Generate Report@@reports.generateReport:Generate Report`
  }
  /**
   * Returns translated string for 'Subcategory'
   * @returns {string} The translated string
   */
  getSubcategoryText(): string {
    return $localize`:General|Label for product subcategory@@general.text.subcategory:Subcategory`
  }

  /**
   * Returns translated string for 'Calculation and Reporting Information'
   * @returns {string} The translated string
   */
  getCalculationAndReportingInfoText(): string {
    return $localize`:General|Section heading for calculation and reporting info@@general.text.calculationAndReportingInfo:Calculation and Reporting Information`
  }

  /**
   * Returns translated string for 'Calculation Method'
   * @returns {string} The translated string
   */
  getCalculationMethodText(): string {
    return $localize`:General|Label for plastic calculation method@@general.text.calculationMethod:Calculation Method`
  }

  /**
   * Returns translated string for 'Other Calculation Method'
   * @returns {string} The translated string
   */
  getCalculationMethodOtherText(): string {
    return $localize`:General|Label for specifying the other calculation method@@general.text.calculationMethodOther:Other Calculation Method`
  }

  /**
   * Returns translated string for 'Reporting Date'
   * @returns {string} The translated string
   */
  getReportingDateText(): string {
    return $localize`:General|Label for the reporting date@@general.text.reportingDate:Reporting Date`
  }

  /**
   * Returns translated string for tooltip describing the reporting date field
   * @returns {string} The translated string
   */
  getReportingDateTooltipText(): string {
    return $localize`:General|Tooltip for reporting date field@@general.text.reportingDateTooltip:Date the product was placed on the market or imported`
  }

  /**
   * Returns translated string for 'Specified Calculation Method'
   * @returns {string} The translated string
   */
  getSpecifiedCalculationMethodText(): string {
    return $localize`:General|Label for specified other calculation method@@general.text.specifiedCalculationMethod:Specified Calculation Method`
  }

  /**
   * Returns translated string for 'Created At'
   * @returns {string} The translated string
   */
  getCreatedAtText(): string {
    return $localize`:FPR Report|Created At@@fprReport.createdAt:Created At`
  }

  /**
   * Returns translated string for 'Last Edited At'
   * @returns {string} The translated string
   */
  getLastEditedAtText(): string {
    return $localize`:FPR Report|Last Edited At@@fprReport.lastEditedAt:Last Edited At`
  }

  /**
   * Returns translated string for 'Published At'
   * @returns {string} The translated string
   */
  getPublishedAtText(): string {
    return $localize`:FPR Report|Published At@@fprReport.publishedAt:Published At`
  }

  /**
   * Returns translated string for 'Auto-updates paused due to inactivity'
   * @returns {string} The translated string
   */
  getAutoUpdatesPausedText(): string {
    return $localize`:FPR Report|Auto-updates paused due to inactivity@@fprReport.autoUpdatesPaused:Auto-updates paused due to inactivity`
  }

  /**
   * Returns translated string for 'Auto-updates resumed'
   * @returns {string} The translated string
   */
  getAutoUpdatesResumedText(): string {
    return $localize`:FPR Report|Auto-updates resumed@@fprReport.autoUpdatesResumed:Auto-updates resumed`
  }
  /*
   * Returns translated string for 'You already own this product and cannot create a copy of it.'
   * @returns {string} The translated string
   */
  getCannotCopyOwnProductErrorText(): string {
    return $localize`:API Error|Cannot Copy Own Product@@apiError.cannotCopyOwnProduct:You already own this product and cannot create a copy of it.`
  }

  /**
   * Returns translated string for 'Specific Component Identification'
   * @returns {string} The translated string
   */
  getCalculationMethodSpecificText(): string {
    return $localize`:General|Label for specific component method@@calc.specific:Specific Component Identification`
  }

  /**
   * Returns translated string for 'Average Bill of Materials (ABOM)'
   * @returns {string} The translated string
   */
  getCalculationMethodAbomText(): string {
    return $localize`:General|Label for ABOM method@@calc.abom:Average Bill of Materials (ABOM)`
  }

  /**
   * Returns translated string for 'Fixed Factor Calculation Method'
   * @returns {string} The translated string
   */
  getCalculationMethodFixedFactorText(): string {
    return $localize`:General|Label for fixed factor method@@calc.fixedFactor:Fixed Factor Calculation Method`
  }

  /**
   * Returns translated string for 'Post-Consumer Recycled'
   * @returns {string} The translated string
   */
  getSourceTypePostConsumerRecycledText(): string {
    return $localize`:General|Source type option@@source.pcr:Post-Consumer Recycled`
  }

  /**
   * Returns translated string for 'Post-Industrial Recycled'
   * @returns {string} The translated string
   */
  getSourceTypePostIndustrialRecycledText(): string {
    return $localize`:General|Source type option@@source.pir:Post-Industrial Recycled`
  }

  /**
   * Returns translated string for 'Virgin Fossil-Based'
   * @returns {string} The translated string
   */
  getSourceTypeVirginFossilText(): string {
    return $localize`:General|Source type option@@source.virginFossil:Virgin Fossil-Based`
  }

  /**
   * Returns translated string for 'Virgin Bio-Based'
   * @returns {string} The translated string
   */
  getSourceTypeVirginBioText(): string {
    return $localize`:General|Source type option@@source.virginBio:Virgin Bio-Based`
  }

  /**
   * Returns translated string for 'Flexible'
   * @returns {string} The translated string
   */
  getPackagingFormFlexibleText(): string {
    return $localize`:General|Packaging form option@@pack.form.flexible:Flexible`
  }

  /**
   * Returns translated string for 'Rigid'
   * @returns {string} The translated string
   */
  getPackagingFormRigidText(): string {
    return $localize`:General|Packaging form option@@pack.form.rigid:Rigid`
  }

  /**
   * Returns translated string for 'Filled'
   * @returns {string} The translated string
   */
  getPackagingStatusFilledText(): string {
    return $localize`:General|Packaging composition/status option@@pack.status.filled:Filled`
  }

  /**
   * Returns translated string for 'Unfilled'
   * @returns {string} The translated string
   */
  getPackagingStatusUnfilledText(): string {
    return $localize`:General|Packaging composition/status option@@pack.status.unfilled:Unfilled`
  }

  /**
   * Returns translated string for 'Packaging'
   * @returns {string} The translated string
   */
  getCategoryPackagingText(): string {
    return $localize`:General|Top-level category for packaging@@categories.packaging:Packaging`
  }

  /**
   * Returns translated string for 'Electronics and Electrical Equipment'
   * @returns {string} The translated string
   */
  getCategoryElectronicsText(): string {
    return $localize`:General|Top-level category for electronics and electrical@@categories.electronics:Electronics and Electrical Equipment`
  }

  /**
   * Returns translated string for 'Construction'
   * @returns {string} The translated string
   */
  getCategoryConstructionText(): string {
    return $localize`:General|Top-level category for construction@@categories.construction:Construction`
  }

  /**
   * Returns translated string for 'Automotive'
   * @returns {string} The translated string
   */
  getCategoryAutomotiveText(): string {
    return $localize`:General|Top-level category for automotive@@categories.automotive:Automotive`
  }

  /**
   * Returns translated string for 'White Goods'
   * @returns {string} The translated string
   */
  getCategoryWhiteGoodsText(): string {
    return $localize`:General|Top-level category for white goods@@categories.whiteGoods:White Goods`
  }

  /**
   * Returns translated string for 'Agriculture'
   * @returns {string} The translated string
   */
  getCategoryAgricultureText(): string {
    return $localize`:General|Top-level category for agriculture@@categories.agriculture:Agriculture`
  }

  /**
   * Returns translated string for 'Textiles'
   * @returns {string} The translated string
   */
  getCategoryTextilesText(): string {
    return $localize`:General|Top-level category for textiles@@categories.textiles:Textiles`
  }

  /**
   * Returns translated string for 'Rigid - Beverage Container'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingRigidBeverageText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.rigidBeverage:Rigid - Beverage Container`
  }

  /**
   * Returns translated string for 'Rigid - Non-Beverage'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingRigidNonBeverageText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.rigidNonBeverage:Rigid - Non-Beverage`
  }

  /**
   * Returns translated string for 'Rigid - Single-Use Plastics'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingRigidSingleUseText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.rigidSingleUse:Rigid - Single-Use Plastics`
  }

  /**
   * Returns translated string for 'Flexible - Beverage Container'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingFlexibleBeverageText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.flexibleBeverage:Flexible - Beverage Container`
  }

  /**
   * Returns translated string for 'Flexible - Non-Beverage'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingFlexibleNonBeverageText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.flexibleNonBeverage:Flexible - Non-Beverage`
  }

  /**
   * Returns translated string for 'Flexible - Single-Use Plastics'
   * @returns {string} The translated string
   */
  getSubcategoryPackagingFlexibleSingleUseText(): string {
    return $localize`:General|Subcategory under Packaging@@subcat.packaging.flexibleSingleUse:Flexible - Single-Use Plastics`
  }

  /**
   * Returns translated string for 'Information Technology Equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsInfoTechText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.infoTech:Information Technology Equipment`
  }

  /**
   * Returns translated string for 'Telecommunications Equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsTelecomText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.telecom:Telecommunications Equipment`
  }

  /**
   * Returns translated string for 'Audio-Visual Equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsAudioVisualText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.audioVisual:Audio-Visual Equipment`
  }

  /**
   * Returns translated string for 'Tools'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsToolsText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.tools:Tools`
  }

  /**
   * Returns translated string for 'Lighting Equipment, Parts, Bulbs and Signs'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsLightingText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.lighting:Lighting Equipment, Parts, Bulbs and Signs`
  }

  /**
   * Returns translated string for 'Sports Equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsSportsText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.sports:Sports Equipment`
  }

  /**
   * Returns translated string for 'Toys'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsToysText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.toys:Toys`
  }

  /**
   * Returns translated string for 'Art, Hobby and Craft Equipment and Decorations'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsArtHobbyText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.artHobby:Art, Hobby and Craft Equipment and Decorations`
  }

  /**
   * Returns translated string for 'Automatic Dispensers'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsDispensersText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.dispensers:Automatic Dispensers`
  }

  /**
   * Returns translated string for 'Measuring, Monitoring, Navigation and Control Instruments'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsMeasuringText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.measuring:Measuring, Monitoring, Navigation and Control Instruments`
  }

  /**
   * Returns translated string for 'Medical Devices, Excluding Implanted or Infected Devices'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsMedicalDevicesText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.medicalDevices:Medical Devices, Excluding Implanted or Infected Devices`
  }

  /**
   * Returns translated string for 'Accessories, Including Cables, Adaptors, Cords and Chargers'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicsAccessoriesText(): string {
    return $localize`:General|Subcategory under Electronics@@subcat.electronics.accessories:Accessories, Including Cables, Adaptors, Cords and Chargers`
  }

  /**
   * Returns translated string for 'Windows and Doors'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionWindowsDoorsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.windowsDoors:Windows and Doors`
  }

  /**
   * Returns translated string for 'Flooring'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionFlooringText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.flooring:Flooring`
  }

  /**
   * Returns translated string for 'Film Products'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionFilmProductsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.film:Film Products`
  }

  /**
   * Returns translated string for 'Insulation'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionInsulationText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.insulation:Insulation`
  }

  /**
   * Returns translated string for 'Paints and Coatings'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionPaintsAndCoatingsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.paints:Paints and Coatings`
  }

  /**
   * Returns translated string for 'Piping'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionPipingText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.piping:Piping`
  }

  /**
   * Returns translated string for 'Roofing Materials'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionRoofingMaterialsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.roofing:Roofing Materials`
  }

  /**
   * Returns translated string for 'Siding'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionSidingText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.siding:Siding`
  }

  /**
   * Returns translated string for 'Motorcycles'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveMotorcyclesText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.motorcycles:Motorcycles`
  }

  /**
   * Returns translated string for 'Multi-Purpose Passenger Vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveMultiPurposePassengerVehiclesText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.mppv:Multi-Purpose Passenger Vehicles`
  }

  /**
   * Returns translated string for 'Passenger Vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotivePassengerVehiclesText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.passenger:Passenger Vehicles`
  }

  /**
   * Returns translated string for 'Snowmobiles'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveSnowmobilesText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.snowmobiles:Snowmobiles`
  }

  /**
   * Returns translated string for 'Trailers'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveTrailersText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.trailers:Trailers`
  }

  /**
   * Returns translated string for 'Trucks'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveTrucksText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.trucks:Trucks`
  }

  /**
   * Returns translated string for 'Buses'
   * @returns {string} The translated string
   */
  getSubcategoryAutomotiveBusesText(): string {
    return $localize`:General|Subcategory under Automotive@@subcat.auto.buses:Buses`
  }

  /**
   * Returns translated string for 'Refrigeration or Freezing'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsRefrigerationOrFreezingText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.refrigeration:Refrigeration or Freezing`
  }

  /**
   * Returns translated string for 'Temperature or Humidity Control'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsTemperatureOrHumidityControlText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.tempHumidity:Temperature or Humidity Control`
  }

  /**
   * Returns translated string for 'Air or Water Purification'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsAirOrWaterPurificationText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.purification:Air or Water Purification`
  }

  /**
   * Returns translated string for 'Cooking or Preparing Food'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsCookingOrPreparingFoodText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.cooking:Cooking or Preparing Food`
  }

  /**
   * Returns translated string for 'Washing or Drying Dishes or Textiles'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsWashingOrDryingDishesOrTextilesText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.washing:Washing or Drying Dishes or Textiles`
  }

  /**
   * Returns translated string for 'Use of Suction, Pressure or Water to Clean Surfaces or Materials'
   * @returns {string} The translated string
   */
  getSubcategoryWhiteGoodsUseOfSuctionPressureOrWaterToCleanSurfacesOrMaterialsText(): string {
    return $localize`:General|Subcategory under White Goods@@subcat.white.cleaning:Use of Suction, Pressure or Water to Clean Surfaces or Materials`
  }

  /**
   * Returns translated string for 'Containers'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureContainersText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.containers:Containers`
  }

  /**
   * Returns translated string for 'Totes and Drums'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureTotesAndDrumsText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.totes:Totes and Drums`
  }

  /**
   * Returns translated string for 'Bags and Large Tote Bags'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureBagsAndLargeToteBagsText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.bags:Bags and Large Tote Bags`
  }

  /**
   * Returns translated string for 'Twine'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureTwineText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.twine:Twine`
  }

  /**
   * Returns translated string for 'Bale Wrap'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureBaleWrapText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.baleWrap:Bale Wrap`
  }

  /**
   * Returns translated string for 'Grain Bags'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureGrainBagsText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.grainBags:Grain Bags`
  }

  /**
   * Returns translated string for 'Silage Film'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureSilageFilmText(): string {
    return $localize`:General|Subcategory under Agriculture@@subcat.agri.silage:Silage Film`
  }

  /**
   * Returns translated string for 'Clothing'
   * @returns {string} The translated string
   */
  getSubcategoryTextilesClothingText(): string {
    return $localize`:General|Subcategory under Textiles@@subcat.textiles.clothing:Clothing`
  }

  /**
   * Returns translated string for 'Interior Textiles'
   * @returns {string} The translated string
   */
  getSubcategoryTextilesInteriorTextilesText(): string {
    return $localize`:General|Subcategory under Textiles@@subcat.textiles.interior:Interior Textiles`
  }

  /**
   * Returns translated string for 'Footwear'
   * @returns {string} The translated string
   */
  getSubcategoryTextilesFootwearText(): string {
    return $localize`:General|Subcategory under Textiles@@subcat.textiles.footwear:Footwear`
  }

  /**
   * Returns translated string for 'Electronic and Electrical Equipment (EEE)'
   * @returns {string} The translated string
   */
  getCategoryElectronicAndElectricalEquipmentEeeText(): string {
    return $localize`:General|Category@@cat.electronic-and-electrical-equipment-eee:Electronic and Electrical Equipment (EEE)`
  }

  /**
   * Returns translated string for 'Tires'
   * @returns {string} The translated string
   */
  getCategoryTiresText(): string {
    return $localize`:General|Category@@cat.tires:Tires`
  }

  /**
   * Returns translated string for 'Transportation'
   * @returns {string} The translated string
   */
  getCategoryTransportationText(): string {
    return $localize`:General|Category@@cat.transportation:Transportation`
  }

  /**
   * Returns translated string for 'Agriculture and horticulture'
   * @returns {string} The translated string
   */
  getCategoryAgricultureAndHorticultureText(): string {
    return $localize`:General|Category@@cat.agriculture-and-horticulture:Agriculture and horticulture`
  }

  /**
   * Returns translated string for 'Fishing and aquaculture'
   * @returns {string} The translated string
   */
  getCategoryFishingAndAquacultureText(): string {
    return $localize`:General|Category@@cat.fishing-and-aquaculture:Fishing and aquaculture`
  }

  /**
   * Returns translated string for 'Apparel and textiles'
   * @returns {string} The translated string
   */
  getCategoryApparelAndTextilesText(): string {
    return $localize`:General|Category@@cat.apparel-and-textiles:Apparel and textiles`
  }

  /**
   * Returns translated string for 'Single-use or disposable products'
   * @returns {string} The translated string
   */
  getCategorySingleUseOrDisposableProductsText(): string {
    return $localize`:General|Category@@cat.single-use-or-disposable-products:Single-use or disposable products`
  }

  /**
   * Returns translated string for 'Electronic or electrical information technology or telecommunication devices or equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalInformationTechnologyOrTelecommunicationDevicesOrEquipmentText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-information-technology-or-telecommunication-devices-or-equipment:Electronic or electrical information technology or telecommunication devices or equipment`
  }

  /**
   * Returns translated string for 'Electronic or electrical audiovisual and consumer equipment or media'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalAudiovisualAndConsumerEquipmentOrMediaText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-audiovisual-and-consumer-equipment-or-media:Electronic or electrical audiovisual and consumer equipment or media`
  }

  /**
   * Returns translated string for 'Electronic or electrical appliances'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalAppliancesText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-appliances:Electronic or electrical appliances`
  }

  /**
   * Returns translated string for 'Electronic or electrical tools, other than large-scale stationary industrial tools'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalToolsOtherThanLargeScaleStationaryIndustrialToolsText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-tools-other-than-large-scale-stationary-industrial-tools:Electronic or electrical tools, other than large-scale stationary industrial tools`
  }

  /**
   * Returns translated string for 'Electronic or electrical lighting equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalLightingEquipmentText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-lighting-equipment:Electronic or electrical lighting equipment`
  }

  /**
   * Returns translated string for 'Electronic or electrical toys'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalToysText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-toys:Electronic or electrical toys`
  }

  /**
   * Returns translated string for 'Electronic or electrical sports equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalSportsEquipmentText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-sports-equipment:Electronic or electrical sports equipment`
  }

  /**
   * Returns translated string for 'Electronic or electrical devices for arts, hobbies or crafts'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalDevicesForArtsHobbiesOrCraftsText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-devices-for-arts-hobbies-or-crafts:Electronic or electrical devices for arts, hobbies or crafts`
  }

  /**
   * Returns translated string for 'Electronic or electrical monitoring and control instruments'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalMonitoringAndControlInstrumentsText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-monitoring-and-control-instruments:Electronic or electrical monitoring and control instruments`
  }

  /**
   * Returns translated string for 'Electronic or electrical dispensers'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalDispensersText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-dispensers:Electronic or electrical dispensers`
  }

  /**
   * Returns translated string for 'Electronic or electrical medical devices or equipment'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeElectronicOrElectricalMedicalDevicesOrEquipmentText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.electronic-or-electrical-medical-devices-or-equipment:Electronic or electrical medical devices or equipment`
  }

  /**
   * Returns translated string for 'Accessories for use with any products referred to in Category 1'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeAccessoriesForUseWithAnyProductsReferredToInCategory1Text(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.accessories-for-use-with-any-products-referred-to-in-category-1:Accessories for use with any products referred to in Category 1`
  }

  /**
   * Returns translated string for 'Photovoltaic panels'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeePhotovoltaicPanelsText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.photovoltaic-panels:Photovoltaic panels`
  }

  /**
   * Returns translated string for 'Chargers for battery-electric vehicles and plug-in hybrid electric vehicles, whether free-standing or wall-mounted'
   * @returns {string} The translated string
   */
  getSubcategoryElectronicAndElectricalEquipmentEeeChargersForBatteryElectricVehiclesAndPlugInHybridElectricVehiclesWhetherFreeStandingOrWallMountedText(): string {
    return $localize`:General|Subcategory under Electronic and Electrical Equipment (EEE)@@subcat.electronic-and-electrical-equipment-eee.chargers-for-battery-electric-vehicles-and-plug-in-hybrid-electric-vehicles-whether-free-standing-or-wall-mounted:Chargers for battery-electric vehicles and plug-in hybrid electric vehicles, whether free-standing or wall-mounted`
  }

  /**
   * Returns translated string for 'Tires for light-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForLightDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-light-duty-vehicles:Tires for light-duty vehicles`
  }

  /**
   * Returns translated string for 'Tires for medium-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForMediumDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-medium-duty-vehicles:Tires for medium-duty vehicles`
  }

  /**
   * Returns translated string for 'Tires for heavy-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForHeavyDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-heavy-duty-vehicles:Tires for heavy-duty vehicles`
  }

  /**
   * Returns translated string for 'Tires for motorcycles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForMotorcyclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-motorcycles:Tires for motorcycles`
  }

  /**
   * Returns translated string for 'Tires for trailers'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForTrailersText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-trailers:Tires for trailers`
  }

  /**
   * Returns translated string for 'Tires for snowmobiles and restricted-use vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForSnowmobilesAndRestrictedUseVehiclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-snowmobiles-and-restricted-use-vehicles:Tires for snowmobiles and restricted-use vehicles`
  }

  /**
   * Returns translated string for 'Tires for mobility aids'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForMobilityAidsText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-mobility-aids:Tires for mobility aids`
  }

  /**
   * Returns translated string for 'Tires for bicycles, bicycle trailers and motor-assisted bicycles'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForBicyclesBicycleTrailersAndMotorAssistedBicyclesText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-bicycles-bicycle-trailers-and-motor-assisted-bicycles:Tires for bicycles, bicycle trailers and motor-assisted bicycles`
  }

  /**
   * Returns translated string for 'Tires for muscular-powered equipment'
   * @returns {string} The translated string
   */
  getSubcategoryTiresTiresForMuscularPoweredEquipmentText(): string {
    return $localize`:General|Subcategory under Tires@@subcat.tires.tires-for-muscular-powered-equipment:Tires for muscular-powered equipment`
  }

  /**
   * Returns translated string for 'Light-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationLightDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.light-duty-vehicles:Light-duty vehicles`
  }

  /**
   * Returns translated string for 'Medium-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationMediumDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.medium-duty-vehicles:Medium-duty vehicles`
  }

  /**
   * Returns translated string for 'Heavy-duty vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationHeavyDutyVehiclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.heavy-duty-vehicles:Heavy-duty vehicles`
  }

  /**
   * Returns translated string for 'Motorcycles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationMotorcyclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.motorcycles:Motorcycles`
  }

  /**
   * Returns translated string for 'Trailers'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationTrailersText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.trailers:Trailers`
  }

  /**
   * Returns translated string for 'Snowmobiles and restricted-use vehicles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationSnowmobilesAndRestrictedUseVehiclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.snowmobiles-and-restricted-use-vehicles:Snowmobiles and restricted-use vehicles`
  }

  /**
   * Returns translated string for 'Mobility aids'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationMobilityAidsText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.mobility-aids:Mobility aids`
  }

  /**
   * Returns translated string for 'Bicycles, bicycle trailers and motor-assisted bicycles'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationBicyclesBicycleTrailersAndMotorAssistedBicyclesText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.bicycles-bicycle-trailers-and-motor-assisted-bicycles:Bicycles, bicycle trailers and motor-assisted bicycles`
  }

  /**
   * Returns translated string for 'Muscular-powered equipment'
   * @returns {string} The translated string
   */
  getSubcategoryTransportationMuscularPoweredEquipmentText(): string {
    return $localize`:General|Subcategory under Transportation@@subcat.transportation.muscular-powered-equipment:Muscular-powered equipment`
  }

  /**
   * Returns translated string for 'Windows and doors'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionWindowsAndDoorsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.windows-and-doors:Windows and doors`
  }

  /**
   * Returns translated string for 'Interior finishes (including flooring)'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionInteriorFinishesIncludingFlooringText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.interior-finishes-including-flooring:Interior finishes (including flooring)`
  }

  /**
   * Returns translated string for 'Construction film and sheeting'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionConstructionFilmAndSheetingText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.construction-film-and-sheeting:Construction film and sheeting`
  }

  /**
   * Returns translated string for 'Insulation materials'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionInsulationMaterialsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.insulation-materials:Insulation materials`
  }

  /**
   * Returns translated string for 'Piping, including pipe fittings'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionPipingIncludingPipeFittingsText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.piping-including-pipe-fittings:Piping, including pipe fittings`
  }

  /**
   * Returns translated string for 'Siding and cladding'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionSidingAndCladdingText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.siding-and-cladding:Siding and cladding`
  }

  /**
   * Returns translated string for 'Decking and fencing'
   * @returns {string} The translated string
   */
  getSubcategoryConstructionDeckingAndFencingText(): string {
    return $localize`:General|Subcategory under Construction@@subcat.construction.decking-and-fencing:Decking and fencing`
  }

  /**
   * Returns translated string for 'Agricultural containers'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAgriculturalContainersText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.agricultural-containers:Agricultural containers`
  }

  /**
   * Returns translated string for 'Animal health product containers'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAnimalHealthProductContainersText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.animal-health-product-containers:Animal health product containers`
  }

  /**
   * Returns translated string for 'Agricultural totes and drums'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAgriculturalTotesAndDrumsText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.agricultural-totes-and-drums:Agricultural totes and drums`
  }

  /**
   * Returns translated string for 'Agricultural bags and large tote bags'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAgriculturalBagsAndLargeToteBagsText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.agricultural-bags-and-large-tote-bags:Agricultural bags and large tote bags`
  }

  /**
   * Returns translated string for 'Clips, supports, hooks'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureClipsSupportsHooksText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.clips-supports-hooks:Clips, supports, hooks`
  }

  /**
   * Returns translated string for 'Bale wrap, bags or tubes'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureBaleWrapBagsOrTubesText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.bale-wrap-bags-or-tubes:Bale wrap, bags or tubes`
  }

  /**
   * Returns translated string for 'Grain bags'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureGrainBagsText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.grain-bags:Grain bags`
  }

  /**
   * Returns translated string for 'Agricultural and horticultural film and sheeting (e.g. for silage, mulch, flooring, plant protection)'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAgriculturalAndHorticulturalFilmAndSheetingEGForSilageMulchFlooringPlantProtectionText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.agricultural-and-horticultural-film-and-sheeting-e-g-for-silage-mulch-flooring-plant-protection:Agricultural and horticultural film and sheeting (e.g. for silage, mulch, flooring, plant protection)`
  }

  /**
   * Returns translated string for 'Net wrap'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureNetWrapText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.net-wrap:Net wrap`
  }

  /**
   * Returns translated string for 'Structural plastic for greenhouses'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureStructuralPlasticForGreenhousesText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.structural-plastic-for-greenhouses:Structural plastic for greenhouses`
  }

  /**
   * Returns translated string for 'CO2 tubing for greenhouses'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureCo2TubingForGreenhousesText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.co2-tubing-for-greenhouses:CO2 tubing for greenhouses`
  }

  /**
   * Returns translated string for 'Propagation trays and growing pots'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticulturePropagationTraysAndGrowingPotsText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.propagation-trays-and-growing-pots:Propagation trays and growing pots`
  }

  /**
   * Returns translated string for 'Irrigation equipment'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureIrrigationEquipmentText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.irrigation-equipment:Irrigation equipment`
  }

  /**
   * Returns translated string for 'Maple syrup tubing and taps'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureMapleSyrupTubingAndTapsText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.maple-syrup-tubing-and-taps:Maple syrup tubing and taps`
  }

  /**
   * Returns translated string for 'Agricultural and horticultural twine'
   * @returns {string} The translated string
   */
  getSubcategoryAgricultureAndHorticultureAgriculturalAndHorticulturalTwineText(): string {
    return $localize`:General|Subcategory under Agriculture and horticulture@@subcat.agriculture-and-horticulture.agricultural-and-horticultural-twine:Agricultural and horticultural twine`
  }

  /**
   * Returns translated string for 'Longlines'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureLonglinesText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.longlines:Longlines`
  }

  /**
   * Returns translated string for 'Nets (e.g. seines, gillnet mesh and associated float and sink lines)'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureNetsEGSeinesGillnetMeshAndAssociatedFloatAndSinkLinesText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.nets-e-g-seines-gillnet-mesh-and-associated-float-and-sink-lines:Nets (e.g. seines, gillnet mesh and associated float and sink lines)`
  }

  /**
   * Returns translated string for 'Traps and pots'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureTrapsAndPotsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.traps-and-pots:Traps and pots`
  }

  /**
   * Returns translated string for 'Trawl nets'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureTrawlNetsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.trawl-nets:Trawl nets`
  }

  /**
   * Returns translated string for 'Trolls'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureTrollsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.trolls:Trolls`
  }

  /**
   * Returns translated string for 'Buoy lines'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureBuoyLinesText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.buoy-lines:Buoy lines`
  }

  /**
   * Returns translated string for 'Anchors'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureAnchorsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.anchors:Anchors`
  }

  /**
   * Returns translated string for 'Fishing gear markers'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureFishingGearMarkersText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.fishing-gear-markers:Fishing gear markers`
  }

  /**
   * Returns translated string for 'Buoys'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureBuoysText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.buoys:Buoys`
  }

  /**
   * Returns translated string for 'Weirs'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureWeirsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.weirs:Weirs`
  }

  /**
   * Returns translated string for 'Groundlines (e.g. ropes and bridles connecting traps/pots in a trawl)'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureGroundlinesEGRopesAndBridlesConnectingTrapsPotsInATrawlText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.groundlines-e-g-ropes-and-bridles-connecting-traps-pots-in-a-trawl:Groundlines (e.g. ropes and bridles connecting traps/pots in a trawl)`
  }

  /**
   * Returns translated string for 'Bait bags'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureBaitBagsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.bait-bags:Bait bags`
  }

  /**
   * Returns translated string for 'Weak links/whale release systems in buoy lines'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureWeakLinksWhaleReleaseSystemsInBuoyLinesText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.weak-links-whale-release-systems-in-buoy-lines:Weak links/whale release systems in buoy lines`
  }

  /**
   * Returns translated string for 'Smart Buoys, smart release buoy systems, other emerging technology for fishing gear recovery systems'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureSmartBuoysSmartReleaseBuoySystemsOtherEmergingTechnologyForFishingGearRecoverySystemsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.smart-buoys-smart-release-buoy-systems-other-emerging-technology-for-fishing-gear-recovery-systems:Smart Buoys, smart release buoy systems, other emerging technology for fishing gear recovery systems`
  }

  /**
   * Returns translated string for 'On-Demand/Ropeless Fishing Systems (floats, inflatable bags, acoustic releases, rope containment systems)'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureOnDemandRopelessFishingSystemsFloatsInflatableBagsAcousticReleasesRopeContainmentSystemsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.on-demand-ropeless-fishing-systems-floats-inflatable-bags-acoustic-releases-rope-containment-systems:On-Demand/Ropeless Fishing Systems (floats, inflatable bags, acoustic releases, rope containment systems)`
  }

  /**
   * Returns translated string for 'Buckets, bins and barrels'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureBucketsBinsAndBarrelsText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.buckets-bins-and-barrels:Buckets, bins and barrels`
  }

  /**
   * Returns translated string for 'Flotation for raft structures for aquaculture'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureFlotationForRaftStructuresForAquacultureText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.flotation-for-raft-structures-for-aquaculture:Flotation for raft structures for aquaculture`
  }

  /**
   * Returns translated string for 'Ropes, bags and netting for aquaculture'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureRopesBagsAndNettingForAquacultureText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.ropes-bags-and-netting-for-aquaculture:Ropes, bags and netting for aquaculture`
  }

  /**
   * Returns translated string for 'Trays for aquaculture'
   * @returns {string} The translated string
   */
  getSubcategoryFishingAndAquacultureTraysForAquacultureText(): string {
    return $localize`:General|Subcategory under Fishing and aquaculture@@subcat.fishing-and-aquaculture.trays-for-aquaculture:Trays for aquaculture`
  }

  /**
   * Returns translated string for 'Apparel'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesApparelText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.apparel:Apparel`
  }

  /**
   * Returns translated string for 'Interior textiles'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesInteriorTextilesText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.interior-textiles:Interior textiles`
  }

  /**
   * Returns translated string for 'Industrial textiles'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesIndustrialTextilesText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.industrial-textiles:Industrial textiles`
  }

  /**
   * Returns translated string for 'Carpet and other floor coverings'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesCarpetAndOtherFloorCoveringsText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.carpet-and-other-floor-coverings:Carpet and other floor coverings`
  }

  /**
   * Returns translated string for 'Footwear'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesFootwearText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.footwear:Footwear`
  }

  /**
   * Returns translated string for 'Yarn, thread, twine, cordage, ropes, cables, woven and knitted or crocheted fabric'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesYarnThreadTwineCordageRopesCablesWovenAndKnittedOrCrochetedFabricText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.yarn-thread-twine-cordage-ropes-cables-woven-and-knitted-or-crocheted-fabric:Yarn, thread, twine, cordage, ropes, cables, woven and knitted or crocheted fabric`
  }

  /**
   * Returns translated string for 'Luggage'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesLuggageText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.luggage:Luggage`
  }

  /**
   * Returns translated string for 'Reusable fabric checkout bags'
   * @returns {string} The translated string
   */
  getSubcategoryApparelAndTextilesReusableFabricCheckoutBagsText(): string {
    return $localize`:General|Subcategory under Apparel and textiles@@subcat.apparel-and-textiles.reusable-fabric-checkout-bags:Reusable fabric checkout bags`
  }

  /**
   * Returns translated string for 'Food service ware - Clamshell containers, lidded containers, boxes, plates, and bowls'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareClamshellContainersLiddedContainersBoxesPlatesAndBowlsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-clamshell-containers-lidded-containers-boxes-plates-and-bowls:Food service ware - Clamshell containers, lidded containers, boxes, plates, and bowls`
  }

  /**
   * Returns translated string for 'Food service ware - Takeaway cups'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareTakeawayCupsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-takeaway-cups:Food service ware - Takeaway cups`
  }

  /**
   * Returns translated string for 'Food service ware - Lids for takeaway cups'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareLidsForTakeawayCupsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-lids-for-takeaway-cups:Food service ware - Lids for takeaway cups`
  }

  /**
   * Returns translated string for 'Food service ware - Rigid ring carriers'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareRigidRingCarriersText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-rigid-ring-carriers:Food service ware - Rigid ring carriers`
  }

  /**
   * Returns translated string for 'Food service ware - Foam trays'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareFoamTraysText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-foam-trays:Food service ware - Foam trays`
  }

  /**
   * Returns translated string for 'Food service ware - Single-serve capsules and pods'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareSingleServeCapsulesAndPodsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-single-serve-capsules-and-pods:Food service ware - Single-serve capsules and pods`
  }

  /**
   * Returns translated string for 'Food service ware - Bags provided by a retailer and filled within the store with produce or bulk products'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsFoodServiceWareBagsProvidedByARetailerAndFilledWithinTheStoreWithProduceOrBulkProductsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.food-service-ware-bags-provided-by-a-retailer-and-filled-within-the-store-with-produce-or-bulk-products:Food service ware - Bags provided by a retailer and filled within the store with produce or bulk products`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Manual toothbrushes and replaceable toothbrush heads'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsManualToothbrushesAndReplaceableToothbrushHeadsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-manual-toothbrushes-and-replaceable-toothbrush-heads:Personal hygiene and care products - Manual toothbrushes and replaceable toothbrush heads`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Disposable diapers and menstrual products'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsDisposableDiapersAndMenstrualProductsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-disposable-diapers-and-menstrual-products:Personal hygiene and care products - Disposable diapers and menstrual products`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Tubes for personal hygiene and care products'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsTubesForPersonalHygieneAndCareProductsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-tubes-for-personal-hygiene-and-care-products:Personal hygiene and care products - Tubes for personal hygiene and care products`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Razors and razor heads'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsRazorsAndRazorHeadsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-razors-and-razor-heads:Personal hygiene and care products - Razors and razor heads`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Dental floss and flossers'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsDentalFlossAndFlossersText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-dental-floss-and-flossers:Personal hygiene and care products - Dental floss and flossers`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Cotton swab sticks'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsCottonSwabSticksText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-cotton-swab-sticks:Personal hygiene and care products - Cotton swab sticks`
  }

  /**
   * Returns translated string for 'Personal hygiene and care products - Wipes'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalHygieneAndCareProductsWipesText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-hygiene-and-care-products-wipes:Personal hygiene and care products - Wipes`
  }

  /**
   * Returns translated string for 'Novelty items'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsNoveltyItemsText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.novelty-items:Novelty items`
  }

  /**
   * Returns translated string for 'Tobacco and vaping products - Tobacco products containing filters'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsTobaccoProductsContainingFiltersText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.tobacco-and-vaping-products-tobacco-products-containing-filters:Tobacco and vaping products - Tobacco products containing filters`
  }

  /**
   * Returns translated string for 'Tobacco and vaping products - Vaping devices'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsVapingDevicesText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.tobacco-and-vaping-products-vaping-devices:Tobacco and vaping products - Vaping devices`
  }

  /**
   * Returns translated string for 'Tobacco and vaping products - Vaping cartridges'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsTobaccoAndVapingProductsVapingCartridgesText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.tobacco-and-vaping-products-vaping-cartridges:Tobacco and vaping products - Vaping cartridges`
  }

  /**
   * Returns translated string for 'Personal protective equipment - Masks'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalProtectiveEquipmentMasksText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-protective-equipment-masks:Personal protective equipment - Masks`
  }

  /**
   * Returns translated string for 'Personal protective equipment - Gloves'
   * @returns {string} The translated string
   */
  getSubcategorySingleUseOrDisposableProductsPersonalProtectiveEquipmentGlovesText(): string {
    return $localize`:General|Subcategory under Single-use or disposable products@@subcat.single-use-or-disposable-products.personal-protective-equipment-gloves:Personal protective equipment - Gloves`
  }

  /**
   * Returns translated string for 'Report saved successfully.'
   * @returns {string} The translated string
   */
  getReportSavedSuccessfullyText(): string {
    return $localize`:General|Snackbar/Toast@@msg.report.saved.success:Report saved successfully.`
  }

  /**
   * Returns translated string for 'Multiple'
   * @returns {string} The translated string
   */
  getMultipleText(): string {
    return $localize`:General|Multiple@@general.multiple:Multiple`
  }
  /**
   * Returns translated string for 'Quantity Waste Generated at Premises'
   * @returns {string} The translated string
   */
  getWasteGeneratedAtPremisesText(): string {
    return $localize`:FPR Report|Quantity Waste Generated at Premises@@fprReport.wasteGeneratedAtPremises:Quantity Waste Generated at Premises`
  }

  /**
   * Returns translated string for 'Quantity Collected at End of Life'
   * @returns {string} The translated string
   */
  getCollectedAtEndOfLifeText(): string {
    return $localize`:FPR Report|Quantity Collected at End of Life@@fprReport.collectedAtEndOfLife:Quantity Collected at End of Life`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Direct Reuse'
   * @returns {string} The translated string
   */
  getDivertedDirectReuseText(): string {
    return $localize`:FPR Report|Quantity Diverted - Direct Reuse@@fprReport.divertedDirectReuse:Quantity Diverted: Direct Reuse`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Refurbishment'
   * @returns {string} The translated string
   */
  getDivertedRefurbishmentText(): string {
    return $localize`:FPR Report|Quantity Diverted - Refurbishment@@fprReport.divertedRefurbishment:Quantity Diverted: Refurbishment`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Repair'
   * @returns {string} The translated string
   */
  getDivertedRepairText(): string {
    return $localize`:FPR Report|Quantity Diverted - Repair@@fprReport.divertedRepair:Quantity Diverted: Repair`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Remanufacturing'
   * @returns {string} The translated string
   */
  getDivertedRemanufacturingText(): string {
    return $localize`:FPR Report|Quantity Diverted - Remanufacturing@@fprReport.divertedRemanufacturing:Quantity Diverted: Remanufacturing`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Mechanical Recycling'
   * @returns {string} The translated string
   */
  getDivertedMechanicalRecyclingText(): string {
    return $localize`:FPR Report|Quantity Diverted - Mechanical Recycling@@fprReport.divertedMechanicalRecycling:Quantity Diverted: Mechanical Recycling`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Chemical Recycling'
   * @returns {string} The translated string
   */
  getDivertedChemicalRecyclingText(): string {
    return $localize`:FPR Report|Quantity Diverted - Chemical Recycling@@fprReport.divertedChemicalRecycling:Quantity Diverted: Chemical Recycling`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Processing into Chemicals/Fuels'
   * @returns {string} The translated string
   */
  getDivertedProcessingIntoChemicalsFuelsText(): string {
    return $localize`:FPR Report|Quantity Diverted - Processing into Chemicals/Fuels@@fprReport.divertedProcessingIntoChemicalsFuels:Quantity Diverted: Processing into Chemicals/Fuels`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Composting'
   * @returns {string} The translated string
   */
  getDivertedCompostingText(): string {
    return $localize`:FPR Report|Quantity Diverted - Composting@@fprReport.divertedComposting:Quantity Diverted: Composting`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Incineration (with Energy Recovery)'
   * @returns {string} The translated string
   */
  getDivertedIncinerationWithEnergyRecoveryText(): string {
    return $localize`:FPR Report|Quantity Diverted - Incineration (with Energy Recovery)@@fprReport.divertedIncinerationWithEnergyRecovery:Quantity Diverted: Incineration (with Energy Recovery)`
  }

  /**
   * Returns translated string for 'Quantity Diverted: Incineration (Industrial Processes)'
   * @returns {string} The translated string
   */
  getDivertedIncinerationIndustrialProcessesText(): string {
    return $localize`:FPR Report|Quantity Diverted - Incineration (Industrial Processes)@@fprReport.divertedIncinerationIndustrialProcesses:Quantity Diverted: Incineration (Industrial Processes)`
  }

  /**
   * Returns translated string for 'Quantity Final Disposal: Incineration (No Energy Recovery)'
   * @returns {string} The translated string
   */
  getFinalDisposalIncinerationNoEnergyRecoveryText(): string {
    return $localize`:FPR Report|Quantity Final Disposal - Incineration (No Energy Recovery)@@fprReport.finalDisposalIncinerationNoEnergyRecovery:Quantity Final Disposal: Incineration (No Energy Recovery)`
  }

  /**
   * Returns translated string for 'Quantity Final Disposal: Landfill'
   * @returns {string} The translated string
   */
  getFinalDisposalLandfillText(): string {
    return $localize`:FPR Report|Quantity Final Disposal - Landfill@@fprReport.finalDisposalLandfill:Quantity Final Disposal: Landfill`
  }

  /**
   * Returns translated string for 'Published'
   * @returns {string} The translated string
   */
  getPublishedText(): string {
    return $localize`:FPR Report|Published@@fprReport.published:Published`
  }

  /**
   * Returns translated string for 'File is too large (max 10 MB).'
   * @returns {string} The translated string
   */
  getFileTooLargeText(): string {
    return $localize`:General|Text for file too large error @@general.text.fileTooLarge:File is too large (max 10 MB).`
  }

  /*
   * Returns translated string for 'Download started'
   * @returns {string} The translated string
   */
  getDownloadStartedText(): string {
    return $localize`:FPR Report|Download started@@fprReport.downloadStarted:Download started`
  }

  /**
   * Returns translated string for "Report copied successfully to 'reportName'"
   * @param {string} reportName The name of the new report
   * @returns {string} The translated string
   */
  getReportCopiedSuccessText(reportName: string): string {
    return $localize`:Reports|Snackbar message after cloning a report @@reports.snackbar.copied:Report copied successfully to '${reportName}'`
  }

  /*
   * Returns translated string for 'Plastic Packaging Filled'
   */
  getPackagingCategoryPlasticFilledText(): string {
    return $localize`:General|Packaging category option@@packaging.category.plasticFilled:Plastic Packaging Filled`
  }

  /**
   * Returns translated string for 'Plastic Packaging Unfilled'
   */
  getPackagingCategoryPlasticUnfilledText(): string {
    return $localize`:General|Packaging category option@@packaging.category.plasticUnfilled:Plastic Packaging Unfilled`
  }

  /**
   * Returns translated string for 'Beverage container'
   */
  getPackagingSubcategoryBeverageContainerText(): string {
    return $localize`:General|Packaging subcategory option@@packaging.subcategory.beverageContainer:Beverage container`
  }

  /**
   * Returns translated string for 'Food contact material'
   */
  getPackagingSubcategoryFoodContactMaterialText(): string {
    return $localize`:General|Packaging subcategory option@@packaging.subcategory.foodContactMaterial:Food contact material`
  }

  /**
   * Returns translated string for 'Packaging for hazardous materials'
   */
  getPackagingSubcategoryHazardousMaterialsPackagingText(): string {
    return $localize`:General|Packaging subcategory option@@packaging.subcategory.hazardousMaterials:Packaging for hazardous materials`
  }

  /**
   * Returns translated string for 'Other packaging'
   */
  getPackagingSubcategoryOtherPackagingText(): string {
    return $localize`:General|Packaging subcategory option@@packaging.subcategory.other:Other packaging`
  }
  /**
   * Returns translated string for 'Federal Plastics Registry Templates'
   * @returns {string} The translated string
   */
  getFprTemplatesText(): string {
    return $localize`:General|Text for input/information@@fprReport.templates:Federal Plastics Registry Templates`
  }

  /**
   * Returns translated string for 'Edit Template'
   * @returns {string} The translated string
   */
  getEditTemplateText(): string {
    return $localize`:General|Text for input/information@@general.text.editTemplate:Edit Template`
  }

  /**
   * Returns translated string for 'Template created successfully.'
   * @returns {string} The translated string
   */
  getTemplateCreatedText(): string {
    return $localize`:General|Text for input/information@@general.text.templateCreated:Template created successfully.`
  }

  /**
   * Returns translated string for 'Template updated successfully.'
   * @returns {string} The translated string
   */
  getTemplateUpdatedText(): string {
    return $localize`:General|Text for input/information@@general.text.templateUpdated:Template updated successfully.`
  }

  /**
   * Returns translated string for
   * 'Map your spreadsheet column names to the FPR fields below. These values are aliases used when parsing uploads.'
   * @returns {string} The translated string
   */
  getFprTemplateMappingHelpText(): string {
    return $localize`:FPR Template Mapping Help@@fprTemplate.mappingHelp:Map your spreadsheet column names to the FPR fields below. These values are aliases used when parsing uploads.`
  }

  /**
   * Returns translated string for 'Import Report Data'
   * @returns {string} The translated string
   */
  getImportReportDataText(): string {
    return $localize`:FPR Import|Import Report Data@@fpr.importReportData:Import Report Data`
  }

  /**
   * Returns translated string for 'File is required'
   * @returns {string} The translated string
   */
  getFileIsRequiredText(): string {
    return $localize`:FPR Import|File is required@@fpr.fileRequired:File is required`
  }

  /**
   * Returns translated string for
   * 'One or more columns from your template could not be found in the spreadsheet.'
   * @returns {string} The translated string
   */
  getMissingFprTemplateColumnsText(): string {
    return $localize`:API Error|Missing FPR Template Columns@@apiError.missingFprTemplateColumns:One or more columns from your template could not be found in the spreadsheet.`
  }

  /**
   * Returns translated string for
   * 'One or more SKUs in the spreadsheet could not be matched to your company's products.'
   * @returns {string} The translated string
   */
  getProductsMissingForImportText(): string {
    return $localize`:API Error|Products Missing For Import@@apiError.productsMissingForImport:One or more SKUs in the spreadsheet could not be matched to your company's products.`
  }

  /**
   * Returns translated string for
   * 'File is too large (max 10 MB).'
   * @returns {string} The translated string
   */
  getFprImportFileTooLargeText(): string {
    return $localize`:API Error|FPR Import File Too Large@@apiError.fprImportFileTooLarge:File is too large (max 10 MB).`
  }

  /**
   * Returns translated string for "Year is required"
   * @returns {string} The translated string
   */
  getYearRequiredText(): string {
    return $localize`:General|Validation message when year field is required @@year.required:Year is required`
  }

  /**
   * Returns translated string for Raw Resin
   * @returns {string} The translated string
   */
  getProductMaterialTypeRawResinText(): string {
    return $localize`:General|Product material type raw resin@@product.materialType.rawResin:Raw Resin`
  }

  /**
   * Returns translated string for Plastic Product (contains resin)
   * @returns {string} The translated string
   */
  getProductMaterialTypePlasticProductText(): string {
    return $localize`:General|Product material type plastic product@@product.materialType.plasticProduct:Plastic Product (contains resin)`
  }

  /**
   * Returns translated string for Non-Plastic product
   * @returns {string} The translated string
   */
  getProductMaterialTypeNonPlasticText(): string {
    return $localize`:General|Product material type non-plastic@@product.materialType.nonPlastic:Non-plastic`
  }

  /**
   * Returns translated label for Product Material Type dropdown
   * @returns {string} The translated string
   */
  getProductMaterialTypeLabel(): string {
    return $localize`:Form label|Label for selecting product material type@@product.materialType.label:Product Material Type`
  }

  /*
   * Returns translated string for 'Plastics Declaration-Related Fields'
   * @returns {string} The translated string
   */
  getPlasticsDeclarationRelatedFieldsText(): string {
    return $localize`:Section header|Plastics declaration related fields@@form.label.plasticsDeclarationRelatedFields:Plastics Declaration-Related Fields`
  }

  /**
   * Returns translated string for 'Is Residential Waste'
   * @returns {string} The translated string
   */
  getIsResidentialWasteText(): string {
    return $localize`:Form label|Is residential waste@@form.label.isResidentialWaste:Is Residential Waste`
  }

  /**
   * Returns translated string for "Is Plastic Packaging"
   * @returns {string} The translated string
   */
  getIsPlasticPackagingText(): string {
    return $localize`:General|Label for plastic packaging field@@plastic.alt.isPlasticPackaging:Is Plastic Packaging`
  }

  /**
   * Returns translated string for "No products were found matching the criteria for this year to generate a report."
   * @returns {string} The translated string
   */
  getNoProductsForYearGenerationText(): string {
    return $localize`:Reports|Shown when FPR generation returns zero rows for the selected year@@fpr.generate.noProductsFound:No products were found matching the criteria for this year to generate a report.`
  }

  /**
   * Returns translated string for "Product Material Type"
   * @returns {string} The translated string
   */
  getProductMaterialTypeText(): string {
    return $localize`:Product|Field label for product material type@@product.productMaterialType:Product Material Type`
  }

  /**
   * Returns translated string for "Material Type"
   * @returns {string} The translated string
   */
  getMaterialTypeText(): string {
    return $localize`:Product|Field label for material type@@product.materialType:Material Type`
  }

  /**
   * Returns translated string for the publish confirmation shown when totals are below the exemption threshold.
   * @param {number} totalKg  Combined plastics + resins in kilograms
   * @param {number} plasticsKg  Plastics subtotal in kilograms
   * @param {number} resinsKg  Resins subtotal in kilograms
   * @param {number} thresholdKg  Exemption threshold in kilograms
   * @returns {string} The translated string
   */
  getPublishConfirmBelowThresholdText(
    totalKg: number,
    plasticsKg: number,
    resinsKg: number,
    thresholdKg: number,
  ): string {
    const totalStr = totalKg.toFixed(2)
    const plasticsStr = plasticsKg.toFixed(2)
    const resinsStr = resinsKg.toFixed(2)
    const thresholdStr = String(thresholdKg)

    return $localize`:Reports|Confirm dialog when total mass is below exemption threshold@@fpr.publish.confirmBelowThreshold:This report totals ${totalStr} kg (Plastics ${plasticsStr} kg, Resins ${resinsStr} kg), which is below ${thresholdStr} kg and may qualify as exempt. Publish anyway?`
  }

  /**
   * Returns translated string for "Product image not available"
   * @returns {string} The translated string
   */
  getProductImageNotAvailable(): string {
    return $localize`:General|Message whenever image is not available on product@@product.productImageNotAvailable:This product does not have an image uploaded.`
  }

  /**
   * Returns translated string for 'Resin Weight (kg)'
   */
  getResinWeightKgText(): string {
    return $localize`:General|Label for Resin Weight (kg)@@general.label.resinWeightKg:Resin Weight (kg)`
  }

  /**
   * Returns translated string for 'Add Resin'
   * @returns {string} The translated string
   */
  getAddResinText(): string {
    return $localize`:Reports|Add resin button label@@reports.text.addResin:Add Resin`
  }

  /**
   * Returns translated string for Resins File
   * @returns {string} The translated string
   */
  getResinsFileText(): string {
    return $localize`:General|Text for resins file@@general.text.resinsFile:Resins File`
  }

  /**
   * Returns translated string for "No resins found"
   * @returns {string} The translated string
   */
  getNoResinsFoundText(): string {
    return $localize`:General|Text when no resins are found@@general.text.noResinsFound:No resins found`
  }

  /**
   * Returns translated string for "Resin breakdown"
   * @returns {string} The translated string
   */
  getResinBreakdownText(): string {
    return $localize`:FPR|Title for resin breakdown bottom sheet@@fpr.text.resinBreakdown:Resin breakdown`
  }

  /**
   * Returns translated string for "NAPCS"
   * @returns {string} The translated string
   */
  getNapcsText(): string {
    return $localize`:FPR|Column header for NAPCS code@@fpr.text.napcs:NAPCS`
  }

  /**
   * Returns translated string for "kg / unit"
   * @returns {string} The translated string
   */
  getKgPerUnitText(): string {
    return $localize`:FPR|Column header for kilograms per unit@@fpr.text.kgPerUnit:kg / unit`
  }

  /**
   * Returns translated string for 'Raw Resins'
   * @returns {string} The translated string
   */
  getRawResinsText(): string {
    return $localize`:General|Text for input/information@@general.text.rawResins:Raw Resins`
  }

  /**
   * Returns translated string for 'Active'
   * @returns {string} The translated string
   */
  getActiveText(): string {
    return $localize`:General|Text for Active@@general.text.active:Active`
  }

  /**
   * Returns translated string for 'Discontinued'
   * @returns {string} The translated string
   */
  getDiscontinuedText(): string {
    return $localize`:General|Text for Discontinued@@general.text.discontinued:Discontinued`
  }

  /**
   * Returns translated string for 'Discontinue Product'
   * @returns {string} The translated string
   */
  getDiscontinueProductTitleText(): string {
    return $localize`:General|Title for discontinue product dialog@@general.text.discontinueProductTitle:Discontinue Product`
  }

  /**
   * Returns translated string for the discontinue product description
   * @returns {string} The translated string
   */
  getDiscontinueProductDescriptionText(): string {
    return $localize`:General|Description for discontinue product dialog@@general.text.discontinueProductDescription:From which reporting year should this product stop being reported?`
  }

  /**
   * Returns translated string for 'Reporting year'
   * @returns {string} The translated string
   */
  getReportingYearLabelText(): string {
    return $localize`:General|Label for reporting year field@@general.text.reportingYearLabel:Initial Reporting Year`
  }

  /**
   * Returns translated string for the reporting year hint
   * @param {number} minYear Minimum year
   * @param {number} maxYear Maximum year
   * @returns {string} The translated string
   */
  getReportingYearHintText(minYear: number, maxYear: number): string {
    return $localize`:General|Hint for reporting year range@@general.text.reportingYearHint:Between ${minYear} and ${maxYear}`
  }

  /**
   * Returns translated text describing when a discontinued product
   * stops being reported, e.g. "Discontinued after 2025".
   * @param {number} year The last year this product is reported
   * @returns {string} The translated string
   */
  getDiscontinuedEffectiveYearText(year: number): string {
    return $localize`:General|Text showing when product stops being reported@@general.text.discontinuedEffectiveYear:Discontinued after ${year}`
  }

  /**
   * Returns translated error message when discontinued year is earlier than the product's initial reporting year
   * @returns {string} The translated string
   */
  getDiscontinuedYearBeforeReportingYearErrorText(): string {
    return $localize`:General|Error when discontinued year is earlier than initial reporting year@@general.error.discontinuedYearBeforeReportingYear:Discontinued year cannot be earlier than the product's initial reporting year`
  }

  /**
   * Returns translated string for the merge mode help text shown above the options
   * @returns {string} The translated string
   */
  getHowApplyQuantitiesText(): string {
    return $localize`:General|Help text asking how to apply quantities from an uploaded spreadsheet@@general.text.howApplyQuantities:How should quantities from this file be applied?`
  }

  /**
   * Returns translated string for the 'overwrite' merge mode label
   * @returns {string} The translated string
   */
  getMergeModeOverwriteText(): string {
    return $localize`:General|Label for overwrite merge mode when importing quantities@@general.text.mergeModeOverwrite:Replace quantities for matching rows`
  }

  /**
   * Returns translated string for the 'add' merge mode label
   * @returns {string} The translated string
   */
  getMergeModeAddText(): string {
    return $localize`:General|Label for add/append merge mode when importing quantities@@general.text.mergeModeAdd:Add quantities to existing values`
  }

  /**
   * Returns translated string summarizing how many products were imported
   * out of the total eligible for the reporting year.
   *
   * @param {number} imported Number of products imported from the spreadsheet
   * @param {number} total Total number of products eligible for this reporting year
   * @returns {string} The translated summary string
   */
  getSpreadsheetImportSummaryText(imported: number, total: number): string {
    return $localize`:Reports|Summary of imported vs eligible products for this reporting year@@reports.text.spreadsheetImportSummary:Spreadsheet imported successfully. ${imported}:imported: out of a possible ${total}:total: products eligible for this reporting year have been imported.`
  }

  /**
   * Returns translated string for 'Check missing products'
   * @returns {string} The translated string
   */
  getCheckMissingProductsText(): string {
    return $localize`:General|Text for Check missing products@@general.text.checkMissingProducts:Check missing products`
  }

  /**
   * Returns translated string for 'No quantities entered'
   * @returns {string} The translated string
   */
  getNoQuantitiesEnteredText(): string {
    return $localize`:General|Text indicating that no quantities have been entered@@general.text.noQuantitiesEntered:No quantities entered`
  }

  /**
   * Title shown at the top of the missing products sheet.
   * @returns {string} The translated title text
   */
  getMissingProductsForYearTitleText(): string {
    return $localize`:General|Title for missing products bottom sheet@@general.text.missingProductsForYear:Missing products for this reporting year`
  }

  /**
   * Summary line showing how many products are missing out of the total eligible.
   * @param missingCount Number of products missing from the report
   * @param totalEligible Total number of products eligible for this year
   * @returns {string} The translated summary text
   */
  getMissingProductsSummaryText(
    missingCount: number,
    totalEligible: number,
  ): string {
    return $localize`:General|Summary of missing vs total eligible products@@general.text.missingProductsSummary:${missingCount} of ${totalEligible} eligible products are missing from this report`
  }

  /**
   * Message shown when there are no missing products.
   * @returns {string} The translated text
   */
  getAllEligibleProductsAlreadyInReportText(): string {
    return $localize`:General|Message when no products are missing from the report@@general.text.allEligibleProductsAlreadyInReport:All eligible products for this reporting year are already in this report`
  }

  /**
   * Label for the primary action button while missing products are being added.
   * @returns {string} The translated text
   */
  getAddingMissingProductsText(): string {
    return $localize`:General|Button label while missing products are being added@@general.text.addingMissingProducts:Adding missing products…`
  }

  /**
   * Label for the primary action button to add missing products into the report.
   * @returns {string} The translated text
   */
  getAddMissingProductsToReportText(): string {
    return $localize`:General|Button label to add missing products to the report@@general.text.addMissingProductsToReport:Add missing products to report`
  }

  /**
   * Returns validation message shown when a component group
   * has fewer than the required minimum number of components.
   *
   * @returns {string} The translated validation message
   */
  getComponentGroupMinSizeErrorText(): string {
    return $localize`:Product|Validation message for component group requiring at least two components@@product.validation.componentGroupMinSize:A component group must contain at least two components.`
  }

  /**
   * Returns translated string for
   * 'Each component can only be used once per product.'
   *
   * @returns {string} The translated string
   */
  getDuplicateProductComponentsText(): string {
    return $localize`:API Error|Duplicate product components@@apiError.duplicateProductComponents:Each component can only be used once per product.`
  }

  /**
   * Returns translated string for the "Add Component Group" button label.
   * @returns {string} The translated string.
   */
  getAddComponentGroupText(): string {
    return $localize`:Product Components|Add component group button label@@product.components.addComponentGroup:Add Component Group`
  }

  /**
   * Returns translated label for a component group with its index.
   * @param groupNumber Display index for the group.
   * @returns {string} The translated string.
   */
  getComponentGroupLabelText(groupNumber: number): string {
    return $localize`:Product Components|Component group label with index@@product.components.componentGroupLabel:Component group ${groupNumber}`
  }

  /**
   * Returns translated string for 'Group Name'
   * @returns {string} The translated string
   */
  getGroupNameText(): string {
    return $localize`:General|Text for input/information@@general.text.groupName:Group Name`
  }

  /**
   * Returns translated string for 'Remove Group'
   * @returns {string} The translated string
   */
  getRemoveGroupText(): string {
    return $localize`:General|Text for input/information@@general.text.removeGroup:Remove Group`
  }

  /**
   * Returns translated string for the "Please select a waste stream" message.
   * @returns {string} The translated string.
   */
  getSelectWasteStreamMessage(): string {
    return $localize`:Plastic Declaration|Select waste stream message@@waste.management.selectWasteStream:Please select a waste stream.`
  }

  /**
   * Returns translated string for the "The '*' indicates that this field is required." message.
   * @returns {string} The translated string.
   */
  getFieldRequiredIndicatorText(): string {
    return $localize`:General|Field required indicator message@@form.instructions.fieldRequiredIndicator:The "*" indicates that this field is required.`
  }

  /**
   * Returns translated string for the "Component-related fields" section header.
   * @returns {string} The translated string.
   */
  getComponentRelatedFieldsText(): string {
    return $localize`:Product CSV Templates|Section header for component-related fields@@product.csvTemplates.components.sectionHeader:Component-related fields`
  }

  /**
   * Returns translated string for the "Parent SKU" field label.
   * @returns {string} The translated string.
   */
  getComponentParentSkuText(): string {
    return $localize`:Product CSV Templates|Label for the parent product SKU field in component mappings@@product.csvTemplates.components.parentSkuLabel:Parent SKU`
  }

  /**
   * Returns translated string for the "Component SKU" field label.
   * @returns {string} The translated string.
   */
  getComponentSkuText(): string {
    return $localize`:Product CSV Templates|Label for the component SKU field in component mappings@@product.csvTemplates.components.skuLabel:Component SKU`
  }

  /**
   * Returns translated string for the "Component name" field label.
   * @returns {string} The translated string.
   */
  getComponentNameText(): string {
    return $localize`:Product CSV Templates|Label for the component name field in component mappings@@product.csvTemplates.components.nameLabel:Component Name`
  }

  /**
   * Returns translated string for the "Component Group Name" field label.
   * @returns {string} The translated string.
   */
  getComponentGroupNameText(): string {
    return $localize`:Product CSV Templates|Label for the component group name field in component mappings@@product.csvTemplates.components.groupNameLabel:Component Group Name`
  }

  /**
   * Returns translated string for the "Component Quantity" field label.
   * @returns {string} The translated string.
   */
  getComponentQuantityText(): string {
    return $localize`:Product CSV Templates|Label for the component quantity field in component mappings@@product.csvTemplates.components.quantityLabel:Component Quantity`
  }

  /**
   * Returns translated string for the "Component Supplier" field label.
   * @returns {string} The translated string.
   */
  getComponentSupplierText(): string {
    return $localize`:Product CSV Templates|Label for the component supplier field in component mappings@@product.csvTemplates.components.supplierLabel:Component Supplier`
  }

  /**
   * Returns translated string for the "Validation status" field label.
   * @returns {string} The translated string.
   */
  getValidationStatusText(): string {
    return $localize`:Import review|Label for the validation status column in import review tables@@import.review.validationStatusLabel:Validation status`
  }

  /**
   * Returns translated string for the "Ungrouped" component group label.
   * @returns {string} The translated string.
   */
  getUngroupedComponentsText(): string {
    return $localize`:Import review|Label for components that are not assigned to a component group@@import.review.components.ungroupedLabel:Ungrouped`
  }

  /**
   * Returns translated string for the "Invalid" status label.
   * @returns {string} The translated string.
   */
  getInvalidText(): string {
    return $localize`:Validation|Label for an invalid status@@validation.status.invalid:Invalid`
  }

  /**
   * Returns translated string for the "Valid" status label.
   * @returns {string} The translated string.
   */
  getValidText(): string {
    return $localize`:Validation|Label for a valid status@@validation.status.valid:Valid`
  }

  /**
   * Returns translated string for the "Components file" field label.
   * @returns {string} The translated string.
   */
  getComponentsFileText(): string {
    return $localize`:Product import|Label for the components CSV upload field@@product.import.componentsFileLabel:Components file`
  }

  /**
   * Returns the translated tooltip text explaining interchangeable component groups.
   */
  getGroupInterchangeabilityTooltip(): string {
    return $localize`:General|Tooltip explaining interchangeable component groups@@product.components.groupInterchangeabilityTooltip:Components in this group are interchangeable options. Their weights are averaged (ABOM) when deriving resins.`
  }

  /**
   * Returns translated string for 'Failed to load companies'
   * @returns {string} The translated string
   */
  getFailedToLoadCompaniesText(): string {
    return $localize`:Companies|Error message when company list fails to load@@companies.text.failedToLoadCompanies:Failed to load companies`
  }

  /**
   * Returns translated string for 'Manage connections for {companyName}'
   * @param {string} companyName The company name to display in the message
   * @returns {string} The translated string
   */
  getManageConnectionsForCompanyText(companyName: string): string {
    return $localize`:Companies|Dialog text for managing company connections@@companies.text.manageConnectionsForCompany:Manage connections for ${companyName}`
  }

  /**
   * Returns translated string for 'Connections'
   * @returns {string} The translated string
   */
  getConnectionsText(): string {
    return $localize`:Companies|Section title for company connections@@companies.text.connections:Connections`
  }

  /**
   * Returns translated string for 'Inactive'
   * @returns {string} The translated string
   */
  getInactiveText(): string {
    return $localize`:General|Label for inactive state@@general.text.inactive:Inactive`
  }

  /**
   * Returns translated string for 'Add connection'
   * @returns {string} The translated string
   */
  getAddConnectionText(): string {
    return $localize`:Companies|Section title for adding a new connection@@companies.text.addConnection:Add connection`
  }

  /**
   * Returns translated string for 'Search'
   * @returns {string} The translated string
   */
  getSearchText(): string {
    return $localize`:General|Button label for search@@general.text.search:Search`
  }

  /**
   * Returns translated string for 'Add'
   * @returns {string} The translated string
   */
  getAddText(): string {
    return $localize`:General|Button label for add@@general.text.add:Add`
  }

  /**
   * Returns translated string for 'Manage connections'
   * @returns {string} The translated string
   */
  getManageConnectionsText(): string {
    return $localize`:Companies|Dialog text for managing company connections@@companies.text.manageConnections:Manage connections`
  }

  /*
   * Returns translated string for
   * 'This Primary Article Identifier already exist in the database.'
   *
   * @returns {string} The translated string
   */
  getPrimaryArticleIdentifierAlreadyExistsText(): string {
    return $localize`:API Error|Primary Article Identifier Exists@@apiError.primaryArticleIdentifierAlreadyExists:This Primary Article Identifier already exist in the database.`
  }

  /**
   * Returns translated string for 'Share'
   * @returns {string} The translated string
   */
  getShareText(): string {
    return $localize`:General|Text for share@@general.text.share:Share`
  }

  /**
   * Returns translated string for 'Shared with'
   * @returns {string} The translated string
   */
  getSharedWithText(): string {
    return $localize`:Share Products Dialog|Section title for companies product is shared with@@shareProductsDialog.text.sharedWith:Shared with`
  }

  /**
   * Returns translated string for 'Not shared yet'
   * @returns {string} The translated string
   */
  getNotSharedYetText(): string {
    return $localize`:Share Products Dialog|Empty state when product has no shares@@shareProductsDialog.text.notSharedYet:Not shared yet`
  }

  /**
   * Returns translated string for 'Overrides'
   * @returns {string} The translated string
   */
  getOverridesText(): string {
    return $localize`:Share Products Dialog|Column header label@@shareProductsDialog.text.overrides:Overrides`
  }

  /**
   * Returns translated string for the overrides tooltip
   * @returns {string} The translated string
   */
  getOverridesHelpText(): string {
    return $localize`:Share Products Dialog|Help tooltip explaining supplier overrides@@shareProductsDialog.text.overridesHelp:When enabled, supplier names will be shown as your company name for the recipient. Product supplier affects the product itself. Components affects all components (and nested components) of the product.`
  }

  /**
   * Returns translated string for 'Product supplier'
   * @returns {string} The translated string
   */
  getProductSupplierText(): string {
    return $localize`:Share Products Dialog|Label indicating product supplier override@@shareProductsDialog.text.productSupplier:Product supplier`
  }

  /**
   * Returns translated string for 'Share'
   * @returns {string} The translated string
   */
  getShareSectionText(): string {
    return $localize`:Share Products Dialog|Section title for sharing controls@@shareProductsDialog.text.shareSection:Share`
  }

  /**
   * Returns translated string for 'Available connections'
   * @returns {string} The translated string
   */
  getAvailableConnectionsText(): string {
    return $localize`:Share Products Dialog|Label for available supply chain connections@@shareProductsDialog.text.availableConnections:Available connections`
  }

  /**
   * Returns translated string for 'No supply chain connections found'
   * @returns {string} The translated string
   */
  getNoSupplyChainConnectionsFoundText(): string {
    return $localize`:Share Products Dialog|Empty state when user has no company connections@@shareProductsDialog.text.noSupplyChainConnectionsFound:No supply chain connections found`
  }

  /**
   * Returns translated string for 'All your connections already have access'
   * @returns {string} The translated string
   */
  getAllConnectionsAlreadyHaveAccessText(): string {
    return $localize`:Share Products Dialog|Empty state when all connections are already shared@@shareProductsDialog.text.allConnectionsAlreadyHaveAccess:All your connections already have access`
  }

  /**
   * Returns translated string for 'Supplier overrides:'
   * @returns {string} The translated string
   */
  getSupplierOverridesText(): string {
    return $localize`:Share Products Dialog|Label introducing supplier override explanation@@shareProductsDialog.text.supplierOverrides:Supplier overrides:`
  }

  /**
   * Returns translated string for the supplier overrides note body
   * @returns {string} The translated string
   */
  getSupplierOverridesNoteText(): string {
    return $localize`:Share Products Dialog|Explanation shown above override checkboxes@@shareProductsDialog.text.supplierOverridesNote:If enabled, the recipient will see supplier names as your company name. “Product supplier” affects the product itself. “Component suppliers” affects all components (and nested components).`
  }

  /**
   * Returns translated string for 'Override product supplier'
   * @returns {string} The translated string
   */
  getOverrideProductSupplierText(): string {
    return $localize`:Share Products Dialog|Checkbox label@@shareProductsDialog.text.overrideProductSupplier:Override product supplier`
  }

  /**
   * Returns translated string for 'Override component suppliers'
   * @returns {string} The translated string
   */
  getOverrideComponentSuppliersText(): string {
    return $localize`:Share Products Dialog|Checkbox label@@shareProductsDialog.text.overrideComponentSuppliers:Override component suppliers`
  }

  /**
   * Returns translated string for 'Product Sharing CSV Templates'
   * @returns {string} The translated string
   */
  getProductSharingCSVTemplatesText(): string {
    return $localize`:Settings|Heading for product sharing CSV templates@@settings.text.productSharingCsvTemplates:Product Sharing CSV Templates`
  }

  /**
   * Returns translated string for 'Share Products'
   * @returns {string} The translated string
   */
  getShareProductsText(): string {
    return $localize`:Products|Heading for sharing products@@products.text.shareProducts:Share Products`
  }

  /**
   * Returns translated string for 'Bulk share completed'
   * @returns {string} The translated string
   */
  getBulkShareSuccessText(): string {
    return $localize`:Products|Success message after bulk sharing@@products.text.bulkShareSuccess:Bulk share completed`
  }

  /**
   * Returns translated string for 'Review'
   * @returns {string} The translated string
   */
  getReviewText(): string {
    return $localize`:Bulk Share|Review section title@@bulkShare.text.review:Review`
  }

  /**
   * Returns translated string for 'Valid rows'
   * @returns {string} The translated string
   */
  getValidRowsText(): string {
    return $localize`:Bulk Share|Label for valid rows count@@bulkShare.text.validRows:valid rows`
  }

  /**
   * Returns translated string for 'Product resolved'
   * @returns {string} The translated string
   */
  getProductResolvedText(): string {
    return $localize`:Bulk Share|Table header for product resolved status@@bulkShare.table.productResolved:Product resolved`
  }

  /**
   * Returns translated string for 'Company valid'
   * @returns {string} The translated string
   */
  getCompanyValidText(): string {
    return $localize`:Bulk Share|Table header for recipient company valid status@@bulkShare.table.companyValid:Company valid`
  }

  /**
   * Returns translated string for 'In supply chain'
   * @returns {string} The translated string
   */
  getConnectedText(): string {
    return $localize`:Bulk Share|Table header for supply chain connection status@@bulkShare.table.inSupplyChain:In supply chain`
  }

  /**
   * Returns translated string for 'Already shared'
   * @returns {string} The translated string
   */
  getAlreadySharedText(): string {
    return $localize`:Bulk Share|Table header for already shared status@@bulkShare.table.alreadyShared:Already shared`
  }

  /**
   * Returns translated string for 'OK'
   * @returns {string} The translated string
   */
  getOkText(): string {
    return $localize`:Bulk Share|Status text OK@@bulkShare.status.ok:OK`
  }

  /**
   * Returns translated string for 'Failed'
   * @returns {string} The translated string
   */
  getFailedText(): string {
    return $localize`:Bulk Share|Status text failed@@bulkShare.status.failed:Failed`
  }

  /**
   * Returns translated string for the template selection helper text on Bulk Share page.
   * @returns {string} The translated string
   */
  getProductShareCsvTemplateSelectionMessageText(): string {
    return $localize`:Bulk Share|Helper text for selecting a CSV template@@bulkShare.text.templateSelectionMessage:Select a CSV template to map your column names to our system fields.`
  }

  /**
   * Returns translated string for the CSV upload helper text on Bulk Share page.
   * @returns {string} The translated string
   */
  getProductShareCsvFileMessageText(): string {
    return $localize`:Bulk Share|Helper text for uploading the product share CSV@@bulkShare.text.csvUploadMessage:Upload a CSV file to validate and preview product share records before creating them.`
  }

  /**
   * Returns translated string for the label 'Product Share CSV file'.
   * @returns {string} The translated string
   */
  getProductShareCsvFileText(): string {
    return $localize`:Bulk Share|Label for product share CSV file input@@bulkShare.text.csvFileLabel:Product share CSV file`
  }

  /**
   * Returns translated string for the Bulk Product Sharing page title.
   * @returns {string} The translated string
   */
  getBulkProductSharingText(): string {
    return $localize`:Bulk Share|Page title for bulk product sharing@@bulkShare.title:Bulk Product Sharing`
  }

  /**
   * Returns translated string for 'Result'
   * @returns {string} The translated string
   */
  getResultText(): string {
    return $localize`:Product Share|Header for bulk share review result column@@productShare.bulkReview.result:Result`
  }

  /**
   * Returns translated string for 'Ready'
   * @returns {string} The translated string
   */
  getReadyText(): string {
    return $localize`:Product Share|Label for a row that is valid and ready to create@@productShare.bulkReview.ready:Ready`
  }

  /**
   * Returns translated string for 'Product not resolved'
   * @returns {string} The translated string
   */
  getProductNotResolvedText(): string {
    return $localize`:Product Share|Row status when the product identifier could not be resolved@@productShare.bulkReview.productNotResolved:Product not resolved`
  }

  /**
   * Returns translated string for 'Company not valid'
   * @returns {string} The translated string
   */
  getCompanyNotValidText(): string {
    return $localize`:Product Share|Row status when the recipient company name is invalid@@productShare.bulkReview.companyNotValid:Company not valid`
  }

  /**
   * Returns translated string for 'Not in supply chain'
   * @returns {string} The translated string
   */
  getNotInSupplyChainText(): string {
    return $localize`:Product Share|Row status when recipient is not connected in supply chain@@productShare.bulkReview.notInSupplyChain:Not in supply chain`
  }

  /**
   * Returns translated string for 'Step 2: Review & Submit Product Connections'
   * @returns {string} The translated string
   */
  getReviewAndSubmitProductConnectionsText(): string {
    return $localize`:General|Text for input/information@@general.text.reviewAndSubmitProductConnections:Step 2: Review & Submit Product Connections`
  }

  /**
   * Returns translated string for 'Please upload a valid CSV file that matches the specified template.'
   * @returns {string} The translated string
   */
  getInvalidCsvForTemplateText(): string {
    return $localize`:API Error|Invalid CSV Template@@apiError.invalidCsvForTemplate:Please upload a valid CSV file that matches the specified template.`
  }

  /**
   * Returns translated string for duplicate CSV field-mapping aliases.
   * Shown when the same alias is used for multiple system fields.
   *
   * @returns {string} The translated string
   */
  getDuplicateFieldMappingAliasText(): string {
    return $localize`:CSV Template Error|Duplicate field mapping alias@@csvTemplate.duplicateFieldMappingAlias:Each field mapping name must be unique. Please update duplicate values and try again.`
  }

  /**
   * Returns the column header text for product share visibility.
   * Indicates whether a product is already shared or will be shared as part of this action.
   *
   * @returns {string} The translated string
   */
  getShareStatusText(): string {
    return $localize`:Product sharing|Share status column header@@productShare.shareStatus:Share status`
  }

  /**
   * Returns the text shown when a product is NOT yet shared
   * and will be shared as part of the current action.
   *
   * @returns {string} The translated string
   */
  getNotSharedText(): string {
    return $localize`:Product sharing|Product will be newly shared@@productShare.notShared:Will be shared`
  }

  /**
   * Returns translated string for indirect product share removal restriction.
   * Shown when attempting to unshare a component that was shared via a parent product.
   *
   * @returns {string} The translated string
   */
  getCannotUnshareIndirectProductText(): string {
    return $localize`:API Error|Cannot unshare indirect product@@apiError.cannotUnshareIndirectProduct:This item was shared as part of a parent product. To remove access, unshare the parent product instead.`
  }

  /*
   * Returns translated string for
   * 'Add external row'
   *
   * @returns {string} The translated string
   */
  getAddExternalRowText(): string {
    return $localize`:FPR Report|Add External Row@@fprReport.addExternalRow:Add external row`
  }

  /*
   * Returns translated string for the tooltip describing the external row action.
   *
   * @returns {string} The translated string
   */
  getAddExternalRowTooltipText(): string {
    return $localize`:FPR Report|Add External Row Tooltip@@fprReport.addExternalRowTooltip:Add a report row for sales data where the product isn’t in the system. Composition is provided manually or derived from other suppliers for plastic products.`
  }

  /*
   * Returns translated string for
   * 'External Product'
   *
   * @returns {string} The translated string
   */
  getExternalProductText(): string {
    return $localize`:FPR Report|External Product@@fprReport.externalProduct:External Product`
  }

  /*
   * Returns translated string for
   * 'Added from external sales data. This product is not in your product library.'
   *
   * @returns {string} The translated string
   */
  getExternalProductTooltipText(): string {
    return $localize`:FPR Report|External Product Tooltip@@fprReport.externalProductTooltip:Added from external sales data. This product is not in your product library.`
  }

  /**
   * Returns translated string for 'ABOM can only be used when the SKU already exists in the report.'
   * @returns {string} The translated string
   */
  getAbomRequiresExistingSkuText(): string {
    return $localize`:FPR Report|Error shown when user selects ABOM for a SKU that does not already exist in the report@@fpr.error.abomRequiresExistingSku:ABOM can only be used when the SKU already exists in the report.`
  }

  /**
   * Returns translated string for 'Product tags must be unique.'
   * @returns {string} The translated string
   */
  getDuplicateProductTagsError(): string {
    return $localize`:API Error|Bad Request@@apiError.duplicateProductTags:Product tags must be unique.`
  }

  /**
   * Returns translated string for 'Tags'
   * @returns {string} The translated string
   */
  getTagsText(): string {
    return $localize`:General|Text for input/information@@general.text.tags:Tags`
  }

  /**
   * Returns translated string for 'Add a tag and press enter'
   * @returns {string} The translated string
   */
  getAddTagPlaceholderText(): string {
    return $localize`:General|Placeholder text@@general.placeholder.addTagAndPressEnter:Add a tag and press enter`
  }

  /**
   * Returns translated string for 'About product tags'
   * @returns {string} The translated string
   */
  getAboutProductTagsText(): string {
    return $localize`:General|Accessible label text@@general.text.aboutProductTags:About product tags`
  }

  /**
   * Returns translated string for 'Tags help you group products for reporting. Add one or more tags here, then select those tags when generating an FPR report.'
   * @returns {string} The translated string
   */
  getProductTagsTooltipText(): string {
    return $localize`:General|Tooltip text@@general.tooltip.productTags:Tags help you group products for reporting. Add one or more tags here, then select those tags when generating an FPR report.`
  }

  /**
   * Returns translated string for 'Optional. Use tags to group this product for FPR reporting.'
   * @returns {string} The translated string
   */
  getProductTagsHintText(): string {
    return $localize`:General|Hint text@@general.hint.productTags:Optional. Use tags to group this product for FPR reporting.`
  }

  /**
   * Returns translated string for 'Remove tag'
   * @returns {string} The translated string
   */
  getRemoveTagText(): string {
    return $localize`:General|Accessible label text@@general.text.removeTag:Remove tag`
  }

  /**
   * Returns translated string for tag delimiter guidance
   * @returns {string} The translated string
   */
  getTagsDelimiterInfoText(): string {
    return $localize`:Tags Import|Instruction@@tags.delimiter.info:Tags should be separated using a semicolon (;) delimiter.`
  }

  /**
   * Returns the heading for a product's custom fields.
   * @returns {string} The translated string.
   */
  getCustomFieldsText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFields:Custom fields`
  }

  /**
   * Returns the label for adding a custom field.
   * @returns {string} The translated string.
   */
  getAddCustomFieldText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.addCustomField:Add field`
  }

  /**
   * Returns the label for removing a custom field.
   * @returns {string} The translated string.
   */
  getRemoveCustomFieldText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.removeCustomField:Remove field`
  }

  /**
   * Returns the label for a custom field's name.
   * @returns {string} The translated string.
   */
  getCustomFieldNameText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldName:Field name`
  }

  /**
   * Returns the label for a custom field's type.
   * @returns {string} The translated string.
   */
  getCustomFieldTypeText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldType:Field type`
  }

  /**
   * Returns the label for a custom field's value.
   * @returns {string} The translated string.
   */
  getCustomFieldValueText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldValue:Value`
  }

  /**
   * Returns the label for an optional numeric unit.
   * @returns {string} The translated string.
   */
  getCustomFieldUnitText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldUnit:Unit (optional)`
  }

  /**
   * Returns the label for the text field type.
   * @returns {string} The translated string.
   */
  getCustomFieldTextText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldText:Text`
  }

  /**
   * Returns the label for the number field type.
   * @returns {string} The translated string.
   */
  getCustomFieldNumberText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldNumber:Number`
  }

  /**
   * Returns the label for the Yes/No field type.
   * @returns {string} The translated string.
   */
  getCustomFieldBooleanText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldBoolean:Yes / No`
  }

  /**
   * Returns the validation message for a blank or oversized field name.
   * @returns {string} The translated string.
   */
  getCustomFieldNameErrorText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldNameError:Enter a field name (maximum 128 bytes).`
  }

  /**
   * Returns the validation message for duplicate field names.
   * @returns {string} The translated string.
   */
  getCustomFieldDuplicateErrorText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldDuplicateError:Field names must be unique, ignoring spaces at the ends and letter case.`
  }

  /**
   * Returns the validation message for a value that does not match its field type.
   * @returns {string} The translated string.
   */
  getCustomFieldValueErrorText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldValueError:Enter a value that matches the field type (text: maximum 2048 bytes).`
  }

  /**
   * Returns the validation message for an oversized unit.
   * @returns {string} The translated string.
   */
  getCustomFieldUnitErrorText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldUnitError:Use at most 64 bytes for the unit.`
  }

  /**
   * Returns the validation message for the custom field count or data-size limit.
   * @returns {string} The translated string.
   */
  getCustomFieldLimitErrorText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldLimitError:Use at most 50 fields and 65,535 bytes of custom field data.`
  }

  /**
   * Returns the visibility guidance shown beside custom fields.
   * @returns {string} The translated string.
   */
  getCustomFieldPublicHintText(): string {
    return $localize`:Product custom fields|Interface text@@customFields.customFieldPublicHint:Custom fields are visible to everyone who can view this product. Keep private compliance information in the existing compliance sections.`
  }
  getMarketplaceImportTemplateText(): string {
    return $localize`:Product import|Import help@@import.marketplaceImportTemplate:Marketplace products (ready to use)`
  }

  getMarketplaceImportHelpText(): string {
    return $localize`:Product import|Import help@@import.marketplaceImportHelp:To import marketplace products, fill in Product name, Product identifier, Marketplace category, Request unit, Price, and Currency (for example USD). Choose a category and unit that match your product. Use a unique product identifier; SKU uses this identifier if omitted. All other columns are optional and may be removed. Additional files are optional.`
  }

  getMarketplaceImportValuesText(): string {
    return $localize`:Product import|Import help@@import.marketplaceImportValues:Use category and subcategory names as shown in product settings. Request unit is a short unit such as each, kg or box. Marketplace products require a positive Price and a three-letter Currency code (for example USD). Condition: New, Used or Refurbished. For Yes / No fields, including Private and Allow requests, use Yes / No or true / false. Additional product specifications are optional.`
  }

  getImportCustomFieldsHelpText(): string {
    return $localize`:Product import|Import help@@import.importCustomFieldsHelp:To import custom fields, create a CSV template in Settings and add a column for each field. Choose its name, type and optional numeric unit in the template. Enter values only in the spreadsheet, such as 0 for a numeric field or No for a Yes / No field. Blank cells omit the field for that product.`
  }

  getMarketplaceImportPrivacyText(): string {
    return $localize`:Product import|Import help@@import.marketplaceImportPrivacy:Products belong to your company. Marketplace visibility follows your company publication setting and each product’s Private setting. Requests are enabled only when Allow requests is Yes. Blank settings default to No.`
  }

  getOptionalImportFilesText(): string {
    return $localize`:Product import|Import help@@import.optionalImportFiles:Optional files — attach only files referenced in your CSV`
  }

  getImportValidationText(message: string): string {
    const custom = /^Custom field \((.*?)\): (.*)$/.exec(message)
    if (custom) {
      const [, column, problem] = custom
      const detail = this.getImportValidationText(problem)
      return $localize`:Product import|Custom field row error@@import.customFieldError:Custom field (${column}:column:): ${detail}:detail:`
    }
    const numeric = /^Invalid number value for (.*)\.$/.exec(message)
    if (numeric) {
      const field = numeric[1]
      return $localize`:Product import|Numeric row error@@import.numberError:Enter a finite number for ${field}:field:.`
    }
    if (
      message.includes(
        'ownership and company publication cannot be set by CSV.',
      )
    ) {
      const field = message.split(':')[0]
      return $localize`:Product import|Ownership row error@@import.ownershipError:${field}:field:: Products belong to your company; company publication cannot be set in the CSV.`
    }
    const errors: Record<string, string> = {
      'Request unit is required.': $localize`:Product import|Validation@@import.requestUnitRequired:Request unit is required.`,
      'request unit is required': $localize`:Product import|Validation@@import.requestUnitRequired:Request unit is required.`,
      'Marketplace category is required.': $localize`:Product import|Validation@@import.categoryRequired:Marketplace category is required.`,
      'invalid marketplace category': $localize`:Product import|Validation@@import.invalidCategory:Choose a marketplace category from product settings.`,
      'invalid marketplace subcategory': $localize`:Product import|Validation@@import.invalidSubcategory:Choose a subcategory belonging to the marketplace category.`,
      'marketplace subcategory requires a category': $localize`:Product import|Validation@@import.subcategoryRequiresCategory:A marketplace subcategory requires a category.`,
      'price is required': $localize`:Product import|Validation@@import.priceRequired:Price is required.`,
      'Picture: product pictures must be 5 MB or smaller': $localize`:Product import|Validation@@import.pictureBytes:Picture must be 5 MB or smaller.`,
      'Picture: product pictures must fit within 2000 by 2000 pixels': $localize`:Product import|Validation@@import.picturePixels:Picture dimensions are too large. Choose a smaller image.`,
      'Picture: invalid product picture': $localize`:Product import|Validation@@import.pictureFormat:Choose a valid JPEG or PNG picture.`,
      'Picture: product pictures must be JPEG or PNG': $localize`:Product import|Validation@@import.pictureFormat:Choose a valid JPEG or PNG picture.`,
      'price must be positive': $localize`:Product import|Validation@@import.positivePrice:Price must be positive.`,
      'price currency must be a three-letter code': $localize`:Product import|Validation@@import.priceCurrency:Price requires a three-letter currency code.`,
      'condition must be New, Used, or Refurbished': $localize`:Product import|Validation@@import.condition:Condition must be New, Used or Refurbished.`,
      'flight time must be positive': $localize`:Product import|Validation@@import.flightTime:Flight time must be positive.`,
      'weight must be a finite nonnegative number': $localize`:Product import|Validation@@import.weight:Weight must be a finite nonnegative number.`,
      'request unit must be at most 64 bytes': $localize`:Product import|Validation@@import.unitLength:Request unit must be at most 64 bytes.`,
      'customFields: name is required and must be at most 128 bytes':
        this.getCustomFieldNameErrorText(),
      'customFields: names must be unique':
        this.getCustomFieldDuplicateErrorText(),
      'customFields: units are only supported for numbers (maximum 64 bytes)':
        this.getCustomFieldUnitErrorText(),
      'customFields: maximum 50 fields': this.getCustomFieldLimitErrorText(),
      'customFields: number value must be finite':
        this.getCustomFieldValueErrorText(),
      'customFields: Yes / No value must be boolean':
        this.getCustomFieldValueErrorText(),
      'customFields: text value is required (maximum 2048 bytes)':
        this.getCustomFieldValueErrorText(),
      'customFields: type must be text, number or boolean': $localize`:Product import|Validation@@import.customFieldType:Field type must be text, number or boolean (Yes / No).`,
    }
    return errors[message] ?? message
  }
  getImportColumnGuideText(): string {
    return $localize`:Product import|Help heading@@import.columnGuide:Column guide and custom fields`
  }
  getImportCsvFileHelpText(): string {
    return $localize`:Product import|File help@@import.csvFileHelp:Select your product CSV. Extra files are optional; attach them only when referenced in your CSV.`
  }
  getImportUploadText(): string {
    return $localize`:Product import|Flow interface@@import.flow.upload:Upload`
  }
  getImportReviewText(): string {
    return $localize`:Product import|Flow interface@@import.flow.review:Review`
  }
  getImportCompleteText(): string {
    return $localize`:Product import|Flow interface@@import.flow.complete:Complete`
  }
  getImportSelectedFilesText(): string {
    return $localize`:Product import|Flow interface@@import.flow.selectedFiles:Selected files`
  }
  getImportAdditionalHelpText(): string {
    return $localize`:Product import|Flow interface@@import.flow.additionalHelp:Additional files and guidance`
  }
  getImportReviewInvalidatedText(): string {
    return $localize`:Product import|Flow interface@@import.flow.reviewInvalidated:Your template or files changed. Validate files again before confirming.`
  }
  getImportValidateFilesText(): string {
    return $localize`:Product import|Flow interface@@import.flow.validateFiles:Validate files`
  }
  getImportBackToUploadText(): string {
    return $localize`:Product import|Flow interface@@import.flow.backToUpload:Back to upload`
  }
  getImportConfirmText(): string {
    return $localize`:Product import|Flow interface@@import.flow.confirm:Confirm import`
  }
  getImportConfirmationHelpText(): string {
    return $localize`:Product import|Flow interface@@import.flow.confirmationHelp:Products are created only after you confirm. All products must pass validation. To correct an issue, return to upload, update your CSV or attachments, and validate again.`
  }
  getImportAnotherText(): string {
    return $localize`:Product import|Flow interface@@import.flow.another:Import more products`
  }
  getImportTotalText(): string {
    return $localize`:Product import|Flow interface@@import.flow.total:Total products`
  }
  getImportReadyText(): string {
    return $localize`:Product import|Flow interface@@import.flow.ready:Ready to import`
  }
  getImportAttentionText(): string {
    return $localize`:Product import|Flow interface@@import.flow.attention:Needs attention`
  }
  getImportShowAllText(): string {
    return $localize`:Product import|Flow interface@@import.flow.showAll:Show all products`
  }
  getImportShowIssuesText(): string {
    return $localize`:Product import|Flow interface@@import.flow.showIssues:Show products needing attention`
  }
  getImportNoProductsText(): string {
    return $localize`:Product import|Flow interface@@import.flow.noProducts:No products were found. Return to upload and select a CSV containing products.`
  }
  getImportUnnamedProductText(): string {
    return $localize`:Product import|Flow interface@@import.flow.unnamedProduct:Unnamed product`
  }
  getImportProductDetailsText(): string {
    return $localize`:Product import|Flow interface@@import.flow.productDetails:Specifications, custom fields and supporting details`
  }
  getImportLinkedIssuesText(): string {
    return $localize`:Product import|Flow interface@@import.flow.linkedIssues:Review the linked components and compliance details, then correct the source files and validate again.`
  }
  getImportAttachmentText(): string {
    return $localize`:Product import|Flow interface@@import.flow.attachment:Product attachment`
  }
  getImportValidatingText(): string {
    return $localize`:Product import|Flow interface@@import.flow.validating:Validating files…`
  }
  getImportConfirmingText(): string {
    return $localize`:Product import|Flow interface@@import.flow.confirming:Importing products…`
  }
  getImportDownloadingText(): string {
    return $localize`:Product import|Flow interface@@import.flow.downloading:Downloading template…`
  }
  getImportValidationFailedText(): string {
    return $localize`:Product import|Flow interface@@import.flow.validationFailed:Validation failed. Your selected files are retained. Check the error, correct your files if needed, and retry.`
  }
  getImportConfirmationFailedText(): string {
    return $localize`:Product import|Flow interface@@import.flow.confirmationFailed:Import failed. Your review is retained. Resolve any reported issues before trying again.`
  }
  getImportResultText(count: number): string {
    if (count === 1)
      return $localize`:Product import|Single product result@@import.flow.resultOne:Imported 1 product successfully.`
    return $localize`:Product import|Flow interface@@import.flow.result:Imported ${count}:count: products successfully.`
  }
  getImportRemoveFilesText(label: string): string {
    return $localize`:Product import|Flow interface@@import.flow.removeFiles:Remove selected files: ${label}:label:`
  }

  /**
   * Returns help text for configuring required and optional CSV mappings.
   * @returns {string} The translated string.
   */
  getCsvTemplateMappingHelpText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.templateMappingHelp:Enter the column headings used in your spreadsheet. Product name, product identifier, price and currency are required. Marketplace imports also require category and request unit. Leave optional headings blank to omit those columns from the download. Expand the section below to configure chemical, recycling, ingredient and component columns.`
  }

  /**
   * Returns the label for the marketplace mapping preset.
   * @returns {string} The translated string.
   */
  getCsvMarketplaceDefaultsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.marketplaceDefaults:Use marketplace columns`
  }

  /**
   * Returns the label for the chemical and recycling mapping preset.
   * @returns {string} The translated string.
   */
  getCsvFullDefaultsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.fullDefaults:Use chemical and recycling columns`
  }

  /**
   * Explains which mappings the marketplace preset fills in and replaces.
   * @returns {string} The translated string.
   */
  getCsvMarketplaceDefaultsTooltipText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.marketplaceDefaultsTooltip:Fills in headings for marketplace listings, including category, request unit, price, currency and optional drone details. Chemical, recycling, ingredient and component columns are left blank. Replaces current headings and removes custom fields; keeps the template name.`
  }

  /**
   * Explains which mappings the chemical and recycling preset fills in and replaces.
   * @returns {string} The translated string.
   */
  getCsvFullDefaultsTooltipText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.fullDefaultsTooltip:Fills in headings for product details, chemical and safety data, recycling, ingredients and components. Marketplace columns are left blank. Replaces current headings and removes custom fields; keeps the template name.`
  }

  /**
   * Returns the heading for product identity and marketplace mappings.
   * @returns {string} The translated string.
   */
  getCsvCoreMappingsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.coreMappings:Product and marketplace columns`
  }

  /**
   * Returns the heading for optional product mappings.
   * @returns {string} The translated string.
   */
  getCsvOptionalMappingsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.optionalMappings:Optional product columns`
  }

  /**
   * Returns the heading for optional drone specification mappings.
   * @returns {string} The translated string.
   */
  getCsvDroneMappingsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.droneMappings:Optional drone specifications`
  }

  /**
   * Returns the heading for chemical, recycling and association mappings.
   * @returns {string} The translated string.
   */
  getCsvAdditionalMappingsText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.additionalMappings:Chemical, recycling and association columns (optional)`
  }

  /**
   * Returns instructions for named custom columns and their template definitions.
   * @returns {string} The translated string.
   */
  getCsvCustomFieldsHelpText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.customFieldsHelp:Add one column for each custom field. Set its name, spreadsheet heading and type here; set an optional unit for numbers. The heading defaults to the field name. In the spreadsheet, enter values only: for example, 0 under Payload (kg) or No under Waterproof. Blank cells omit that field for the product. You can add up to 50 fields.`
  }

  /**
   * Returns the validation message for a missing column heading.
   * @returns {string} The translated string.
   */
  getCsvMappingRequiredText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.mappingRequired:This field is required.`
  }

  /**
   * Returns the validation message for duplicate headings within one CSV.
   * @returns {string} The translated string.
   */
  getCsvMappingDuplicateText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.mappingDuplicate:Use a different heading; this one maps to another column in the same CSV.`
  }

  /**
   * Returns the validation message for headings exceeding their storage limit.
   * @returns {string} The translated string.
   */
  getCsvMappingTooLongText(): string {
    return $localize`:CSV templates|Template management@@csvTemplates.mappingTooLong:Use at most 128 bytes.`
  }

  /**
   * Returns a translated validation message for an API or form mapping error.
   * @param {string} code Mapping error code; unknown codes use the required-heading message.
   * @returns {string} The translated validation message.
   */
  getCsvMappingErrorText(code: string): string {
    if (code === 'duplicate') return this.getCsvMappingDuplicateText()
    if (code === 'tooLong') return this.getCsvMappingTooLongText()
    if (code === 'limit') return this.getCustomFieldLimitErrorText()
    if (code === 'invalidUnit') return this.getCustomFieldUnitErrorText()
    if (code === 'invalidType') return this.getCsvCustomFieldTypeErrorText()
    return this.getCsvMappingRequiredText()
  }

  /**
   * Returns the label for a custom field's spreadsheet heading.
   * @returns {string} The translated label.
   */
  getCsvColumnHeadingText(): string {
    return $localize`:CSV templates|Column label@@csvTemplates.columnHeading:CSV column heading`
  }

  /**
   * Returns the validation message for an unsupported field type.
   * @returns {string} The translated message.
   */
  getCsvCustomFieldTypeErrorText(): string {
    return $localize`:CSV templates|Validation@@csvTemplates.customFieldTypeError:Choose Text, Number or Yes / No.`
  }

  /**
   * Describes a named custom column in the mapping preview.
   * @param {string} name Product field name.
   * @param {string} type Saved field type.
   * @param {string} [unit] Optional numeric unit.
   * @returns {string} Field name with its translated type and unit.
   */
  getCsvCustomFieldMappingText(
    name: string,
    type: string,
    unit?: string,
  ): string {
    const label =
      type === 'number'
        ? this.getCustomFieldNumberText()
        : type === 'boolean'
          ? this.getCustomFieldBooleanText()
          : this.getCustomFieldTextText()
    return `${name} (${label}${unit ? ', ' + unit : ''})`
  }

  /**
   * Returns a translated region label for CSV mappings.
   * @param {string} region English region name used by the mapping field list.
   * @returns {string} The translated label, or the supplied name for an unknown region.
   */
  getCsvRegionText(region: string): string {
    const regions: Record<string, string> = {
      Alberta: $localize`:CSV templates|Region@@csvTemplates.alberta:Alberta`,
      'British Columbia': $localize`:CSV templates|Region@@csvTemplates.britishColumbia:British Columbia`,
      Manitoba: $localize`:CSV templates|Region@@csvTemplates.manitoba:Manitoba`,
      'New Brunswick': $localize`:CSV templates|Region@@csvTemplates.newBrunswick:New Brunswick`,
      'Newfoundland and Labrador': $localize`:CSV templates|Region@@csvTemplates.newfoundland:Newfoundland and Labrador`,
      'Northwest Territories': $localize`:CSV templates|Region@@csvTemplates.northwestTerritories:Northwest Territories`,
      'Nova Scotia': $localize`:CSV templates|Region@@csvTemplates.novaScotia:Nova Scotia`,
      Nunavut: $localize`:CSV templates|Region@@csvTemplates.nunavut:Nunavut`,
      Ontario: $localize`:CSV templates|Region@@csvTemplates.ontario:Ontario`,
      'Prince Edward Island': $localize`:CSV templates|Region@@csvTemplates.princeEdwardIsland:Prince Edward Island`,
      Quebec: $localize`:CSV templates|Region@@csvTemplates.quebec:Quebec`,
      Saskatchewan: $localize`:CSV templates|Region@@csvTemplates.saskatchewan:Saskatchewan`,
      Yukon: $localize`:CSV templates|Region@@csvTemplates.yukon:Yukon`,
      'Hong Kong': $localize`:CSV templates|Region@@csvTemplates.hongKong:Hong Kong`,
    }
    return regions[region] ?? region
  }
}
